// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

/**
 * Payment API schemas
 */

import { z } from 'zod';
import { addLosslessNumbers, LosslessNumber } from '../lossless-json-codec';
import {
    PaymentTransactionStatus,
    PaymentTransactionLifecycleEventType,
    PaymentReconciliationStatus,
    Status,
} from '@pillar-portal/database/dist/frontend';
import {
    CardType,
    MerchantPaymentRequestStatusBucket,
    PaymentRequestStatus,
    DeliveryChannel,
    DeliveryProvider,
    PaymentRail,
    GatewayTransactionType,
    ReceiptTransactionType,
    PaymentTransactionFlowType,
    GatewayTransactionDetailsType,
    PaymentFeeDefinedBy,
    PublicPaymentRequestState,
    PublicPaymentRequestErrorCode,
    PaymentMethodTokenDetailsStorageVersion,
    PaymentRequestAmountBreakdownStorageVersion,
    PaymentRequestDetailsStorageVersion,
    PaymentRequestSourceDocumentKeyStorageVersion,
    PaymentTransactionConfigurationOverrideReason,
    PaymentTransactionAmountBreakdownStorageVersion,
    PaymentTransactionConfigurationStorageVersion,
    PaymentTransactionDetailsStorageVersion,
    CustomerDetailsStorageVersion,
    PillarPaymentRequestType,
    PillarPaymentResponseType,
    PaymentRequestGeneratedDocumentKind,
    PaymentRequestTransactionOperationOutcome,
    GuestPaymentSuccessAccountCtaKind,
    AccessibleTerminalAuthorizationAction,
    SelectableTerminalAuthorizationAction,
    AccessibleTerminalAuthorizationResourceType,
    SelectableTerminalAccessFailureReason,
    TerminalCardEntryMode,
    LegacyTerminalCardEntryMode,
    PaymentActionAvailabilityStatus,
    PaymentActionUnavailableReason,
    PaymentReadinessAction,
    HostedCardTokenMetadataTier,
    type GatewayCardTokenizationResponse,
} from '../payment.types';
import { CredentialType } from '../secret.types';
import { CardDetailsModelSchema } from '../types/ipg/ipg-card-details-model.types';
import type {
    CoreTransactionDetails,
    GenericIpwTransactionDetails,
    GenericIpwTransactionDetailsV0,
    GenericIpwTransactionDetailsDynamicField,
    GenericIpwTransactionDetailsDynamicFields,
    GenericTransactionDetails,
    TransactionDetails,
    GetOpenBatchRequest,
    GetPaymentAuditLogRequest,
    GetPaymentAuditLogSearchRequest,
    GetPaymentAuditLogResponse,
    GetSavedCardPaymentMethodsRequest,
    GetSavedCardPaymentMethodsResponse,
    CustomerPaymentMethodListRequest,
    CustomerPaymentMethodListItem,
    CustomerPaymentMethodListResponse,
    MerchantPaymentMethodTokenListItem,
    MerchantPaymentMethodTokenListRequest,
    MerchantPaymentMethodTokenListResponse,
    MerchantPaymentMethodTokenDetailsView,
    CustomerPaymentMethodBootstrapRequest,
    CustomerPaymentMethodBootstrapResponse,
    CustomerPaymentMethodBinLookupRequest,
    CustomerPaymentRequestDetailsRequest,
    MerchantPaymentRequestDetailsRequest,
    CreateCustomerPaymentMethodRequest,
    CreateCustomerPaymentMethodResponse,
    RefundTransactionResponse,
    RefundPaymentRequestRequest,
    RefundPaymentRequestResponse,
    RefundPaymentRequestTransactionResult,
    ResetPaymentRequestReconciliationResponse,
    ResetTransactionReconciliationResponse,
    GetDelegateTokenRequest,
    GetDelegateTokenResponse,
    BinLookupRequest,
    BinLookupResponse,
    SavedCardPaymentMethod,
    GetVaultTransactionsSearchRequest,
    GetVaultTransactionsResponse,
    PaymentAuditLogEntry,
    PaymentTransactionCreateData,
    CardTokenCreateData,
    PillarPaymentRequest,
    PillarPaymentRequestLatest,
    PillarPaymentRequestStorageV1,
    PillarPaymentRequestStorageV2,
    PillarPaymentRequestStorageV3,
    LegacyTerminalSaleRequest,
    TerminalSaleRequestStorageV1,
    PillarPaymentResponse,
    PillarPaymentAuditLogResponse,
    ProcessCardTokenPaymentRequest,
    SubmitCardTokenPaymentRequest,
    ProcessStoredPaymentMethodPaymentRequest,
    GuestProcessCardTokenPaymentRequest,
    AuthenticatedProcessCardTokenPaymentRequest,
    GuestProcessStoredPaymentMethodPaymentRequest,
    GuestBinLookupRequest,
    HostedCardTokenQuoteRequest,
    GuestHostedCardTokenQuoteRequest,
    HostedCardTokenQuoteResponse,
    UpdateCustomerPaymentMethodRequest,
    UpdateCustomerPaymentMethodResponse,
    DeleteCustomerPaymentMethodRequest,
    DeleteCustomerPaymentMethodResponse,
    ProcessCardTokenPaymentResponse,
    ProcessCardNotPresentPaymentLifecycleRequest,
    ProcessCardNotPresentPaymentResponse,
    TokenizationResult,
    CardPaymentTerminalMenuItem,
    TerminalCardEntryCapability,
    AccessibleCredentialInfoListItem,
    AccessibleTerminalInfoListItem,
    ListCardPaymentTerminalDevicesRequest,
    ListAccessibleCredentialsInfoRequest,
    ListAccessibleTerminalsInfoRequest,
    ListCardPaymentTerminalDevicesResponse,
    ListAccessibleCredentialsInfoResponse,
    ListAccessibleTerminalsInfoResponse,
    GetPaymentConfigurationRequest,
    PaymentConfigurationResponse,
    AvailablePaymentAction,
    ErrorPaymentAction,
    UnavailablePaymentAction,
    PaymentActionAvailability,
    PaymentActionAvailabilityV2,
    PaymentActionReadinessResponse,
    PaymentActionReadinessResponseV1,
    PaymentActionReadinessResponseV2,
    SelectedPaymentActionReadinessResponseV1,
    PaymentTransactionConfiguration,
    PaymentTransactionConfigurationStorageV1,
    PaymentTransactionConfigurationStorageV2,
    PaymentTransactionConfigurationStorageV2Value,
    PaymentTransactionConfigurationStorageV3,
    ProcessAchPaymentRequest,
    ProcessAchPaymentResponse,
    PaymentTransactionAmountBreakdown,
    LegacyPaymentTransactionAmountBreakdown,
    PaymentTransactionAmountBreakdownStorageV1,
    PaymentTransactionAmountBreakdownStorageV2,
    PaymentTransactionAmountBreakdownStorageV3,
    TransactionDetailsCreateRequest,
    TerminalTokenizationRequest,
    TerminalTokenizationResponse,
    RecordTokenRequest,
    RecordTokenResponse,
    CreditCardPaymentMethodTokenDetails,
    DebitCardPaymentMethodTokenDetails,
    AchPaymentMethodTokenDetails,
    PaymentMethodTokenDetails,
    PaymentMethodTokenDetailsStorageV1,
    PaymentMethodTokenDetailsStorageV2,
    PaymentRequestAmountBreakdownStorageV1,
    PaymentRequestAmountBreakdownStorageV2,
    PaymentRequestDetails,
    PaymentRequestDetailsStorageV1,
    PaymentRequestDetailsStorageV2,
    PaymentRequestSourceDocumentKey,
    PaymentRequestSourceDocumentKeyStorageV1,
    PaymentRequestSourceDocumentKeyStorageV2,
    PaymentRequestSourceDocumentKeyStorageV3,
    DealertrackPaymentRequestSourceDocumentKey,
    GenericPaymentRequestSourceDocumentKey,
    CustomerDetails,
    CustomerDetailsStorageV1,
    CustomerDetailsStorageV2,
    CustomerDetailsStorageV3,
    PaymentRequestDocumentDescriptor,
    DocumentTemplateDescriptor,
    UploadPaymentRequestDocumentResponse,
    GeneratePaymentRequestDocumentRequest,
    GeneratePaymentRequestDocumentResponse,
    CreditCardReceiptTemplateSelection,
    InvoiceTemplateSelection,
    PaymentRequestDocumentTemplateSelection,
    DocumentGenerationMerchantBranding,
    DocumentGenerationPaymentRequestSnapshot,
    CreditCardReceiptDocumentHydrationContext,
    DealertrackPartsInvoiceDocumentHydrationContext,
    DealertrackServiceInvoiceDocumentHydrationContext,
    DealertrackDealInvoiceDocumentHydrationContext,
    DealertrackLeasingInvoiceDocumentHydrationContext,
    EInvoiceEmailHydrationContext,
    InvoiceDocumentHydrationContext,
    PaymentRequestDocumentHydrationContext,
    PaymentRequestDocumentReference,
    CreateDraftPaymentRequestRequest,
    CreateDraftPaymentRequestResponse,
    PaymentRequestOwnerCandidate,
    PaymentRequestOwnerCandidatesResponse,
    FinalizeSendPaymentRequestEmailOnlyRequest,
    FinalizeSendPaymentRequestSmsOnlyRequest,
    FinalizeSendPaymentRequestEmailAndSmsRequest,
    FinalizeSendPaymentRequestRequest,
    ResendPaymentRequestEmailOnlyRequest,
    ResendPaymentRequestSmsOnlyRequest,
    ResendPaymentRequestEmailAndSmsRequest,
    ResendPaymentRequestRequest,
    CancelAndReissuePaymentRequestEmailOnlyRequest,
    CancelAndReissuePaymentRequestSmsOnlyRequest,
    CancelAndReissuePaymentRequestEmailAndSmsRequest,
    CancelAndReissuePaymentRequestRequest,
    FinalizeSendPaymentRequestChannelDispatchResult,
    FinalizeSendPaymentRequestResponse,
    CancelAndReissuePaymentRequestResponse,
    PublicPaymentLinkActionResponse,
    PublicPaymentRequestDocumentView,
    PublicActivePaymentRequestView,
    PublicCanceledPaymentRequestView,
    PublicExpiredPaymentRequestView,
    PublicPaidPaymentRequestView,
    PublicReissuedPaymentRequestView,
    PublicErroredPaymentRequestView,
    PublicPaymentRequestView,
    GuestPaymentEntryContextView,
    GuestPaymentEntryLoginCtaView,
    AuthenticatedPaymentEntryContextView,
    GuestPaymentSuccessAccountCtaView,
    GuestPaymentSuccessView,
    GuestPaymentRequestDocumentAccessResponse,
    GuestPaymentRequestDocumentInlineAccessResponse,
    GuestPaymentRequestDocumentRedirectAccessResponse,
    TwilioStatusCallbackPayload,
    PaymentRequestDeliveryEventPayload,
    PaymentRequestDeliveryAcceptedProviderPayload,
    PaymentRequestDeliveryProviderPayloadRow,
    StoredSendgridEventWebhookEvent,
    StoredTwilioStatusCallbackPayload,
    SendgridEventWebhookPayload,
    VaultTransactionListItem,
    PaymentTransactionLifecycleEventListItem,
    PaymentTransactionLifecycleEventSearchRequest,
    PaymentTransactionLifecycleEventListResponse,
    PaymentRequestDeliveryEventView,
    PaymentRequestDeliveryEventList,
    PaymentRequestDeliveryEventListLegacy,
    PaymentRequestDeliveryEventListRequest,
    PaymentRequestDeliveryEventListRequestLegacy,
    PaymentRequestDeliveryEventDetailsView,
    PaymentRequestListItem,
    PaymentRequestList,
    MerchantPaymentRequestSearchRequest,
    MerchantPaymentRequestListResponse,
    PaymentRequestDetailsView,
    PaymentRequestDetailsViewLegacy,
    PaymentRequestTransactionListItem,
    VoidPaymentRequestResponse,
    VoidPaymentRequestTransactionResult,
    CustomerPortalDashboardRequest,
    CustomerPortalDashboardMerchantOption,
    CustomerPortalDashboardSummary,
    CustomerPortalDashboardResponse,
    CustomerPaymentRequestListRequest,
    CustomerPaymentRequestSearchRequest,
    CustomerPaymentRequestListResponse,
    CustomerPaymentHistoryRequest,
    CustomerPaymentHistoryListItem,
    CustomerPaymentHistoryResponse,
    RecordReconciliationRequest,
    RecordReconciliationResponse,
    RecordPaymentRequestReconciliationRequest,
    RecordPaymentRequestReconciliationResponse,
    ReconcilePaymentsCandidate,
    GetReconcilePaymentsCandidatesRequest,
    GetReconcilePaymentsCandidatesResponse,
    CoreGatewayTransactionDetails,
    GatewayTransactionDetails,
    GatewayTransactionDetailsFetchResult,
    PaymentTransactionDetailsStorageV1,
    PaymentTransactionDetailsStorageV2,
    TransactionLifecycleEvent,
    GetTransactionDetailsResponse,
} from '../payment.types';
import {
    DealertrackPartsTransactionDetailsV1Schema,
    DealertrackServiceTransactionDetailsV1Schema,
    DealertrackDealTransactionDetailsV1Schema,
    DealertrackArStatementCustomersTransactionDetailsV1Schema,
    DealertrackLeasingTransactionDetailsV1Schema,
} from './ipw/dealertrack/dealertrack-transaction-details.schemas';

import { CursorPaginationSchema, OpaqueCursorPaginationSchema } from '../authorization.types';
import { AddressSchema, BusinessDetailsSchema, type Address } from '../backend_config.types';
import { MerchantTimeZoneSchema } from '../timezone.utils';
import {
    JsonValueSchema,
    JsonLosslessNumberSchema,
    JsonLosslessNumberInputSchema,
    JsonNonnegativeLosslessNumberInputSchema,
    JsonPercentageLosslessNumberInputSchema,
    JsonPositiveLosslessNumberInputSchema,
    isJsonValuePrunedSensitiveKey,
    NonEmptyStringSchema,
    PositiveIntSchema,
    PhoneNumberSchema,
    SearchFieldPredicatesSchema,
    PhysicalJsonStructurePredicatesSchema,
    SearchTermsSchema,
} from './common.schemas';
import { REDACTED_LOG_VALUE } from '../logging.types';
import {
    GatewayIdSchema,
    NonCoreThirdPartySystemIdSchema,
    NumericIpwSystemIdSchema,
    NumericIpgSystemIdSchema,
} from './third-party-system.schemas';
import { IpgSystemId, IpwSystemId, ThirdPartySystemId } from '../types/third-party-system.enums';
import { TransactionDetailsTypeId } from '../types/transaction-details.enums';
import { StoredMaskedPanSchema } from './masked_pan.schemas';
import { IpgBrowserTokenizationSessionSchema } from './ipg/ipg-browser-tokenization-session-registry.schemas';
import { IpgBrowserTokenizationResultSchema } from './ipg/ipg-browser-tokenization-result-registry.schemas';
import { IpgCardTokenizationResponseSchema } from './ipg/ipg-card-tokenization-response-registry.schemas';
import { IpgGatewayTransactionDetailsSchema } from './ipg/ipg-gateway-transaction-details-registry.schemas';
import { PaymentGatewayTerminalSaleResultSchema } from './ipg/gateway-terminal-sale.schemas';
import {
    GatewayLifecycleRequestSchema,
    normalizeStoredGatewayLifecycleRequestParts,
    normalizeStoredGatewayLifecycleRequestNumericLeaves,
} from './ipg/gateway-lifecycle-request.schemas';
import {
    GatewayLifecycleResponseJsonBodySchema,
    GatewayLifecycleResponseSchema,
    normalizeStoredGatewayLifecycleResponseNumericLeaves,
    StoredProviderGatewayResponseSnapshotSchema,
} from './ipg/gateway-lifecycle-response.schemas';
import {
    CustomerContactRequiredErrorMessage,
    CustomerDetailsSchema,
    CustomerDetailsStorageV1Schema,
    CustomerDetailsStorageV3DetailsSchema,
    customerDetailsHasContact,
} from './customer-details.schemas';
import {
    buildStoredJsonParser,
    buildStoredJsonValueParser,
    defineStoredJsonContract,
    defineStoredJsonSemanticFieldValue,
    type StoredJsonRowBoundary,
} from '../stored-json-contract.types';
import {
    type SafeIntegerDomain,
    convertStoredJsonNumberToSafeInteger,
    defineSafeIntegerDomain,
    normalizeStoredJsonNumberToLosslessNumber,
    normalizeStoredJsonSafeIntegerFieldsAtPath,
    normalizeStoredJsonSafeIntegerObjectFields,
    PostgresqlGeneratedBigintIdentifierSafeIntegerDomain,
    StoredJsonNumberRecordSchema,
    StoredLosslessNumberSchema,
} from '../stored-json-number';

const PaymentMethodTokenDetailsStorageVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment-method-token-details.payment_method_token_details_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(PaymentMethodTokenDetailsStorageVersion),
    },
});

const PaymentRequestDetailsStorageVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment-request-details.payment_request_details_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(PaymentRequestDetailsStorageVersion),
    },
});

const CustomerDetailsStorageVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'customer-details.customer_details_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(CustomerDetailsStorageVersion),
    },
});

export {
    CustomerContactRequiredErrorMessage,
    CustomerDetailsSchema,
    CustomerDetailsStorageV1Schema,
    CustomerDetailsStorageV3DetailsSchema,
    customerDetailsHasContact,
} from './customer-details.schemas';

export const GatewayResponseHeaderSchema: z.ZodType<Record<string, string>> = z.record(
    z.string(),
    z.string()
);

interface LifecycleBoundaryPayloads {
    readonly gateway_request?: unknown;
    readonly gateway_request_type?: unknown;
    readonly gateway_response_header?: unknown;
    readonly gateway_response_json?: unknown;
    readonly gateway_response_type?: unknown;
    readonly normalized_result?: unknown;
    readonly pillar_request?: unknown;
    readonly pillar_response?: unknown;
}

function validateRedactedLifecyclePayload(
    value: unknown,
    context: z.RefinementCtx,
    path: ReadonlyArray<PropertyKey>
): void {
    if (value === undefined || value === null) {
        return;
    }
    if (Array.isArray(value)) {
        value.forEach((item: unknown, index: number) =>
            validateRedactedLifecyclePayload(item, context, [...path, index])
        );
        return;
    }
    const recordResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!recordResult.success) {
        return;
    }
    for (const key of Object.keys(recordResult.data)) {
        const item = recordResult.data[key];
        if (item === undefined) {
            continue;
        }
        if (isJsonValuePrunedSensitiveKey(key)) {
            if (item !== REDACTED_LOG_VALUE) {
                context.addIssue({
                    code: 'custom',
                    path: [...path, key],
                    message: `Sensitive lifecycle field "${key}" must be redacted`,
                });
            }
            continue;
        }
        validateRedactedLifecyclePayload(item, context, [...path, key]);
    }
}

function validateLifecycleBoundaryPayloads(
    value: LifecycleBoundaryPayloads,
    context: z.RefinementCtx,
    allowMissingLegacyResponseType: boolean = false
): void {
    validateRedactedLifecyclePayload(value.gateway_request, context, ['gateway_request']);
    validateRedactedLifecyclePayload(value.gateway_response_header, context, [
        'gateway_response_header',
    ]);
    validateRedactedLifecyclePayload(value.gateway_response_json, context, [
        'gateway_response_json',
    ]);
    validateRedactedLifecyclePayload(value.normalized_result, context, ['normalized_result']);
    validateRedactedLifecyclePayload(value.pillar_request, context, ['pillar_request']);
    validateRedactedLifecyclePayload(value.pillar_response, context, ['pillar_response']);

    if ((value.gateway_request === undefined) !== (value.gateway_request_type === undefined)) {
        context.addIssue({
            code: 'custom',
            path: ['gateway_request'],
            message: 'gateway_request and gateway_request_type must be provided together',
        });
    } else if (value.gateway_request !== undefined && value.gateway_request_type !== undefined) {
        const nestedEnvelopeResult = z
            .object({
                gateway_request_type: z.string(),
                gateway_request: z.unknown(),
            })
            .passthrough()
            .safeParse(value.gateway_request);
        if (nestedEnvelopeResult.success) {
            context.addIssue({
                code: 'custom',
                path: ['gateway_request'],
                message:
                    'gateway_request must contain the raw request payload, not a lifecycle envelope',
            });
        } else {
            const gatewayRequestResult = GatewayLifecycleRequestSchema.safeParse({
                gateway_request_type: value.gateway_request_type,
                gateway_request: value.gateway_request,
            });
            if (!gatewayRequestResult.success) {
                context.addIssue({
                    code: 'custom',
                    path: ['gateway_request'],
                    message: gatewayRequestResult.error.message,
                });
            }
        }
    }

    if (
        value.gateway_response_json !== undefined &&
        value.gateway_response_type === undefined &&
        !allowMissingLegacyResponseType
    ) {
        context.addIssue({
            code: 'custom',
            path: ['gateway_response_json'],
            message: 'gateway_response_type is required with gateway_response_json',
        });
    } else if (
        value.gateway_response_json !== undefined &&
        value.gateway_response_type !== undefined
    ) {
        const gatewayResponseResult = GatewayLifecycleResponseSchema.safeParse({
            gateway_response_type: value.gateway_response_type,
            gateway_response_json: value.gateway_response_json,
        });
        if (!gatewayResponseResult.success) {
            context.addIssue({
                code: 'custom',
                path: ['gateway_response_json'],
                message: gatewayResponseResult.error.message,
            });
        }
    }
}

function validateStoredLifecycleBoundaryPayloads(
    value: LifecycleBoundaryPayloads,
    context: z.RefinementCtx
): void {
    validateLifecycleBoundaryPayloads(value, context, true);
}

function normalizeStoredLifecycleGatewayRequest(value: unknown): unknown {
    const eventResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!eventResult.success || eventResult.data.gateway_request === undefined) {
        return value;
    }
    const requestTypeResult = z.string().safeParse(eventResult.data.gateway_request_type);
    const normalizedParts = normalizeStoredGatewayLifecycleRequestParts({
        requestType: requestTypeResult.success ? requestTypeResult.data : '',
        payload: eventResult.data.gateway_request,
    });
    const normalizedRequestResult = StoredJsonNumberRecordSchema.safeParse(
        normalizeStoredGatewayLifecycleRequestNumericLeaves({
            gateway_request_type: normalizedParts.requestType,
            gateway_request: normalizedParts.payload,
        })
    );
    if (!normalizedRequestResult.success) {
        return value;
    }
    return {
        ...eventResult.data,
        gateway_request_type: normalizedRequestResult.data.gateway_request_type,
        gateway_request: normalizedRequestResult.data.gateway_request,
    };
}

export function normalizeGatewayResponseHeadersStorageV1(
    headers: Record<string, string>
): Record<string, string> {
    return headers;
}

export const GatewayResponseHeadersStorageContract = defineStoredJsonContract({
    contractName: 'GatewayResponseHeaders',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'GatewayResponseHeaderSchema',
            schema: GatewayResponseHeaderSchema,
            guards: [],
            normalize: normalizeGatewayResponseHeadersStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const StoredGatewayResponseHeadersParser: z.ZodType<Record<string, string>> =
    buildStoredJsonValueParser(GatewayResponseHeadersStorageContract);

export const CardTypeSchema: z.ZodType<CardType> = z.enum(CardType);
export const PaymentRailSchema = z.enum(PaymentRail);
export const PaymentTransactionStatusSchema: z.ZodType<PaymentTransactionStatus> =
    z.nativeEnum(PaymentTransactionStatus);
export const AccessibleTerminalAuthorizationActionSchema: z.ZodType<
    import('../payment.types').AccessibleTerminalAuthorizationAction
> = z.enum([
    AccessibleTerminalAuthorizationAction.TAKE_PAYMENT,
    AccessibleTerminalAuthorizationAction.CREATE,
]);
export const AccessibleTerminalAuthorizationResourceTypeSchema: z.ZodType<
    import('../payment.types').AccessibleTerminalAuthorizationResourceType
> = z.enum([
    AccessibleTerminalAuthorizationResourceType.TERMINAL_DEVICE,
    AccessibleTerminalAuthorizationResourceType.PAYMENT_REQUEST,
]);
export const StatusSchema: z.ZodType<Status> = z.enum(Status);
export const PaymentRequestStatusSchema = z.enum(PaymentRequestStatus);
export const DeliveryChannelSchema = z.enum(DeliveryChannel);
export const DeliveryProviderSchema = z.enum(DeliveryProvider);
const UuidSchema = z.string().uuid();

interface StoredPaymentNumericObjectContract {
    readonly safeIntegerDomains: ReadonlyMap<string, SafeIntegerDomain>;
    readonly boundedNumberFields: ReadonlySet<string>;
    readonly excludedSubtreeFields?: ReadonlySet<string>;
}

const NoStoredPaymentNumericFields: ReadonlySet<string> = new Set();

const IpwSystemIdentifierSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.ipw_system_id',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(IpwSystemId),
    },
});

const IpgSystemIdentifierSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.ipg_system_id',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(IpgSystemId),
    },
});

const TransactionDetailsTypeIdentifierSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.transaction_details_type_id',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(TransactionDetailsTypeId),
    },
});

const TransactionDetailsTypeVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.transaction_details_type_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: [0, 1],
    },
});

const HttpStatusSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.http_status',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'HTTP status',
        minimum: 100,
        maximum: 599,
    },
});

const PaginationSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.pagination',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'payment pagination schemas',
        minimum: 0,
        maximum: Number.MAX_SAFE_INTEGER,
    },
});

const ProviderTimestampSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.provider_timestamp',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'provider timestamp schemas',
        minimum: 0,
        maximum: Number.MAX_SAFE_INTEGER,
    },
});

const ProviderErrorCodeSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.provider_error_code',
    provenance: {
        kind: 'fixed-width-protocol-integer',
        bits: 32,
        signed: true,
    },
});

const ProviderRecordCountSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.provider_record_count',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'provider record count schemas',
        minimum: 0,
        maximum: Number.MAX_SAFE_INTEGER,
    },
});

const DatabaseIntegerSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.database_integer',
    provenance: { kind: 'postgresql-integer' },
});

const StoredTransactionDetailsSafeIntegerDomains: ReadonlyMap<string, SafeIntegerDomain> = new Map([
    ['ipw_system_id', IpwSystemIdentifierSafeIntegerDomain],
    ['transaction_details_type_id', TransactionDetailsTypeIdentifierSafeIntegerDomain],
    ['transaction_details_type_version', TransactionDetailsTypeVersionSafeIntegerDomain],
]);

const PaymentRequestSourceDocumentKeyVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.payment_request_source_document_key_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(PaymentRequestSourceDocumentKeyStorageVersion),
    },
});

const PaymentTransactionConfigurationVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.payment_transaction_configuration_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(PaymentTransactionConfigurationStorageVersion),
    },
});

const PaymentTransactionDetailsVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.payment_transaction_details_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(PaymentTransactionDetailsStorageVersion),
    },
});

const PaymentTransactionAmountBreakdownVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.payment_transaction_amount_breakdown_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(PaymentTransactionAmountBreakdownStorageVersion),
    },
});

const PaymentRequestAmountBreakdownVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.payment_request_amount_breakdown_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(PaymentRequestAmountBreakdownStorageVersion),
    },
});

const PillarPaymentRequestVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'payment.pillar_payment_request_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: [2, 3],
    },
});

const StoredTransactionDetailsNumberFields: ReadonlySet<string> = new Set([
    'quantity',
    'available',
    'listPrice',
    'netPrice',
    'extendedPrice',
    'freight',
    'fees',
    'partsSubtotal',
    'taxes',
    'deposit',
    'total',
    'labor',
    'parts',
    'deductible',
    'sublet',
    'shopSupplies',
    'paintMaterials',
    'miscCharges',
    'totalCharges',
    'salesTax',
    'totalDue',
    'mileage',
    'odometer',
    'msrp',
    'price',
    'downPayment',
    'tradeAllowance',
    'payoff',
    'rebate',
    'apr',
    'termMonths',
    'paymentAmount',
    'amountOptions',
    'serviceContractAmount',
    'insuranceAmount',
    'accessoriesAmount',
    'dealTax',
    'residualPercentage',
    'moneyFactor',
    'cashReceived',
    'upfrontAmount',
    'cashCapReduction',
    'capReductionAmount',
    'acquisitionFee',
    'leaseTax',
    'cashRequired',
    'securityDeposit',
    'feeAmount',
    'annualMiles',
    'amount',
    'detectedAmount',
    'tax',
    'fee',
    'surcharge',
    'value',
]);

function normalizeStoredPaymentNumericLeaves(
    value: unknown,
    contract: StoredPaymentNumericObjectContract
): unknown {
    if (StoredLosslessNumberSchema.safeParse(value).success) {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map((entry: unknown): unknown =>
            normalizeStoredPaymentNumericLeaves(entry, contract)
        );
    }
    const recordResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!recordResult.success) {
        return value;
    }

    const normalized: Record<string, unknown> = { ...recordResult.data };
    for (const key of Object.keys(recordResult.data)) {
        const safeIntegerDomain = contract.safeIntegerDomains.get(key);
        const isLosslessNumberField = contract.boundedNumberFields.has(key);
        if (safeIntegerDomain === undefined) {
            if (isLosslessNumberField) {
                normalized[key] = normalizeStoredJsonNumberToLosslessNumber(recordResult.data[key]);
                continue;
            }
            normalized[key] = contract.excludedSubtreeFields?.has(key)
                ? recordResult.data[key]
                : normalizeStoredPaymentNumericLeaves(recordResult.data[key], contract);
            continue;
        }
        const conversionResult = convertStoredJsonNumberToSafeInteger(
            recordResult.data[key],
            safeIntegerDomain
        );
        normalized[key] = conversionResult.success
            ? conversionResult.value
            : recordResult.data[key];
    }
    return normalized;
}

export function normalizeStoredTransactionDetailsNumericLeaves(value: unknown): unknown {
    return normalizeStoredPaymentNumericLeaves(value, {
        safeIntegerDomains: StoredTransactionDetailsSafeIntegerDomains,
        boundedNumberFields: StoredTransactionDetailsNumberFields,
    });
}

export const CreditCardPaymentMethodTokenDetailsSchema = z
    .object({
        payment_rail: z.literal(PaymentRail.CREDIT_CARD),
        masked_pan: StoredMaskedPanSchema,
        card_type: z.literal(CardType.CREDIT),
        card_brand: NonEmptyStringSchema,
        cardholder_name: NonEmptyStringSchema,
        expiry_date: z
            .string()
            .regex(/^\d{2}\/\d{2}$/)
            .optional(),
    })
    .strict() satisfies z.ZodType<CreditCardPaymentMethodTokenDetails>;

export const DebitCardPaymentMethodTokenDetailsSchema = z
    .object({
        payment_rail: z.literal(PaymentRail.DEBIT_CARD),
        masked_pan: StoredMaskedPanSchema,
        card_type: z.enum([CardType.DEBIT, CardType.PREPAID, CardType.UNKNOWN]),
        card_brand: NonEmptyStringSchema,
        cardholder_name: NonEmptyStringSchema,
        expiry_date: z
            .string()
            .regex(/^\d{2}\/\d{2}$/)
            .optional(),
    })
    .strict() satisfies z.ZodType<DebitCardPaymentMethodTokenDetails>;

export const AchPaymentMethodTokenDetailsSchema = z
    .object({
        payment_rail: z.literal(PaymentRail.ACH),
        account_holder_name: NonEmptyStringSchema,
        masked_account_number: NonEmptyStringSchema,
        masked_routing_number: NonEmptyStringSchema,
        bank_name: NonEmptyStringSchema.optional(),
    })
    .strict() satisfies z.ZodType<AchPaymentMethodTokenDetails>;

export const PaymentMethodTokenDetailsSchema = z.discriminatedUnion('payment_rail', [
    CreditCardPaymentMethodTokenDetailsSchema,
    DebitCardPaymentMethodTokenDetailsSchema,
    AchPaymentMethodTokenDetailsSchema,
]) satisfies z.ZodType<PaymentMethodTokenDetails>;

export const PaymentMethodTokenDetailsStorageVersionSchema: z.ZodType<
    typeof PaymentMethodTokenDetailsStorageVersion.V2
> = z.literal(PaymentMethodTokenDetailsStorageVersion.V2);

export const PaymentMethodTokenDetailsStorageV1Schema: z.ZodType<PaymentMethodTokenDetailsStorageV1> =
    PaymentMethodTokenDetailsSchema;

export const PaymentMethodTokenDetailsStorageV2Schema: z.ZodType<PaymentMethodTokenDetailsStorageV2> =
    z
        .object({
            payment_method_token_details_version: PaymentMethodTokenDetailsStorageVersionSchema,
            details: PaymentMethodTokenDetailsSchema,
        })
        .strict();

export const StoredPaymentMethodTokenDetailsStorageV2Schema: z.ZodType<PaymentMethodTokenDetailsStorageV2> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: PaymentMethodTokenDetailsStorageVersionSafeIntegerDomain,
                fields: ['payment_method_token_details_version'],
            }),
        PaymentMethodTokenDetailsStorageV2Schema
    );

export const PaymentMethodTokenDetailsVersionedObjectMarkerSchema: z.ZodType<{
    readonly payment_method_token_details_version: unknown;
}> = z
    .object({
        payment_method_token_details_version: z.unknown(),
    })
    .passthrough();

export function normalizePaymentMethodTokenDetailsStorageV1(
    details: PaymentMethodTokenDetailsStorageV1
): PaymentMethodTokenDetails {
    return details;
}

export function normalizePaymentMethodTokenDetailsStorageV2(
    details: PaymentMethodTokenDetailsStorageV2
): PaymentMethodTokenDetails {
    return details.details;
}

export const PaymentMethodTokenDetailsStorageContract = defineStoredJsonContract({
    contractName: 'PaymentMethodTokenDetails',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'PaymentMethodTokenDetailsStorageV1Schema',
            schema: PaymentMethodTokenDetailsStorageV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['payment_method_token_details_version'],
                },
            ],
            normalize: normalizePaymentMethodTokenDetailsStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredPaymentMethodTokenDetailsStorageV2Schema',
            schema: StoredPaymentMethodTokenDetailsStorageV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['payment_method_token_details_version'],
                    markerValue: PaymentMethodTokenDetailsStorageVersion.V2,
                },
            ],
            normalize: normalizePaymentMethodTokenDetailsStorageV2,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const StoredPaymentMethodTokenDetailsParser: z.ZodType<PaymentMethodTokenDetails> =
    buildStoredJsonValueParser(PaymentMethodTokenDetailsStorageContract);

export function serializePaymentMethodTokenDetailsV2(
    details: PaymentMethodTokenDetails
): PaymentMethodTokenDetailsStorageV2 {
    return {
        payment_method_token_details_version: PaymentMethodTokenDetailsStorageVersion.V2,
        details: PaymentMethodTokenDetailsSchema.parse(details),
    };
}

export const GENERIC_IPW_INVOICE_ID_MAX_LENGTH = 128;

const DealertrackPartsPaymentRequestSourceDocumentKeySchema =
    DealertrackPartsTransactionDetailsV1Schema.pick({
        transaction_details_type_id: true,
        transaction_details_type_version: true,
        partsInvoiceNumber: true,
    }) satisfies z.ZodType<
        Extract<PaymentRequestSourceDocumentKey, { transaction_details_type_id: unknown }>
    >;

const DealertrackServicePaymentRequestSourceDocumentKeySchema =
    DealertrackServiceTransactionDetailsV1Schema.pick({
        transaction_details_type_id: true,
        transaction_details_type_version: true,
        roNumber: true,
    }) satisfies z.ZodType<
        Extract<PaymentRequestSourceDocumentKey, { transaction_details_type_id: unknown }>
    >;

const DealertrackDealPaymentRequestSourceDocumentKeySchema =
    DealertrackDealTransactionDetailsV1Schema.pick({
        transaction_details_type_id: true,
        transaction_details_type_version: true,
        stockNumber: true,
    }) satisfies z.ZodType<
        Extract<PaymentRequestSourceDocumentKey, { transaction_details_type_id: unknown }>
    >;

const DealertrackLeasingPaymentRequestSourceDocumentKeySchema =
    DealertrackLeasingTransactionDetailsV1Schema.pick({
        transaction_details_type_id: true,
        transaction_details_type_version: true,
        stockNumber: true,
    }) satisfies z.ZodType<
        Extract<PaymentRequestSourceDocumentKey, { transaction_details_type_id: unknown }>
    >;

const StoredPaymentRequestSourceDocumentKeySafeIntegerDomains: ReadonlyMap<
    string,
    SafeIntegerDomain
> = new Map([
    [
        'payment_request_source_document_key_version',
        PaymentRequestSourceDocumentKeyVersionSafeIntegerDomain,
    ],
    ['transaction_details_type_id', TransactionDetailsTypeIdentifierSafeIntegerDomain],
    ['transaction_details_type_version', TransactionDetailsTypeVersionSafeIntegerDomain],
]);

function normalizeStoredPaymentRequestSourceDocumentKeyNumericLeaves(value: unknown): unknown {
    return normalizeStoredPaymentNumericLeaves(value, {
        safeIntegerDomains: StoredPaymentRequestSourceDocumentKeySafeIntegerDomains,
        boundedNumberFields: NoStoredPaymentNumericFields,
    });
}

// Frozen: the only keys stored versions 1 and 2 can hold.
const DealertrackPaymentRequestSourceDocumentKeySchema: z.ZodType<DealertrackPaymentRequestSourceDocumentKey> =
    z.discriminatedUnion('transaction_details_type_id', [
        DealertrackPartsPaymentRequestSourceDocumentKeySchema,
        DealertrackServicePaymentRequestSourceDocumentKeySchema,
        DealertrackDealPaymentRequestSourceDocumentKeySchema,
        DealertrackLeasingPaymentRequestSourceDocumentKeySchema,
    ]);

const GenericPaymentRequestSourceDocumentKeySchema = z
    .object({
        transaction_details_type_id: z.literal(TransactionDetailsTypeId.GENERIC),
        transaction_details_type_version: z.literal(1),
        invoiceId: z.string().min(1).max(GENERIC_IPW_INVOICE_ID_MAX_LENGTH),
    })
    .strict() satisfies z.ZodType<GenericPaymentRequestSourceDocumentKey>;

export const PaymentRequestSourceDocumentKeySchema = z.discriminatedUnion(
    'transaction_details_type_id',
    [
        GenericPaymentRequestSourceDocumentKeySchema,
        DealertrackPartsPaymentRequestSourceDocumentKeySchema,
        DealertrackServicePaymentRequestSourceDocumentKeySchema,
        DealertrackDealPaymentRequestSourceDocumentKeySchema,
        DealertrackLeasingPaymentRequestSourceDocumentKeySchema,
    ]
) satisfies z.ZodType<PaymentRequestSourceDocumentKey>;

export const PaymentRequestSourceDocumentKeyStorageV1Schema: z.ZodType<PaymentRequestSourceDocumentKeyStorageV1> =
    DealertrackPaymentRequestSourceDocumentKeySchema;

export const StoredPaymentRequestSourceDocumentKeyStorageV1Schema: z.ZodType<PaymentRequestSourceDocumentKeyStorageV1> =
    z.preprocess(
        normalizeStoredPaymentRequestSourceDocumentKeyNumericLeaves,
        PaymentRequestSourceDocumentKeyStorageV1Schema
    );

export const PaymentRequestSourceDocumentKeyStorageV2Schema: z.ZodType<PaymentRequestSourceDocumentKeyStorageV2> =
    z
        .object({
            payment_request_source_document_key_version: z.literal(
                PaymentRequestSourceDocumentKeyStorageVersion.V2
            ),
            source_document_key: DealertrackPaymentRequestSourceDocumentKeySchema,
        })
        .strict();

export const StoredPaymentRequestSourceDocumentKeyStorageV2Schema: z.ZodType<PaymentRequestSourceDocumentKeyStorageV2> =
    z.preprocess(
        normalizeStoredPaymentRequestSourceDocumentKeyNumericLeaves,
        PaymentRequestSourceDocumentKeyStorageV2Schema
    );

export const PaymentRequestSourceDocumentKeyStorageV3Schema: z.ZodType<PaymentRequestSourceDocumentKeyStorageV3> =
    z
        .object({
            payment_request_source_document_key_version: z.literal(
                PaymentRequestSourceDocumentKeyStorageVersion.V3
            ),
            source_document_key: PaymentRequestSourceDocumentKeySchema,
        })
        .strict();

export const StoredPaymentRequestSourceDocumentKeyStorageV3Schema: z.ZodType<PaymentRequestSourceDocumentKeyStorageV3> =
    z.preprocess(
        normalizeStoredPaymentRequestSourceDocumentKeyNumericLeaves,
        PaymentRequestSourceDocumentKeyStorageV3Schema
    );

export const PaymentRequestSourceDocumentKeyVersionedObjectMarkerSchema: z.ZodType<{
    readonly payment_request_source_document_key_version: unknown;
}> = z
    .object({
        payment_request_source_document_key_version: z.unknown(),
    })
    .passthrough();

export function normalizePaymentRequestSourceDocumentKeyStorageV1(
    sourceDocumentKey: PaymentRequestSourceDocumentKeyStorageV1
): PaymentRequestSourceDocumentKey {
    return sourceDocumentKey;
}

export function normalizePaymentRequestSourceDocumentKeyStorageV2(
    sourceDocumentKey: PaymentRequestSourceDocumentKeyStorageV2
): PaymentRequestSourceDocumentKey {
    return sourceDocumentKey.source_document_key;
}

export function normalizePaymentRequestSourceDocumentKeyStorageV3(
    sourceDocumentKey: PaymentRequestSourceDocumentKeyStorageV3
): PaymentRequestSourceDocumentKey {
    return sourceDocumentKey.source_document_key;
}

export const PaymentRequestSourceDocumentKeyStorageContract = defineStoredJsonContract({
    contractName: 'PaymentRequestSourceDocumentKey',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredPaymentRequestSourceDocumentKeyStorageV1Schema',
            schema: StoredPaymentRequestSourceDocumentKeyStorageV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['payment_request_source_document_key_version'],
                },
            ],
            normalize: normalizePaymentRequestSourceDocumentKeyStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredPaymentRequestSourceDocumentKeyStorageV2Schema',
            schema: StoredPaymentRequestSourceDocumentKeyStorageV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['payment_request_source_document_key_version'],
                    markerValue: PaymentRequestSourceDocumentKeyStorageVersion.V2,
                },
            ],
            normalize: normalizePaymentRequestSourceDocumentKeyStorageV2,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v3: {
            schemaName: 'StoredPaymentRequestSourceDocumentKeyStorageV3Schema',
            schema: StoredPaymentRequestSourceDocumentKeyStorageV3Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['payment_request_source_document_key_version'],
                    markerValue: PaymentRequestSourceDocumentKeyStorageVersion.V3,
                },
            ],
            normalize: normalizePaymentRequestSourceDocumentKeyStorageV3,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v3',
});

export const StoredPaymentRequestSourceDocumentKeyParser: z.ZodType<PaymentRequestSourceDocumentKey> =
    buildStoredJsonValueParser(PaymentRequestSourceDocumentKeyStorageContract);

export function serializePaymentRequestSourceDocumentKeyV3(
    sourceDocumentKey: PaymentRequestSourceDocumentKey
): PaymentRequestSourceDocumentKeyStorageV3 {
    return {
        payment_request_source_document_key_version:
            PaymentRequestSourceDocumentKeyStorageVersion.V3,
        source_document_key: PaymentRequestSourceDocumentKeySchema.parse(sourceDocumentKey),
    };
}

/**
 * Every stored form a key may have been written in since version 2, for exact
 * JSON matching against rows written before version 3. A generic key has no
 * version 2 form. Version 1 rows were never matched and still are not.
 */
export function storedPaymentRequestSourceDocumentKeyMatchForms(
    sourceDocumentKey: PaymentRequestSourceDocumentKey
): ReadonlyArray<
    PaymentRequestSourceDocumentKeyStorageV2 | PaymentRequestSourceDocumentKeyStorageV3
> {
    const latest: PaymentRequestSourceDocumentKeyStorageV3 =
        serializePaymentRequestSourceDocumentKeyV3(sourceDocumentKey);
    if (
        latest.source_document_key.transaction_details_type_id === TransactionDetailsTypeId.GENERIC
    ) {
        return [latest];
    }
    const previous: PaymentRequestSourceDocumentKeyStorageV2 = {
        payment_request_source_document_key_version:
            PaymentRequestSourceDocumentKeyStorageVersion.V2,
        source_document_key: DealertrackPaymentRequestSourceDocumentKeySchema.parse(
            latest.source_document_key
        ),
    };
    return [previous, latest];
}

export const PaymentTransactionConfigurationOverrideReasonSchema = z.enum(
    PaymentTransactionConfigurationOverrideReason
);

const PaymentTransactionConfigurationSchemaObject = z.object({
    credential_id: PositiveIntSchema,
    credential_type: z.enum([CredentialType.VIRTUAL_TERMINAL, CredentialType.PHYSICAL_TERMINAL]),
    is_virtual: z.boolean().nullable(),
    allow_surcharge: z.boolean(),
    allow_convenience_fee: z.boolean(),
    allow_override_fee: z.boolean(),
    surcharge_pct: JsonPercentageLosslessNumberInputSchema.nullable(),
    convenience_fee: JsonNonnegativeLosslessNumberInputSchema.nullable(),
    processor_tid: NonEmptyStringSchema.optional(),
    transaction_configuration_override: z.boolean().optional(),
    transaction_configuration_override_reason:
        PaymentTransactionConfigurationOverrideReasonSchema.optional(),
    cardpointe_gateway_credential_uuid: z.string().uuid().optional(),
});

export const PaymentTransactionConfigurationSchema: z.ZodType<PaymentTransactionConfiguration> =
    PaymentTransactionConfigurationSchemaObject;

const PaymentTransactionConfigurationStorageV2ValueSchema: z.ZodType<PaymentTransactionConfigurationStorageV2Value> =
    PaymentTransactionConfigurationSchemaObject.omit({
        cardpointe_gateway_credential_uuid: true,
    });

const StoredPaymentTransactionConfigurationSafeIntegerDomains: ReadonlyMap<
    string,
    SafeIntegerDomain
> = new Map([
    [
        'payment_transaction_configuration_version',
        PaymentTransactionConfigurationVersionSafeIntegerDomain,
    ],
    ['credential_id', PostgresqlGeneratedBigintIdentifierSafeIntegerDomain],
]);

const StoredPaymentTransactionConfigurationNumberFields: ReadonlySet<string> = new Set([
    'surcharge_pct',
    'convenience_fee',
]);

function normalizeStoredPaymentTransactionConfigurationNumericLeaves(value: unknown): unknown {
    return normalizeStoredPaymentNumericLeaves(value, {
        safeIntegerDomains: StoredPaymentTransactionConfigurationSafeIntegerDomains,
        boundedNumberFields: StoredPaymentTransactionConfigurationNumberFields,
    });
}

export const PaymentTransactionConfigurationStorageVersionSchema: z.ZodType<
    typeof PaymentTransactionConfigurationStorageVersion.V2
> = z.literal(PaymentTransactionConfigurationStorageVersion.V2);

export const PaymentTransactionConfigurationStorageV1Schema: z.ZodType<PaymentTransactionConfigurationStorageV1> =
    PaymentTransactionConfigurationStorageV2ValueSchema;

export const StoredPaymentTransactionConfigurationStorageV1Schema: z.ZodType<PaymentTransactionConfigurationStorageV1> =
    z.preprocess(
        normalizeStoredPaymentTransactionConfigurationNumericLeaves,
        PaymentTransactionConfigurationStorageV1Schema
    );

export const PaymentTransactionConfigurationStorageV2Schema: z.ZodType<PaymentTransactionConfigurationStorageV2> =
    z
        .object({
            payment_transaction_configuration_version:
                PaymentTransactionConfigurationStorageVersionSchema,
            config: PaymentTransactionConfigurationStorageV2ValueSchema,
        })
        .strict();

export const StoredPaymentTransactionConfigurationStorageV2Schema: z.ZodType<PaymentTransactionConfigurationStorageV2> =
    z.preprocess(
        normalizeStoredPaymentTransactionConfigurationNumericLeaves,
        PaymentTransactionConfigurationStorageV2Schema
    );

export const PaymentTransactionConfigurationStorageV3Schema: z.ZodType<PaymentTransactionConfigurationStorageV3> =
    z
        .object({
            payment_transaction_configuration_version: z.literal(
                PaymentTransactionConfigurationStorageVersion.V3
            ),
            config: PaymentTransactionConfigurationSchema,
        })
        .strict();

export const StoredPaymentTransactionConfigurationStorageV3Schema: z.ZodType<PaymentTransactionConfigurationStorageV3> =
    z.preprocess(
        normalizeStoredPaymentTransactionConfigurationNumericLeaves,
        PaymentTransactionConfigurationStorageV3Schema
    );

export const PaymentTransactionConfigurationVersionedObjectMarkerSchema: z.ZodType<{
    readonly payment_transaction_configuration_version: unknown;
}> = z
    .object({
        payment_transaction_configuration_version: z.unknown(),
    })
    .passthrough();

export function normalizePaymentTransactionConfigurationStorageV1(
    config: PaymentTransactionConfigurationStorageV1
): PaymentTransactionConfiguration {
    return config;
}

export function normalizePaymentTransactionConfigurationStorageV2(
    config: PaymentTransactionConfigurationStorageV2
): PaymentTransactionConfiguration {
    return config.config;
}

export function normalizePaymentTransactionConfigurationStorageV3(
    config: PaymentTransactionConfigurationStorageV3
): PaymentTransactionConfiguration {
    return config.config;
}

export const PaymentTransactionConfigurationStorageContract = defineStoredJsonContract({
    contractName: 'PaymentTransactionConfiguration',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredPaymentTransactionConfigurationStorageV1Schema',
            schema: StoredPaymentTransactionConfigurationStorageV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['payment_transaction_configuration_version'],
                },
            ],
            normalize: normalizePaymentTransactionConfigurationStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredPaymentTransactionConfigurationStorageV2Schema',
            schema: StoredPaymentTransactionConfigurationStorageV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['payment_transaction_configuration_version'],
                    markerValue: PaymentTransactionConfigurationStorageVersion.V2,
                },
            ],
            normalize: normalizePaymentTransactionConfigurationStorageV2,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v3: {
            schemaName: 'StoredPaymentTransactionConfigurationStorageV3Schema',
            schema: StoredPaymentTransactionConfigurationStorageV3Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['payment_transaction_configuration_version'],
                    markerValue: PaymentTransactionConfigurationStorageVersion.V3,
                },
            ],
            normalize: normalizePaymentTransactionConfigurationStorageV3,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v3',
});

export const StoredPaymentTransactionConfigurationParser: z.ZodType<PaymentTransactionConfiguration> =
    buildStoredJsonValueParser(PaymentTransactionConfigurationStorageContract);

export function serializePaymentTransactionConfigurationV2(
    config: PaymentTransactionConfiguration
): PaymentTransactionConfigurationStorageV2 {
    return {
        payment_transaction_configuration_version: PaymentTransactionConfigurationStorageVersion.V2,
        config: PaymentTransactionConfigurationStorageV2ValueSchema.parse(config),
    };
}

export function serializePaymentTransactionConfigurationV3(
    config: PaymentTransactionConfiguration
): PaymentTransactionConfigurationStorageV3 {
    return {
        payment_transaction_configuration_version: PaymentTransactionConfigurationStorageVersion.V3,
        config: PaymentTransactionConfigurationSchema.parse(config),
    };
}

export const GatewayTransactionDetailsTypeSchema = z.enum(GatewayTransactionDetailsType);

export const GenericIpwTransactionDetailsDynamicFieldSchema: z.ZodType<GenericIpwTransactionDetailsDynamicField> =
    z
        .object({
            value: JsonValueSchema,
            display_name: NonEmptyStringSchema.optional(),
            source_element_key: NonEmptyStringSchema.optional(),
        })
        .strict();

export const GenericIpwTransactionDetailsDynamicFieldsSchema: z.ZodType<GenericIpwTransactionDetailsDynamicFields> =
    z.record(NonEmptyStringSchema, GenericIpwTransactionDetailsDynamicFieldSchema);

export const CoreTransactionDetailsSchema: z.ZodType<CoreTransactionDetails> = z
    .object({
        amount: JsonNonnegativeLosslessNumberInputSchema,
        detectedAmount: JsonPositiveLosslessNumberInputSchema.optional(),
        tax: JsonLosslessNumberInputSchema.optional(),
        fee: JsonLosslessNumberInputSchema.optional(),
        surcharge: JsonLosslessNumberInputSchema.optional(),
        currency: z.string().min(1),
        merchant_transaction_id: z.string().optional(),
        customerName: z.string().optional(),
        mainPhone: PhoneNumberSchema.optional(),
        otherPhones: z.array(PhoneNumberSchema).default([]),
        mainEmail: z.string().email().optional(),
        address: AddressSchema.optional(),
        billingAddress: AddressSchema.optional(),
        description: z.string().optional(),
        notes: z.string().optional(),
        ipw_customer_id: z.string().optional(),
    })
    .strict();

const GenericIpwTransactionDetailsSharedShape = {
    amount: JsonNonnegativeLosslessNumberInputSchema,
    detectedAmount: JsonPositiveLosslessNumberInputSchema.optional(),
    tax: JsonLosslessNumberInputSchema.optional(),
    fee: JsonLosslessNumberInputSchema.optional(),
    surcharge: JsonLosslessNumberInputSchema.optional(),
    currency: z.string().min(1),
    merchant_transaction_id: z.string().optional(),
    customerName: z.string().optional(),
    mainPhone: PhoneNumberSchema.optional(),
    otherPhones: z.array(PhoneNumberSchema).default([]),
    mainEmail: z.string().email().optional(),
    address: AddressSchema.optional(),
    billingAddress: AddressSchema.optional(),
    description: z.string().optional(),
    notes: z.string().optional(),
    ipw_customer_id: z.string().optional(),
    ipw_system_id: z.literal(ThirdPartySystemId.GENERIC),
    transaction_details_type_id: z.literal(TransactionDetailsTypeId.GENERIC),
    rbs_dynamic_fields: GenericIpwTransactionDetailsDynamicFieldsSchema.optional(),
};

export const GenericIpwTransactionDetailsV0Schema: z.ZodType<GenericIpwTransactionDetailsV0> = z
    .object({
        ...GenericIpwTransactionDetailsSharedShape,
        transaction_details_type_version: z.literal(0),
    })
    .strict();

/** Exact schema of the latest generic version; writers validate against this. */
export const GenericIpwTransactionDetailsV1Schema: z.ZodType<GenericIpwTransactionDetails> = z
    .object({
        ...GenericIpwTransactionDetailsSharedShape,
        transaction_details_type_version: z.literal(1),
        invoiceId: z.string().min(1).max(GENERIC_IPW_INVOICE_ID_MAX_LENGTH).optional(),
    })
    .strict();

export function normalizeGenericIpwTransactionDetailsV0(
    details: GenericIpwTransactionDetailsV0
): GenericIpwTransactionDetails {
    return { ...details, transaction_details_type_version: 1 };
}

/** Reads every supported generic version and normalizes it to the current version. */
export const GenericIpwTransactionDetailsSchema: z.ZodType<GenericIpwTransactionDetails> = z.union([
    GenericIpwTransactionDetailsV1Schema,
    GenericIpwTransactionDetailsV0Schema.transform(normalizeGenericIpwTransactionDetailsV0),
]);

export const GenericTransactionDetailsSchema: z.ZodType<GenericTransactionDetails> =
    GenericIpwTransactionDetailsSchema;

export const IpwTransactionDetailsSchema: z.ZodType<TransactionDetails> = z.union([
    DealertrackPartsTransactionDetailsV1Schema,
    DealertrackServiceTransactionDetailsV1Schema,
    DealertrackDealTransactionDetailsV1Schema,
    DealertrackLeasingTransactionDetailsV1Schema,
    DealertrackArStatementCustomersTransactionDetailsV1Schema,
]);

export const TransactionDetailsSchema: z.ZodType<TransactionDetails> = z.union([
    GenericTransactionDetailsSchema,
    IpwTransactionDetailsSchema,
]);

export const PaymentRequestDetailsSchema: z.ZodType<PaymentRequestDetails> =
    TransactionDetailsSchema;

/** Exact latest physical shape of every variant; storage writers validate against this. */
export const LatestTransactionDetailsSchema: z.ZodType<TransactionDetails> = z.union([
    GenericIpwTransactionDetailsV1Schema,
    IpwTransactionDetailsSchema,
]);

export const StoredTransactionDetailsSchema: z.ZodType<TransactionDetails> = z.preprocess(
    normalizeStoredTransactionDetailsNumericLeaves,
    TransactionDetailsSchema
);

function normalizeStoredPaymentRequestDetailsStorageV2(value: unknown): unknown {
    const normalizedRoot = normalizeStoredJsonSafeIntegerObjectFields(value, {
        safeIntegerDomain: PaymentRequestDetailsStorageVersionSafeIntegerDomain,
        fields: ['payment_request_details_version'],
    });
    const rootResult = StoredJsonNumberRecordSchema.safeParse(normalizedRoot);
    if (!rootResult.success || !Object.hasOwn(rootResult.data, 'details')) {
        return normalizedRoot;
    }
    return {
        ...rootResult.data,
        details: normalizeStoredTransactionDetailsNumericLeaves(rootResult.data.details),
    };
}

export const PaymentRequestDetailsStorageVersionSchema: z.ZodType<
    typeof PaymentRequestDetailsStorageVersion.V2
> = z.literal(PaymentRequestDetailsStorageVersion.V2);

export const PaymentRequestDetailsStorageV1Schema: z.ZodType<PaymentRequestDetailsStorageV1> =
    PaymentRequestDetailsSchema;

export const StoredPaymentRequestDetailsStorageV1Schema: z.ZodType<PaymentRequestDetailsStorageV1> =
    StoredTransactionDetailsSchema;

export const PaymentRequestDetailsStorageV2Schema: z.ZodType<PaymentRequestDetailsStorageV2> = z
    .object({
        payment_request_details_version: PaymentRequestDetailsStorageVersionSchema,
        details: PaymentRequestDetailsSchema,
    })
    .strict();

export const StoredPaymentRequestDetailsStorageV2Schema: z.ZodType<PaymentRequestDetailsStorageV2> =
    z.preprocess(
        normalizeStoredPaymentRequestDetailsStorageV2,
        PaymentRequestDetailsStorageV2Schema
    );

export const PaymentRequestDetailsVersionedObjectMarkerSchema: z.ZodType<{
    readonly payment_request_details_version: unknown;
}> = z
    .object({
        payment_request_details_version: z.unknown(),
    })
    .passthrough();

export function normalizePaymentRequestDetailsStorageV1(
    details: PaymentRequestDetailsStorageV1
): PaymentRequestDetails {
    return details;
}

export function normalizePaymentRequestDetailsStorageV2(
    details: PaymentRequestDetailsStorageV2
): PaymentRequestDetails {
    return details.details;
}

export const PaymentRequestDetailsStorageContract = defineStoredJsonContract({
    contractName: 'PaymentRequestDetails',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredPaymentRequestDetailsStorageV1Schema',
            schema: StoredPaymentRequestDetailsStorageV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['payment_request_details_version'],
                },
            ],
            normalize: normalizePaymentRequestDetailsStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredPaymentRequestDetailsStorageV2Schema',
            schema: StoredPaymentRequestDetailsStorageV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['payment_request_details_version'],
                    markerValue: PaymentRequestDetailsStorageVersion.V2,
                },
            ],
            normalize: normalizePaymentRequestDetailsStorageV2,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const StoredPaymentRequestDetailsParser: z.ZodType<PaymentRequestDetails> =
    buildStoredJsonValueParser(PaymentRequestDetailsStorageContract);

export function serializePaymentRequestDetailsV2(
    details: PaymentRequestDetails
): PaymentRequestDetailsStorageV2 {
    return {
        payment_request_details_version: PaymentRequestDetailsStorageVersion.V2,
        details: LatestTransactionDetailsSchema.parse(details),
    };
}

export const PaymentTransactionDetailsStorageVersionSchema: z.ZodType<
    typeof PaymentTransactionDetailsStorageVersion.V2
> = z.literal(PaymentTransactionDetailsStorageVersion.V2);

const StoredPaymentTransactionDetailsSafeIntegerDomains: ReadonlyMap<string, SafeIntegerDomain> =
    new Map([
        ...StoredTransactionDetailsSafeIntegerDomains,
        ['payment_transaction_details_version', PaymentTransactionDetailsVersionSafeIntegerDomain],
    ]);

function normalizeStoredPaymentTransactionDetailsNumericLeaves(value: unknown): unknown {
    return normalizeStoredPaymentNumericLeaves(value, {
        safeIntegerDomains: StoredPaymentTransactionDetailsSafeIntegerDomains,
        boundedNumberFields: StoredTransactionDetailsNumberFields,
    });
}

export const PaymentTransactionDetailsStorageV1Schema: z.ZodType<PaymentTransactionDetailsStorageV1> =
    TransactionDetailsSchema;

export const StoredPaymentTransactionDetailsStorageV1Schema: z.ZodType<PaymentTransactionDetailsStorageV1> =
    StoredTransactionDetailsSchema;

export const PaymentTransactionDetailsStorageV2Schema: z.ZodType<PaymentTransactionDetailsStorageV2> =
    z
        .object({
            payment_transaction_details_version: PaymentTransactionDetailsStorageVersionSchema,
            details: TransactionDetailsSchema,
        })
        .strict();

export const StoredPaymentTransactionDetailsStorageV2Schema: z.ZodType<PaymentTransactionDetailsStorageV2> =
    z.preprocess(
        normalizeStoredPaymentTransactionDetailsNumericLeaves,
        PaymentTransactionDetailsStorageV2Schema
    );

export function normalizePaymentTransactionDetailsStorageV1(
    details: PaymentTransactionDetailsStorageV1
): TransactionDetails {
    return details;
}

export function normalizePaymentTransactionDetailsStorageV2(
    details: PaymentTransactionDetailsStorageV2
): TransactionDetails {
    return details.details;
}

export const PaymentTransactionDetailsStorageContract = defineStoredJsonContract({
    contractName: 'TransactionDetails',
    semanticFieldValues: {
        customerName: defineStoredJsonSemanticFieldValue<string>('scalar'),
        mainPhone: defineStoredJsonSemanticFieldValue<string>('scalar'),
        partsInvoiceNumber: defineStoredJsonSemanticFieldValue<string>('scalar'),
        repairOrderNumber: defineStoredJsonSemanticFieldValue<string>('scalar'),
        invoiceId: defineStoredJsonSemanticFieldValue<string>('scalar'),
    },
    variants: {
        v1: {
            schemaName: 'StoredPaymentTransactionDetailsStorageV1Schema',
            schema: StoredPaymentTransactionDetailsStorageV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['payment_transaction_details_version'],
                },
            ],
            normalize: normalizePaymentTransactionDetailsStorageV1,
            semanticPaths: {
                customerName: ['customerName'],
                mainPhone: ['mainPhone'],
                partsInvoiceNumber: ['partsInvoiceNumber'],
                repairOrderNumber: ['roNumber'],
                invoiceId: ['invoiceId'],
            },
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredPaymentTransactionDetailsStorageV2Schema',
            schema: StoredPaymentTransactionDetailsStorageV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['payment_transaction_details_version'],
                    markerValue: PaymentTransactionDetailsStorageVersion.V2,
                },
            ],
            normalize: normalizePaymentTransactionDetailsStorageV2,
            semanticPaths: {
                customerName: ['details', 'customerName'],
                mainPhone: ['details', 'mainPhone'],
                partsInvoiceNumber: ['details', 'partsInvoiceNumber'],
                repairOrderNumber: ['details', 'roNumber'],
                invoiceId: ['details', 'invoiceId'],
            },
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const StoredPaymentTransactionDetailsRowParser: z.ZodType<TransactionDetails> =
    buildStoredJsonParser(PaymentTransactionDetailsStorageContract);

export const StoredPaymentTransactionDetailsParser: z.ZodType<TransactionDetails> = z.preprocess(
    (value: unknown): unknown => ({ value }),
    StoredPaymentTransactionDetailsRowParser
);

export function serializePaymentTransactionDetailsV2(
    details: TransactionDetails
): PaymentTransactionDetailsStorageV2 {
    return {
        payment_transaction_details_version: PaymentTransactionDetailsStorageVersion.V2,
        details: LatestTransactionDetailsSchema.parse(details),
    };
}

export const CustomerDetailsStorageV2Schema: z.ZodType<CustomerDetailsStorageV2> = z
    .object({
        customer_details_version: z.literal(CustomerDetailsStorageVersion.V2),
        details: CustomerDetailsStorageV1Schema,
    })
    .strict();

export const StoredCustomerDetailsStorageV2Schema: z.ZodType<CustomerDetailsStorageV2> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: CustomerDetailsStorageVersionSafeIntegerDomain,
                fields: ['customer_details_version'],
            }),
        CustomerDetailsStorageV2Schema
    );

export function normalizeCustomerDetailsStorageV1(
    details: CustomerDetailsStorageV1
): CustomerDetails {
    return { ...details, otherPhones: details.otherPhones ?? [], otherEmails: [] };
}

export function normalizeCustomerDetailsStorageV2(
    details: CustomerDetailsStorageV2
): CustomerDetails {
    return normalizeCustomerDetailsStorageV1(details.details);
}

export const CustomerDetailsStorageV3Schema: z.ZodType<CustomerDetailsStorageV3> = z
    .object({
        customer_details_version: z.literal(CustomerDetailsStorageVersion.V3),
        details: CustomerDetailsStorageV3DetailsSchema,
    })
    .strict();

export const StoredCustomerDetailsStorageV3Schema: z.ZodType<CustomerDetailsStorageV3> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain: CustomerDetailsStorageVersionSafeIntegerDomain,
                fields: ['customer_details_version'],
            }),
        CustomerDetailsStorageV3Schema
    );

export function normalizeCustomerDetailsStorageV3(
    customerDetailsStorageV3: CustomerDetailsStorageV3
): CustomerDetails {
    return {
        ...customerDetailsStorageV3.details,
        otherPhones: customerDetailsStorageV3.details.otherPhones ?? [],
        otherEmails: customerDetailsStorageV3.details.otherEmails ?? [],
    };
}

export const CustomerDetailsStorageContract = defineStoredJsonContract({
    contractName: 'CustomerDetails',
    semanticFieldValues: {
        customerName: defineStoredJsonSemanticFieldValue<string>('scalar'),
        mainPhone: defineStoredJsonSemanticFieldValue<string>('scalar'),
        otherPhones:
            defineStoredJsonSemanticFieldValue<NonNullable<CustomerDetails['otherPhones']>>(
                'array'
            ),
        mainEmail: defineStoredJsonSemanticFieldValue<string>('scalar'),
        otherEmails:
            defineStoredJsonSemanticFieldValue<NonNullable<CustomerDetails['otherEmails']>>(
                'array'
            ),
    },
    variants: {
        v1: {
            schemaName: 'CustomerDetailsStorageV1Schema',
            schema: CustomerDetailsStorageV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['customer_details_version'],
                },
            ],
            normalize: normalizeCustomerDetailsStorageV1,
            semanticPaths: {
                customerName: ['customerName'],
                mainPhone: ['mainPhone'],
                otherPhones: ['otherPhones'],
                mainEmail: ['mainEmail'],
            },
            unrepresentedSemanticFields: ['otherEmails'],
        },
        v2: {
            schemaName: 'StoredCustomerDetailsStorageV2Schema',
            schema: StoredCustomerDetailsStorageV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['customer_details_version'],
                    markerValue: CustomerDetailsStorageVersion.V2,
                },
            ],
            normalize: normalizeCustomerDetailsStorageV2,
            semanticPaths: {
                customerName: ['details', 'customerName'],
                mainPhone: ['details', 'mainPhone'],
                otherPhones: ['details', 'otherPhones'],
                mainEmail: ['details', 'mainEmail'],
            },
            unrepresentedSemanticFields: ['otherEmails'],
        },
        v3: {
            schemaName: 'StoredCustomerDetailsStorageV3Schema',
            schema: StoredCustomerDetailsStorageV3Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['customer_details_version'],
                    markerValue: CustomerDetailsStorageVersion.V3,
                },
            ],
            normalize: normalizeCustomerDetailsStorageV3,
            semanticPaths: {
                customerName: ['details', 'customerName'],
                mainPhone: ['details', 'mainPhone'],
                otherPhones: ['details', 'otherPhones'],
                mainEmail: ['details', 'mainEmail'],
                otherEmails: ['details', 'otherEmails'],
            },
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v3',
});

export const StoredCustomerDetailsRowParser: z.ZodType<CustomerDetails> = buildStoredJsonParser(
    CustomerDetailsStorageContract
);

export const StoredCustomerDetailsParser: z.ZodType<CustomerDetails> = z.preprocess(
    (value: unknown): unknown => ({ value }),
    StoredCustomerDetailsRowParser
);

export function serializeCustomerDetailsV3(details: CustomerDetails): CustomerDetailsStorageV3 {
    return CustomerDetailsStorageV3Schema.parse({
        customer_details_version: CustomerDetailsStorageVersion.V3,
        details,
    });
}

export const CardholderBillingAddressSchema: z.ZodType<Address> = AddressSchema.superRefine(
    (value: Address, ctx: z.RefinementCtx) => {
        if (value.street.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Billing street is required',
                path: ['street'],
            });
        }
        if (value.city.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Billing city is required',
                path: ['city'],
            });
        }
        if (value.country === 'US' && value.zip === '00000') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Billing ZIP must not be 00000',
                path: ['zip'],
            });
        }
    }
);

export const BillingAddressSchema: z.ZodType<Address> = CardholderBillingAddressSchema;

const TransactionDetailsCreateRequestSchema: z.ZodType<
    TransactionDetailsCreateRequest<TransactionDetails>
> = z.lazy(() => TransactionDetailsSchema);

export const PaymentTransactionCreateDataSchema: z.ZodType<PaymentTransactionCreateData> = z.object(
    {
        merchantOrganizationId: PositiveIntSchema,
        terminalDeviceid: PositiveIntSchema,
        transactionConfiguration: PaymentTransactionConfigurationSchema,
        merchant_transaction_id: z.never().optional(),
        customerId: z.union([PositiveIntSchema, z.undefined()]),
        paymentMethodTokenId: PositiveIntSchema.nullable(),
        gatewayId: GatewayIdSchema,
        gatewayTransactionType: z.enum(GatewayTransactionType),
        transactionDetails: TransactionDetailsCreateRequestSchema,
        gatewayTxnId: NonEmptyStringSchema.optional(),
        gatewayBatch: NonEmptyStringSchema.optional(),
        gatewayAuthCode: NonEmptyStringSchema.optional(),
        gatewayStan: NonEmptyStringSchema.optional(),
        gatewayRrn: NonEmptyStringSchema.optional(),
        cardBrand: NonEmptyStringSchema.optional(),
        cardType: CardTypeSchema.optional(),
        cardLast4: z
            .string()
            .regex(/^\d{4}$/)
            .optional(),
        cardholderName: NonEmptyStringSchema.optional(),
        status: z.enum(PaymentTransactionStatus).optional(),
        batchedAmount: JsonPositiveLosslessNumberInputSchema.optional(),
        refundedAmount: JsonNonnegativeLosslessNumberInputSchema.optional(),
    }
);

export const CardTokenCreateDataSchema: z.ZodType<CardTokenCreateData> = z.object({
    holderOrganizationId: PositiveIntSchema,
    terminalDeviceid: PositiveIntSchema,
    customerId: z.union([PositiveIntSchema, z.undefined()]),
    gatewayId: GatewayIdSchema,
    gatewayToken: NonEmptyStringSchema,
    cardBrand: NonEmptyStringSchema,
    cardType: CardTypeSchema,
    cardMaskedPan: NonEmptyStringSchema,
    cardholderName: NonEmptyStringSchema,
    expiryDate: z
        .string()
        .regex(/^\d{2}\/\d{2}$/)
        .optional(),
    customerDetails: CustomerDetailsSchema.optional(),
    createdByTransactionId: PositiveIntSchema.optional(),
    storedForFutureUse: z.boolean().optional(),
});

/**
 * Terminal Sale Request
 */
export const TerminalSaleRequestSchema = z.object({
    terminal_device_uuid: UuidSchema,
    terminal_card_entry_mode: z.enum(TerminalCardEntryMode),
    amount: z.string().optional(),
    description: z.string().optional(),
    customer_name: z.string().optional(),
    transaction_details: TransactionDetailsCreateRequestSchema,
    merchant_transaction_id: z.never().optional(),
});

/**
 * Terminal Sale Response
 */
export const TokenizationResultSchema: z.ZodType<TokenizationResult> = z.union([
    z
        .object({
            payment_method_token_uuid: z.string().uuid(),
            error: z.never().optional(),
        })
        .strict(),
    z
        .object({
            error: z.string().min(1),
            payment_method_token_uuid: z.never().optional(),
        })
        .strict(),
]);

export const TerminalSaleResponseSchema = z.object({
    payment_transaction_uuid: UuidSchema.optional(),
    gateway_txn_id: z.string().optional(),
    success: z.boolean(),
    error: z.string().optional(),
    tokenization_result: TokenizationResultSchema.optional(),
});

export const TerminalTokenizationRequestSchema: z.ZodType<TerminalTokenizationRequest> = z.object({
    terminal_device_uuid: UuidSchema,
    card_type: CardTypeSchema,
    card_brand: NonEmptyStringSchema,
    masked_pan: StoredMaskedPanSchema,
    cardholder_name: NonEmptyStringSchema,
    customer_details: CustomerDetailsSchema,
    transaction_details: TransactionDetailsCreateRequestSchema,
});

export const TerminalTokenizationSuccessResponseSchema: z.ZodType<
    Extract<TerminalTokenizationResponse, { success: true }>
> = z.object({
    success: z.literal(true),
    payment_transaction_uuid: UuidSchema,
    payment_method_token_uuid: UuidSchema,
});

export const TerminalTokenizationFailureResponseSchema: z.ZodType<
    Extract<TerminalTokenizationResponse, { success: false }>
> = z.object({
    success: z.literal(false),
    payment_transaction_uuid: UuidSchema,
    error: NonEmptyStringSchema,
});

export const TerminalTokenizationResponseSchema: z.ZodType<TerminalTokenizationResponse> = z.union([
    TerminalTokenizationSuccessResponseSchema,
    TerminalTokenizationFailureResponseSchema,
]);

/**
 * Terminal Abort Request
 */
export const TerminalAbortRequestSchema = z.object({
    terminal_device_uuid: UuidSchema,
    merchant_transaction_id: NonEmptyStringSchema.optional(),
    query_only: z.boolean().optional(),
});

/**
 * Terminal Abort Response
 */
export const TerminalAbortResponseSchema = z.object({
    success: z.boolean(),
    error: z.string().optional(),
    payment_transaction_uuid: UuidSchema.optional(),
    transaction_status: z.enum(PaymentTransactionStatus).optional(),
    gateway_txn_id: NonEmptyStringSchema.optional(),
});

/**
 * Void Transaction Request
 */
export const VoidTransactionRequestSchema = z.object({
    payment_transaction_uuid: UuidSchema,
    account_id: z.string().optional(),
});

/**
 * Void Transaction Response
 */
export const VoidTransactionResponseSchema = z.object({
    success: z.boolean(),
    voided_amount: z.string(),
    remaining_batched_amount: z.string(),
    error: z.string().optional(),
});

/**
 * Refund Transaction Request
 */
export const RefundTransactionRequestSchema = z.object({
    payment_transaction_uuid: UuidSchema,
    amount: z.string(),
    account_id: z.string().optional(),
});

/**
 * Refund Transaction Response
 */
export const RefundTransactionResponseSchema: z.ZodType<RefundTransactionResponse> = z.object({
    success: z.boolean(),
    refunded_amount: z.string(),
    cumulative_refunded_amount: z.string(),
    refund_amount_breakdown: z.lazy(() => PaymentTransactionAmountBreakdownSchema).optional(),
    cumulative_refund_amount_breakdown: z
        .lazy(() => PaymentTransactionAmountBreakdownSchema)
        .optional(),
    error: z.string().optional(),
});

/**
 * Reset Transaction Reconciliation Response
 */
export const ResetTransactionReconciliationResponseSchema: z.ZodType<ResetTransactionReconciliationResponse> =
    z.object({
        success: z.literal(true),
        reconciled: z.enum(PaymentReconciliationStatus),
        payment_transaction_lifecycle_event_uuid: UuidSchema,
    });

/**
 * Reset Payment Request Reconciliation Response
 */
export const ResetPaymentRequestReconciliationResponseSchema: z.ZodType<ResetPaymentRequestReconciliationResponse> =
    z.object({
        success: z.literal(true),
        reconciled: z.enum(PaymentReconciliationStatus),
        payment_transaction_lifecycle_event_uuids: z.array(UuidSchema),
    });

export const PaymentRequestTransactionOperationOutcomeSchema: z.ZodType<PaymentRequestTransactionOperationOutcome> =
    z.enum([
        PaymentRequestTransactionOperationOutcome.SUCCESS,
        PaymentRequestTransactionOperationOutcome.SKIPPED,
        PaymentRequestTransactionOperationOutcome.FAILED,
    ]);

export const VoidPaymentRequestTransactionResultSchema: z.ZodType<VoidPaymentRequestTransactionResult> =
    z.object({
        payment_transaction_uuid: UuidSchema,
        merchant_transaction_id: NonEmptyStringSchema.optional(),
        outcome: PaymentRequestTransactionOperationOutcomeSchema,
        voided_amount: z.string(),
        remaining_batched_amount: z.string(),
        message: z.string().optional(),
    });

export const VoidPaymentRequestResponseSchema: z.ZodType<VoidPaymentRequestResponse> = z.object({
    success: z.boolean(),
    transaction_results: z.array(VoidPaymentRequestTransactionResultSchema),
});

export const RefundPaymentRequestRequestSchema: z.ZodType<RefundPaymentRequestRequest> = z
    .object({
        amount: z.string(),
    })
    .strict();

export const RefundPaymentRequestTransactionResultSchema: z.ZodType<RefundPaymentRequestTransactionResult> =
    z.object({
        payment_transaction_uuid: UuidSchema,
        merchant_transaction_id: NonEmptyStringSchema.optional(),
        outcome: PaymentRequestTransactionOperationOutcomeSchema,
        requested_amount: z.string(),
        refunded_amount: z.string(),
        cumulative_refunded_amount: z.string(),
        message: z.string().optional(),
    });

export const RefundPaymentRequestResponseSchema: z.ZodType<RefundPaymentRequestResponse> = z.object(
    {
        success: z.boolean(),
        requested_amount: z.string(),
        refunded_amount: z.string(),
        remaining_unfulfilled_amount: z.string(),
        transaction_results: z.array(RefundPaymentRequestTransactionResultSchema),
    }
);

export const GatewayCardTokenizationResponseSchema: z.ZodType<GatewayCardTokenizationResponse> =
    IpgCardTokenizationResponseSchema;

export const RecordTokenRequestSchema: z.ZodType<RecordTokenRequest> = z
    .object({
        customer_details: CustomerDetailsSchema,
        terminal_device_uuid: UuidSchema,
        stored_for_future_use: z.boolean().optional(),
        card_type: CardTypeSchema,
        card_brand: NonEmptyStringSchema,
        masked_pan: StoredMaskedPanSchema,
        cardholder_name: NonEmptyStringSchema,
        expiry_date: z
            .string()
            .regex(/^\d{2}\/\d{2}$/)
            .optional(),
        gateway_response: GatewayCardTokenizationResponseSchema,
        transaction_details: TransactionDetailsCreateRequestSchema.optional(),
    })
    .refine(({ customer_details }) => customerDetailsHasContact(customer_details), {
        message: CustomerContactRequiredErrorMessage,
        path: ['customer_details'],
    });

/**
 * Record Online Payment Response
 */
export const RecordTokenResponseSchema: z.ZodType<RecordTokenResponse> = z.object({
    success: z.boolean(),
    payment_method_token_uuid: UuidSchema,
    payment_transaction_uuid: UuidSchema.optional(),
});

/**
 * Process Card Token Payment Request
 */
export const MetadataRichHostedCardTokenSchemaShape = {
    token_metadata_tier: z.literal(HostedCardTokenMetadataTier.METADATA_RICH).optional(),
    pan_prefix: z
        .string()
        .regex(/^\d{6,9}$/)
        .optional(),
    card_type: CardTypeSchema,
    card_brand: NonEmptyStringSchema,
    card_last4: z.string().regex(/^\d{4}$/),
} as const satisfies z.ZodRawShape;

export const MetadataRichSubmittedHostedCardTokenSchemaShape = {
    token_metadata_tier: z.literal(HostedCardTokenMetadataTier.METADATA_RICH).optional(),
    pan_prefix: z
        .string()
        .regex(/^\d{6,9}$/)
        .optional(),
    card_brand: NonEmptyStringSchema,
    card_last4: z.string().regex(/^\d{4}$/),
} as const satisfies z.ZodRawShape;

export const TokenOnlyHostedCardTokenSchemaShape = {
    token_metadata_tier: z.literal(HostedCardTokenMetadataTier.TOKEN_ONLY),
    pan_prefix: z.never().optional(),
    card_type: z.never().optional(),
    card_brand: z.never().optional(),
    card_last4: z.never().optional(),
} as const satisfies z.ZodRawShape;

const ProcessCardTokenPaymentRequestStorageV1CommonShape = {
    terminal_device_uuid: UuidSchema,
    token: z.string().min(1),
    tokenization_result: IpgBrowserTokenizationResultSchema.optional(),
    cardholder_name: NonEmptyStringSchema,
    expiry_date: z
        .string()
        .regex(/^\d{2}\/\d{2}$/)
        .optional(),
    billing_address: BillingAddressSchema.optional(),
    amount: z.string().optional(),
    description: z.string().optional(),
    transaction_details: TransactionDetailsCreateRequestSchema,
    surcharge_amount: JsonNonnegativeLosslessNumberInputSchema,
    convenience_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
    total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
    total_amount_paid: JsonNonnegativeLosslessNumberInputSchema,
    merchant_transaction_id: z.never().optional(),
} as const satisfies z.ZodRawShape;

const ProcessCardTokenPaymentRequestCommonShape = {
    ...ProcessCardTokenPaymentRequestStorageV1CommonShape,
    cardholder_name: z.string(),
} as const satisfies z.ZodRawShape;

export const ProcessCardTokenPaymentRequestSchema: z.ZodType<ProcessCardTokenPaymentRequest> =
    z.union([
        z
            .object({
                ...ProcessCardTokenPaymentRequestCommonShape,
                ...MetadataRichHostedCardTokenSchemaShape,
            })
            .strict(),
        z
            .object({
                ...ProcessCardTokenPaymentRequestCommonShape,
                ...TokenOnlyHostedCardTokenSchemaShape,
            })
            .strict(),
    ]);

const ProcessCardTokenPaymentRequestStorageV1Schema: z.ZodType<ProcessCardTokenPaymentRequest> =
    z.union([
        z
            .object({
                ...ProcessCardTokenPaymentRequestStorageV1CommonShape,
                ...MetadataRichHostedCardTokenSchemaShape,
            })
            .strict(),
        z
            .object({
                ...ProcessCardTokenPaymentRequestStorageV1CommonShape,
                ...TokenOnlyHostedCardTokenSchemaShape,
            })
            .strict(),
    ]);

export const SubmitCardTokenPaymentRequestSchema: z.ZodType<SubmitCardTokenPaymentRequest> =
    z.union([
        z
            .object({
                ...ProcessCardTokenPaymentRequestCommonShape,
                ...MetadataRichSubmittedHostedCardTokenSchemaShape,
                customer_details: CustomerDetailsSchema.optional(),
                stored_for_future_use: z.boolean().optional(),
            })
            .strict(),
        z
            .object({
                ...ProcessCardTokenPaymentRequestCommonShape,
                ...TokenOnlyHostedCardTokenSchemaShape,
                customer_details: CustomerDetailsSchema.optional(),
                stored_for_future_use: z.boolean().optional(),
            })
            .strict(),
    ]);

export const ProcessStoredPaymentMethodPaymentRequestSchema: z.ZodType<ProcessStoredPaymentMethodPaymentRequest> =
    z
        .object({
            terminal_device_uuid: UuidSchema,
            payment_method_token_uuid: UuidSchema,
            billing_address: BillingAddressSchema.optional(),
            transaction_details: TransactionDetailsCreateRequestSchema,
            surcharge_amount: JsonNonnegativeLosslessNumberInputSchema,
            convenience_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
            total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
            total_amount_paid: JsonNonnegativeLosslessNumberInputSchema,
        })
        .strict();

const ProcessCardNotPresentPaymentLifecycleRequestStorageV1Schema: z.ZodType<ProcessCardNotPresentPaymentLifecycleRequest> =
    z.union([
        z
            .object({
                terminal_device_uuid: UuidSchema,
                cardholder_name: NonEmptyStringSchema,
                expiry_date: z
                    .string()
                    .regex(/^\d{2}\/\d{2}$/)
                    .optional(),
                billing_address: BillingAddressSchema.optional(),
                customer_details: CustomerDetailsSchema,
                stored_for_future_use: z.boolean(),
                transaction_details: TransactionDetailsCreateRequestSchema,
                surcharge_amount: JsonNonnegativeLosslessNumberInputSchema,
                convenience_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
                total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
                total_amount_paid: JsonNonnegativeLosslessNumberInputSchema,
                ...MetadataRichHostedCardTokenSchemaShape,
            })
            .strict(),
        z
            .object({
                terminal_device_uuid: UuidSchema,
                cardholder_name: NonEmptyStringSchema,
                expiry_date: z
                    .string()
                    .regex(/^\d{2}\/\d{2}$/)
                    .optional(),
                billing_address: BillingAddressSchema.optional(),
                customer_details: CustomerDetailsSchema,
                stored_for_future_use: z.boolean(),
                transaction_details: TransactionDetailsCreateRequestSchema,
                surcharge_amount: JsonNonnegativeLosslessNumberInputSchema,
                convenience_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
                total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
                total_amount_paid: JsonNonnegativeLosslessNumberInputSchema,
                ...TokenOnlyHostedCardTokenSchemaShape,
            })
            .strict(),
    ]);

export const ProcessCardNotPresentPaymentLifecycleRequestSchema: z.ZodType<ProcessCardNotPresentPaymentLifecycleRequest> =
    z.union([
        z
            .object({
                terminal_device_uuid: UuidSchema,
                cardholder_name: z.string(),
                expiry_date: z
                    .string()
                    .regex(/^\d{2}\/\d{2}$/)
                    .optional(),
                billing_address: BillingAddressSchema.optional(),
                customer_details: CustomerDetailsSchema,
                stored_for_future_use: z.boolean(),
                transaction_details: TransactionDetailsCreateRequestSchema,
                surcharge_amount: JsonNonnegativeLosslessNumberInputSchema,
                convenience_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
                total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
                total_amount_paid: JsonNonnegativeLosslessNumberInputSchema,
                ...MetadataRichHostedCardTokenSchemaShape,
            })
            .strict(),
        z
            .object({
                terminal_device_uuid: UuidSchema,
                cardholder_name: z.string(),
                expiry_date: z
                    .string()
                    .regex(/^\d{2}\/\d{2}$/)
                    .optional(),
                billing_address: BillingAddressSchema.optional(),
                customer_details: CustomerDetailsSchema,
                stored_for_future_use: z.boolean(),
                transaction_details: TransactionDetailsCreateRequestSchema,
                surcharge_amount: JsonNonnegativeLosslessNumberInputSchema,
                convenience_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
                total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
                total_amount_paid: JsonNonnegativeLosslessNumberInputSchema,
                ...TokenOnlyHostedCardTokenSchemaShape,
            })
            .strict(),
    ]);

export const ReceiptEmailSchema: z.ZodType<string> = z.string().trim().email();

const GuestProcessCardTokenPaymentRequestCommonShape = {
    token: z.string().min(1),
    tokenization_result: IpgBrowserTokenizationResultSchema.optional(),
    cardholder_name: NonEmptyStringSchema,
    expiry_date: z
        .string()
        .regex(/^\d{2}\/\d{2}$/)
        .optional(),
    billing_address: BillingAddressSchema.optional(),
    receipt_email: ReceiptEmailSchema.optional(),
    surcharge_amount: JsonNonnegativeLosslessNumberInputSchema,
    convenience_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
    total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
    total_amount_paid: JsonNonnegativeLosslessNumberInputSchema,
} as const;

export const GuestProcessCardTokenPaymentRequestObjectSchema = z.union([
    z
        .object({
            ...GuestProcessCardTokenPaymentRequestCommonShape,
            ...MetadataRichSubmittedHostedCardTokenSchemaShape,
        })
        .strict(),
    z
        .object({
            ...GuestProcessCardTokenPaymentRequestCommonShape,
            ...TokenOnlyHostedCardTokenSchemaShape,
        })
        .strict(),
]);

export const GuestProcessCardTokenPaymentRequestSchema: z.ZodType<GuestProcessCardTokenPaymentRequest> =
    GuestProcessCardTokenPaymentRequestObjectSchema;

export const GuestBinLookupRequestSchema: z.ZodType<GuestBinLookupRequest> = z.object({
    pan_prefix: z.string().regex(/^\d{6,9}$/),
});

export const HostedCardTokenQuoteRequestSchema: z.ZodType<HostedCardTokenQuoteRequest> = z
    .object({
        terminal_device_uuid: UuidSchema,
        tokenization_result: IpgBrowserTokenizationResultSchema,
        cardholder_name: z.string(),
        billing_postal_code: z.string().trim().min(1).optional(),
        transaction_details: TransactionDetailsCreateRequestSchema.optional(),
    })
    .strict();

export const GuestHostedCardTokenQuoteRequestSchema: z.ZodType<GuestHostedCardTokenQuoteRequest> = z
    .object({
        tokenization_result: IpgBrowserTokenizationResultSchema,
        cardholder_name: NonEmptyStringSchema,
        billing_postal_code: z.string().trim().min(1).optional(),
    })
    .strict();

export const HostedCardTokenQuoteResponseSchema: z.ZodType<HostedCardTokenQuoteResponse> = z.union([
    z
        .object({
            token_metadata_tier: z.literal(HostedCardTokenMetadataTier.METADATA_RICH),
            card_type: CardTypeSchema,
            card_brand: NonEmptyStringSchema,
            card_last4: z.string().regex(/^\d{4}$/),
            surcharge_amount: JsonLosslessNumberSchema,
        })
        .strict(),
    z
        .object({
            token_metadata_tier: z.literal(HostedCardTokenMetadataTier.TOKEN_ONLY),
        })
        .strict(),
]);

/**
 * Process Card Token Payment Response
 */
export const ProcessCardTokenPaymentResponseSchema: z.ZodType<ProcessCardTokenPaymentResponse> =
    z.object({
        payment_transaction_uuid: UuidSchema.optional(),
        gateway_txn_id: z.string().optional(),
        success: z.boolean(),
        error: z.string().optional(),
        tokenization_result: TokenizationResultSchema.optional(),
    });

export const ProcessCardNotPresentPaymentResponseSchema: z.ZodType<ProcessCardNotPresentPaymentResponse> =
    z
        .object({
            payment_transaction_uuid: UuidSchema.optional(),
            gateway_txn_id: z.string().optional(),
            success: z.boolean(),
            error: z.string().optional(),
            tokenization_result: TokenizationResultSchema.optional(),
        })
        .strict();

export const GetPaymentConfigurationRequestSchema: z.ZodType<GetPaymentConfigurationRequest> =
    z.object({
        terminal_device_uuid: UuidSchema,
    });

export const PaymentConfigurationResponseSchema: z.ZodType<PaymentConfigurationResponse> = z.object(
    {
        account_id: z.string().min(1),
        credential_uuid: UuidSchema,
        gateway_id: GatewayIdSchema,
        credential_type: z.enum([
            CredentialType.VIRTUAL_TERMINAL,
            CredentialType.PHYSICAL_TERMINAL,
        ]),
        is_virtual: z.boolean().nullable(),
        card_details_model: CardDetailsModelSchema,
        allow_surcharge: z.boolean(),
        allow_convenience_fee: z.boolean(),
        allow_override_fee: z.boolean(),
        addressRequiredForCardPayment: z.boolean(),
        surcharge_pct: JsonPercentageLosslessNumberInputSchema.nullable(),
        convenience_fee: JsonNonnegativeLosslessNumberInputSchema.nullable(),
    }
);

export const PaymentActionAvailabilityStatusSchema: z.ZodType<PaymentActionAvailabilityStatus> =
    z.enum(PaymentActionAvailabilityStatus);

export const PaymentActionUnavailableReasonSchema: z.ZodType<PaymentActionUnavailableReason> =
    z.enum(PaymentActionUnavailableReason);

export const AvailablePaymentActionSchema: z.ZodType<AvailablePaymentAction> = z
    .object({
        status: z.literal(PaymentActionAvailabilityStatus.AVAILABLE),
    })
    .strict();

export const UnavailablePaymentActionSchema: z.ZodType<UnavailablePaymentAction> = z
    .object({
        status: z.literal(PaymentActionAvailabilityStatus.UNAVAILABLE),
        reason: PaymentActionUnavailableReasonSchema,
        message: z.string().min(1),
    })
    .strict();

export const ErrorPaymentActionSchema: z.ZodType<ErrorPaymentAction> = z
    .object({
        status: z.literal(PaymentActionAvailabilityStatus.ERROR),
        message: z.string().min(1),
    })
    .strict();

export const PaymentActionAvailabilitySchema: z.ZodType<PaymentActionAvailability> = z.union([
    AvailablePaymentActionSchema,
    ErrorPaymentActionSchema,
    UnavailablePaymentActionSchema,
]);

const PaymentActionAvailabilityV2Schema: z.ZodType<PaymentActionAvailabilityV2> = z.union([
    AvailablePaymentActionSchema,
    UnavailablePaymentActionSchema,
]);

export const PaymentReadinessActionSchema: z.ZodType<PaymentReadinessAction> =
    z.enum(PaymentReadinessAction);

export const SelectedPaymentActionReadinessResponseV1Schema: z.ZodType<SelectedPaymentActionReadinessResponseV1> =
    z
        .object({
            readiness_version: z.literal(1),
            action: PaymentReadinessActionSchema,
            availability: PaymentActionAvailabilitySchema,
        })
        .strict();

export const SelectedPaymentActionReadinessResponseContract = {
    variants: {
        v1: {
            normalize: (
                selectedPaymentActionReadinessResponseV1: SelectedPaymentActionReadinessResponseV1
            ): SelectedPaymentActionReadinessResponseV1 =>
                SelectedPaymentActionReadinessResponseV1Schema.parse(
                    selectedPaymentActionReadinessResponseV1
                ),
        },
    },
    writerVariant: 'v1',
} as const;

export const PaymentActionReadinessResponseV1Schema: z.ZodType<PaymentActionReadinessResponseV1> = z
    .object({
        cardEntry: PaymentActionAvailabilityV2Schema,
        storedCard: PaymentActionAvailabilityV2Schema,
        terminalSale: PaymentActionAvailabilityV2Schema,
    })
    .strict();

export const PaymentActionReadinessResponseV2Schema: z.ZodType<PaymentActionReadinessResponseV2> = z
    .object({
        readiness_version: z.literal(2),
        cardEntry: PaymentActionAvailabilityV2Schema,
        eInvoice: PaymentActionAvailabilityV2Schema,
        storedCard: PaymentActionAvailabilityV2Schema,
        terminalSale: PaymentActionAvailabilityV2Schema,
    })
    .strict();

export const PaymentActionReadinessResponseSchema: z.ZodType<PaymentActionReadinessResponse> = z
    .object({
        readiness_version: z.literal(3),
        cardEntry: PaymentActionAvailabilitySchema,
        eInvoice: PaymentActionAvailabilitySchema,
        storedCard: PaymentActionAvailabilitySchema,
        terminalSale: PaymentActionAvailabilitySchema,
    })
    .strict();

export function downgradePaymentActionReadinessResponseToV1(
    response: PaymentActionReadinessResponse
): PaymentActionReadinessResponseV1 {
    const parsedResponse = PaymentActionReadinessResponseSchema.parse(response);
    return PaymentActionReadinessResponseV1Schema.parse({
        cardEntry: downgradePaymentActionAvailabilityToV2(
            parsedResponse.cardEntry,
            PaymentActionUnavailableReason.CARD_ENTRY_UNAVAILABLE
        ),
        storedCard: downgradePaymentActionAvailabilityToV2(
            parsedResponse.storedCard,
            PaymentActionUnavailableReason.STORED_CARD_UNAVAILABLE
        ),
        terminalSale: downgradePaymentActionAvailabilityToV2(
            parsedResponse.terminalSale,
            PaymentActionUnavailableReason.TERMINAL_UNAVAILABLE
        ),
    });
}

function downgradePaymentActionAvailabilityToV2(
    paymentActionAvailability: PaymentActionAvailability,
    paymentActionUnavailableReason: PaymentActionUnavailableReason
): PaymentActionAvailabilityV2 {
    switch (paymentActionAvailability.status) {
        case PaymentActionAvailabilityStatus.AVAILABLE:
        case PaymentActionAvailabilityStatus.UNAVAILABLE:
            return paymentActionAvailability;
        case PaymentActionAvailabilityStatus.ERROR:
            return {
                status: PaymentActionAvailabilityStatus.UNAVAILABLE,
                reason: paymentActionUnavailableReason,
                message: paymentActionAvailability.message,
            };
    }
}

export function downgradePaymentActionReadinessResponseToV2(
    response: PaymentActionReadinessResponse
): PaymentActionReadinessResponseV2 {
    const parsedResponse = PaymentActionReadinessResponseSchema.parse(response);
    return PaymentActionReadinessResponseV2Schema.parse({
        ...parsedResponse,
        readiness_version: 2,
    });
}

export const PaymentActionReadinessResponseContract = {
    variants: {
        v2: {
            normalize: (
                response: PaymentActionReadinessResponseV2
            ): PaymentActionReadinessResponse =>
                PaymentActionReadinessResponseSchema.parse({
                    ...PaymentActionReadinessResponseV2Schema.parse(response),
                    readiness_version: 3,
                }),
        },
        v3: {
            normalize: (response: PaymentActionReadinessResponse): PaymentActionReadinessResponse =>
                PaymentActionReadinessResponseSchema.parse(response),
        },
    },
    writerVariant: 'v3',
} as const;

export const PaymentActionReadinessResponseDowngradeContract = {
    v1: downgradePaymentActionReadinessResponseToV1,
    v2: downgradePaymentActionReadinessResponseToV2,
} as const;

const CoreGatewayTransactionDetailsSchemaBase = z.object({
    gateway_id: GatewayIdSchema,
    gateway_transaction_details_type: GatewayTransactionDetailsTypeSchema,
    gateway_transaction_details_version: PositiveIntSchema,
    gateway_txn_id: NonEmptyStringSchema,
    base_amount: JsonNonnegativeLosslessNumberInputSchema,
    total_amount: JsonNonnegativeLosslessNumberInputSchema,
    total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
    total_tax_amount: JsonNonnegativeLosslessNumberInputSchema,
    is_void: z.boolean(),
    is_settled: z.boolean(),
    refund_amount: JsonNonnegativeLosslessNumberInputSchema,
    refund_count: z.number().int().nonnegative(),
    card_brand: NonEmptyStringSchema.optional(),
    card_last4: z
        .string()
        .regex(/^\d{4}$/)
        .optional(),
    cardholder_name: NonEmptyStringSchema.optional(),
    card_type: CardTypeSchema.optional(),
});

export const CoreGatewayTransactionDetailsSchema: z.ZodType<CoreGatewayTransactionDetails> =
    CoreGatewayTransactionDetailsSchemaBase;

export const GatewayTransactionDetailsSchema: z.ZodType<GatewayTransactionDetails> =
    IpgGatewayTransactionDetailsSchema;

export const GatewayTransactionDetailsFetchResultSchema: z.ZodType<GatewayTransactionDetailsFetchResult> =
    z.object({
        gateway_transaction_details: GatewayTransactionDetailsSchema,
        gateway_response: JsonValueSchema,
        gateway_response_type: NonEmptyStringSchema,
        gateway_response_status: z.number().int(),
        gateway_response_header: GatewayResponseHeaderSchema,
        gateway_endpoint_url: NonEmptyStringSchema.optional(),
    });

export const TerminalCardEntryCapabilitySchema: z.ZodType<TerminalCardEntryCapability> =
    z.discriminatedUnion('terminal_card_entry_mode', [
        z
            .object({
                terminal_card_entry_mode: z.literal(TerminalCardEntryMode.CARD_PRESENT),
            })
            .strict(),
        z
            .object({
                terminal_card_entry_mode: z.literal(TerminalCardEntryMode.MANUAL),
            })
            .strict(),
        z
            .object({
                terminal_card_entry_mode: z.literal(TerminalCardEntryMode.HYBRID),
            })
            .strict(),
    ]);

export const CardPaymentTerminalMenuItemSchema: z.ZodType<CardPaymentTerminalMenuItem> = z.object({
    terminal_device_uuid: UuidSchema,
    terminal_device_name: z.string().nullable(),
    logical_terminal_uuid: UuidSchema,
    merchant_account_uuid: UuidSchema,
    processor_name: NonEmptyStringSchema,
    processor_mid: NonEmptyStringSchema,
    processor_tid: NonEmptyStringSchema,
    processor_device_id: NonEmptyStringSchema,
    account_id: NonEmptyStringSchema,
    credential_uuid: UuidSchema,
    organization_uuid: UuidSchema,
    credential_type: z.enum([CredentialType.VIRTUAL_TERMINAL, CredentialType.PHYSICAL_TERMINAL]),
    system_id: NumericIpgSystemIdSchema,
    terminal_card_entry_capabilities: z.array(TerminalCardEntryCapabilitySchema).min(1),
    credential_is_active: z.boolean(),
    credential_applies_to_children: z.boolean(),
    effective_allow_override_fee: z.boolean(),
    addressRequiredForCardPayment: z.boolean(),
    can_override_fee: z.boolean(),
    effective_surcharge_pct: JsonPercentageLosslessNumberInputSchema.nullable(),
    effective_surcharge_defined_by: z.enum(PaymentFeeDefinedBy),
    effective_convenience_fee: JsonNonnegativeLosslessNumberInputSchema.nullable(),
    effective_convenience_fee_defined_by: z.enum(PaymentFeeDefinedBy),
    display_label: NonEmptyStringSchema,
});

const SelectableTerminalAuthorizationActionSchemaBase = z.enum(
    SelectableTerminalAuthorizationAction
);

export const SelectableTerminalAuthorizationActionSchema: z.ZodType<
    import('../payment.types').SelectableTerminalAuthorizationAction
> = SelectableTerminalAuthorizationActionSchemaBase;

const ListCardPaymentTerminalDevicesRequestSchemaBase = z
    .object({
        system_id: NumericIpgSystemIdSchema.optional(),
        is_virtual: z
            .union([z.boolean(), z.enum(['true', 'false'])])
            .transform((value) => value === true || value === 'true'),
        action: SelectableTerminalAuthorizationActionSchema,
        resource_type: AccessibleTerminalAuthorizationResourceTypeSchema.optional(),
        credential_type: z
            .enum([CredentialType.VIRTUAL_TERMINAL, CredentialType.PHYSICAL_TERMINAL])
            .optional(),
        limit: z.coerce.number().int().positive().optional(),
        cursor: NonEmptyStringSchema.optional(),
        include_empty_reason: z
            .union([z.boolean(), z.enum(['true', 'false'])])
            .transform((value) => value === true || value === 'true')
            .optional(),
    })
    .superRefine((value, ctx) => {
        if (value.resource_type === undefined) {
            return;
        }
        const isTerminalTakePayment =
            value.action === AccessibleTerminalAuthorizationAction.TAKE_PAYMENT &&
            value.resource_type === AccessibleTerminalAuthorizationResourceType.TERMINAL_DEVICE;
        const isPaymentRequestCreate =
            value.action === AccessibleTerminalAuthorizationAction.CREATE &&
            value.resource_type === AccessibleTerminalAuthorizationResourceType.PAYMENT_REQUEST;
        if (isTerminalTakePayment || isPaymentRequestCreate) {
            return;
        }
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'resource_type must match the requested action for selectable terminals',
        });
    });

export const ListCardPaymentTerminalDevicesRequestSchema: z.ZodType<ListCardPaymentTerminalDevicesRequest> =
    ListCardPaymentTerminalDevicesRequestSchemaBase;

const ListAccessibleTerminalsInfoRequestSchemaBase = z
    .object({
        system_id: NumericIpgSystemIdSchema.optional(),
        is_virtual: z
            .union([z.boolean(), z.enum(['true', 'false'])])
            .transform((value) => value === true || value === 'true'),
        action: AccessibleTerminalAuthorizationActionSchema.optional(),
        resource_type: AccessibleTerminalAuthorizationResourceTypeSchema.optional(),
        limit: z.coerce.number().int().positive().optional(),
        cursor: NonEmptyStringSchema.optional(),
        include_empty_reason: z
            .union([z.boolean(), z.enum(['true', 'false'])])
            .transform((value) => value === true || value === 'true')
            .optional(),
    })
    .superRefine((value, ctx) => {
        const hasAction: boolean = value.action !== undefined;
        const hasResourceType: boolean = value.resource_type !== undefined;
        if (hasAction !== hasResourceType) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'action and resource_type must be provided together',
            });
            return;
        }
        if (!hasAction || !hasResourceType) {
            return;
        }
        const isTerminalTakePayment =
            value.action === AccessibleTerminalAuthorizationAction.TAKE_PAYMENT &&
            value.resource_type === AccessibleTerminalAuthorizationResourceType.TERMINAL_DEVICE;
        const isPaymentRequestCreate =
            value.action === AccessibleTerminalAuthorizationAction.CREATE &&
            value.resource_type === AccessibleTerminalAuthorizationResourceType.PAYMENT_REQUEST;
        if (isTerminalTakePayment || isPaymentRequestCreate) {
            return;
        }
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Unsupported accessible-terminal authorization pair',
        });
    });

export const ListAccessibleTerminalsInfoRequestSchema: z.ZodType<ListAccessibleTerminalsInfoRequest> =
    ListAccessibleTerminalsInfoRequestSchemaBase;

export const ListAccessibleCredentialsInfoRequestSchema: z.ZodType<ListAccessibleCredentialsInfoRequest> =
    z.object({
        system_id: z.coerce
            .number()
            .refine(
                (value: number) => NonCoreThirdPartySystemIdSchema.safeParse(value).success,
                'Invalid third-party system id'
            )
            .transform((value: number) => NonCoreThirdPartySystemIdSchema.parse(value))
            .optional(),
        is_virtual: z
            .union([z.boolean(), z.enum(['true', 'false'])])
            .transform((value) => value === true || value === 'true'),
        credential_type: z.enum([
            CredentialType.VIRTUAL_TERMINAL,
            CredentialType.PHYSICAL_TERMINAL,
        ]),
        limit: z.coerce.number().int().positive().optional(),
        cursor: NonEmptyStringSchema.optional(),
    });

export const ListCardPaymentTerminalDevicesResponseSchema: z.ZodType<ListCardPaymentTerminalDevicesResponse> =
    z.object({
        terminals: z.array(CardPaymentTerminalMenuItemSchema),
        pagination: z.object({
            has_more: z.boolean(),
            next_cursor: NonEmptyStringSchema.optional(),
        }),
        empty_reason: z.enum(SelectableTerminalAccessFailureReason).optional(),
    });

export const AccessibleCredentialInfoListItemSchema: z.ZodType<AccessibleCredentialInfoListItem> =
    z.object({
        credential_uuid: z.string().uuid(),
        organization_uuid: z.string().uuid(),
        system_id: NonCoreThirdPartySystemIdSchema,
        account_id: NonEmptyStringSchema,
        credential_type: z.enum([
            CredentialType.VIRTUAL_TERMINAL,
            CredentialType.PHYSICAL_TERMINAL,
        ]),
        public_fields: z.record(z.string(), z.unknown()),
        status: z.enum(Status),
        applies_to_children: z.boolean(),
        linked_terminal_device_processor_device_id: z.string().nullable(),
    });

export const ListAccessibleCredentialsInfoResponseSchema: z.ZodType<ListAccessibleCredentialsInfoResponse> =
    z.object({
        credentials: z.array(AccessibleCredentialInfoListItemSchema),
        pagination: z.object({
            has_more: z.boolean(),
            next_cursor: NonEmptyStringSchema.optional(),
        }),
    });

export const AccessibleTerminalInfoListItemSchema: z.ZodType<AccessibleTerminalInfoListItem> =
    z.object({
        terminal_device_uuid: UuidSchema,
        terminal_device_name: z.string().nullable(),
        display_label: NonEmptyStringSchema,
        logical_terminal_uuid: UuidSchema,
        merchant_account_uuid: UuidSchema,
        processor_name: NonEmptyStringSchema,
        processor_mid: NonEmptyStringSchema,
        processor_tid: NonEmptyStringSchema,
        processor_device_id: NonEmptyStringSchema,
        system_id: NumericIpgSystemIdSchema,
        credential_uuid: UuidSchema.nullable(),
        terminal_device_credential_type: z
            .enum([CredentialType.VIRTUAL_TERMINAL, CredentialType.PHYSICAL_TERMINAL])
            .nullable(),
        linked_credential_type: z
            .enum([CredentialType.VIRTUAL_TERMINAL, CredentialType.PHYSICAL_TERMINAL])
            .nullable(),
        qa_has_credential_link: z.boolean(),
        qa_terminal_device_credential_type_set: z.boolean(),
        qa_terminal_device_credential_type_matches_linked_credential_type: z.boolean(),
        qa_terminal_virtuality_matches_linked_credential_type: z.boolean(),
        effective_organization_uuid: UuidSchema,
        effective_applies_to_children: z.boolean(),
        effective_allow_override_fee: z.boolean(),
        effective_surcharge_pct: JsonPercentageLosslessNumberInputSchema.nullable(),
        effective_surcharge_defined_by: z.enum(PaymentFeeDefinedBy),
        effective_convenience_fee: JsonNonnegativeLosslessNumberInputSchema.nullable(),
        effective_convenience_fee_defined_by: z.enum(PaymentFeeDefinedBy),
    });

export const ListAccessibleTerminalsInfoResponseSchema: z.ZodType<ListAccessibleTerminalsInfoResponse> =
    z.object({
        terminals: z.array(AccessibleTerminalInfoListItemSchema),
        pagination: z.object({
            has_more: z.boolean(),
            next_cursor: NonEmptyStringSchema.optional(),
        }),
        empty_reason: z.enum(SelectableTerminalAccessFailureReason).optional(),
    });

/**
 * Process ACH Payment Request
 */
export const ProcessAchPaymentRequestSchema: z.ZodType<ProcessAchPaymentRequest> = z.object({
    transaction_details: TransactionDetailsCreateRequestSchema,
    gateway_txn_id: z.string().optional(),
    gateway_request: GatewayLifecycleRequestSchema.optional(),
    gateway_response: JsonValueSchema.optional(),
    gateway_response_status: z.number().int().optional(),
    gateway_response_header: GatewayResponseHeaderSchema.optional(),
});

/**
 * Process ACH Payment Response
 */
export const ProcessAchPaymentResponseSchema: z.ZodType<ProcessAchPaymentResponse> = z.object({
    success: z.boolean(),
    payment_transaction_uuid: UuidSchema,
});

/**
 * Payment Audit Log Entry
 */
const PaymentAuditLogEntryObjectSchema = z.object({
    payment_transaction_lifecycle_event_uuid: UuidSchema,
    payment_transaction_uuid: UuidSchema,
    event_type: z.enum(PaymentTransactionLifecycleEventType),
    event_start: z.string(),
    event_end: z.string(),
    event_ts: z.string().optional(),
    merchant_transaction_id: NonEmptyStringSchema,
    customer_name: z.string().optional(),
    gateway_txn_id: z.string().optional(),
    gateway_request: JsonValueSchema.optional(),
    gateway_response_http_status: z.number().int().optional(),
    gateway_response_header: GatewayResponseHeaderSchema.optional(),
    gateway_response_json: GatewayLifecycleResponseJsonBodySchema.optional(),
    gateway_response_raw: z.string().optional(),
    gateway_request_type: z.string().optional(),
    gateway_response_type: z.string().optional(),
    error_code: z.string().optional(),
    error_message: z.string().optional(),
    pillar_request: z.lazy(() => PillarPaymentRequestSchema).optional(),
    pillar_response: z.lazy(() => PillarPaymentResponseSchema).optional(),
    client_request: z.lazy(() => PillarPaymentRequestSchema).optional(),
});

export const PaymentAuditLogEntrySchema: z.ZodType<PaymentAuditLogEntry> =
    PaymentAuditLogEntryObjectSchema.superRefine(validateLifecycleBoundaryPayloads);

/**
 * Get Open Batch Request
 */
export const GetOpenBatchRequestSchema: z.ZodType<GetOpenBatchRequest> = z.object({
    account_id: z.string().min(1),
    ipg_system_id: NumericIpgSystemIdSchema,
});

/**
 * Get Open Batch Response
 */
export const GetOpenBatchResponseSchema = z.object({
    gateway_txn_ids: z.array(z.string()),
    batch_no: z.string().optional(),
});

/**
 * Query Transactions Request
 */
export const QueryTransactionsRequestSchema = z.object({
    ipg_system_id: NumericIpgSystemIdSchema,
    merchant_transaction_id: z.string().optional(),
    date_filter: z.string().optional(),
    amount: z.string().optional(),
    account_id: z.string().min(1),
});

/**
 * Query Transactions Response
 */
export const QueryTransactionsResponseSchema = z.object({
    transactions: z.array(
        z.object({
            txnid: z.string(),
            amount: z.string().optional(),
            date: z.string().optional(),
            merchant_transaction_id: z.string().optional(),
        })
    ),
});

/**
 * Get Transaction Status Request
 */
export const GetTransactionStatusRequestSchema = z.object({
    req_txn_id: z.string().optional(),
    txn_id: z.string().optional(),
    channel_id: z.string().optional(),
    account_id: z.string().min(1),
});

/**
 * Get Transaction Status Response
 */
export const GetTransactionStatusResponseSchema = z.object({
    state: z.string().optional(),
    txn_id: z.string().optional(),
    merchant_transaction_id: z.string().optional(),
    amount: z.string().optional(),
    error_code: z.string().optional(),
    error_msg: z.string().optional(),
}); // Allow additional properties

/**
 * Pagination parameters
 */
export const PaginationParamsSchema = z.object({
    page: z.number().int().positive().optional(),
    limit: z.number().int().positive().optional(),
    cursor: z.string().optional(),
});

/**
 * Payment Audit Log Request
 */
export const GetPaymentAuditLogRequestSchema: z.ZodType<GetPaymentAuditLogRequest> =
    PaginationParamsSchema.extend({
        ipw_system_id: NumericIpwSystemIdSchema,
        search: z.string().optional(),
    });

export const GetPaymentAuditLogSearchRequestSchema: z.ZodType<GetPaymentAuditLogSearchRequest> =
    PaginationParamsSchema.extend({
        ipw_system_id: NumericIpwSystemIdSchema,
        terms: SearchTermsSchema,
    }).strict();

/**
 * Payment Audit Log Response
 */
export const GetPaymentAuditLogResponseSchema: z.ZodType<GetPaymentAuditLogResponse> = z.object({
    events: z.array(PaymentAuditLogEntrySchema),
    pagination: OpaqueCursorPaginationSchema,
    merchant_timezone: MerchantTimeZoneSchema.nullable(),
});

/**
 * Get Saved Cards (from recent transactions) Request
 */
export const GetSavedCardPaymentMethodsRequestSchema: z.ZodType<GetSavedCardPaymentMethodsRequest> =
    z
        .object({
            customer_name: NonEmptyStringSchema,
            phone_number: PhoneNumberSchema.optional(),
            email_address: z.string().trim().email().optional(),
            ipw_system_id: NumericIpwSystemIdSchema,
            terminal_device_uuid: UuidSchema,
        })
        .strict()
        .refine(
            ({ phone_number, email_address }) =>
                phone_number !== undefined || email_address !== undefined,
            {
                message: 'A valid phone_number or email_address is required',
            }
        );

export const SavedCardPaymentMethodSchema: z.ZodType<SavedCardPaymentMethod> = z
    .object({
        payment_method_token_uuid: UuidSchema,
        created_ts: z.string(), // ISO date string
        masked_pan: StoredMaskedPanSchema,
        card_type: CardTypeSchema,
        card_brand: NonEmptyStringSchema,
        card_last4: z.string().regex(/^\d{4}$/),
        cardholder_name: NonEmptyStringSchema,
        billingAddress: AddressSchema.optional(),
    })
    .strict();

/**
 * Get Saved Cards (from recent transactions) Response
 */
export const GetSavedCardPaymentMethodsResponseSchema: z.ZodType<GetSavedCardPaymentMethodsResponse> =
    z
        .object({
            cards: z.array(SavedCardPaymentMethodSchema),
            merchant_timezone: MerchantTimeZoneSchema.nullable(),
        })
        .strict();

export const CustomerPaymentMethodListRequestSchema: z.ZodType<CustomerPaymentMethodListRequest> =
    z.object({
        customer_organization_uuid: UuidSchema.optional(),
    });

export const CustomerPaymentMethodListItemSchema: z.ZodType<CustomerPaymentMethodListItem> =
    z.object({
        payment_method_token_uuid: UuidSchema,
        status: StatusSchema,
        created_ts: z.string().datetime(),
        stored_for_future_use: z.boolean(),
        payment_method_token_details: PaymentMethodTokenDetailsSchema,
        customer_details: CustomerDetailsSchema.optional(),
    });

export const CustomerPaymentMethodListResponseSchema: z.ZodType<CustomerPaymentMethodListResponse> =
    z.object({
        payment_methods: z.array(CustomerPaymentMethodListItemSchema),
    });

export const MerchantPaymentMethodTokenListRequestSchema: z.ZodType<MerchantPaymentMethodTokenListRequest> =
    z.object({
        limit: z.number().int().positive().optional(),
        cursor: z.number().int().min(0).optional(),
    });

export const MerchantPaymentMethodTokenListItemSchema: z.ZodType<MerchantPaymentMethodTokenListItem> =
    z.object({
        payment_method_token_uuid: UuidSchema,
        holder_organization_uuid: UuidSchema,
        holder_organization_name: NonEmptyStringSchema,
        created_ts: z.string().datetime(),
        stored_for_future_use: z.boolean(),
        payment_method_token_details: PaymentMethodTokenDetailsSchema,
        can_display: z.boolean(),
        can_delete: z.boolean(),
    });

export const MerchantPaymentMethodTokenListResponseSchema: z.ZodType<MerchantPaymentMethodTokenListResponse> =
    z.object({
        payment_method_tokens: z.array(MerchantPaymentMethodTokenListItemSchema),
        pagination: CursorPaginationSchema,
    });

export const MerchantPaymentMethodTokenDetailsViewSchema: z.ZodType<MerchantPaymentMethodTokenDetailsView> =
    z.object({
        payment_method_token_uuid: UuidSchema,
        holder_organization_uuid: UuidSchema,
        holder_organization_name: NonEmptyStringSchema,
        payment_rail: PaymentRailSchema,
        gateway_id: GatewayIdSchema,
        gateway_token: NonEmptyStringSchema,
        created_by_transaction_uuid: UuidSchema.optional(),
        created_ts: z.string().datetime(),
        stored_for_future_use: z.boolean(),
        payment_method_token_details: PaymentMethodTokenDetailsSchema,
        customer_details: CustomerDetailsSchema.optional(),
        can_delete: z.boolean(),
        merchant_timezone: MerchantTimeZoneSchema.nullable(),
    });

export const CustomerPaymentMethodBootstrapRequestSchema: z.ZodType<CustomerPaymentMethodBootstrapRequest> =
    z.object({
        merchant_organization_uuid: UuidSchema.optional(),
        customer_organization_uuid: UuidSchema.optional(),
    });

export const CustomerPaymentMethodBinLookupRequestSchema: z.ZodType<CustomerPaymentMethodBinLookupRequest> =
    z.object({
        merchant_organization_uuid: UuidSchema,
        customer_organization_uuid: UuidSchema.optional(),
        pan_prefix: z.string().regex(/^\d{6,9}$/),
    });

export const CreateCustomerPaymentMethodRequestSchema: z.ZodType<CreateCustomerPaymentMethodRequest> =
    z.object({
        merchant_organization_uuid: UuidSchema,
        customer_organization_uuid: UuidSchema.optional(),
        gateway_response: GatewayCardTokenizationResponseSchema,
        card_brand: NonEmptyStringSchema,
        card_type: CardTypeSchema,
        masked_pan: StoredMaskedPanSchema,
        cardholder_name: NonEmptyStringSchema,
        customer_details: CustomerDetailsSchema.optional(),
    });

export const CreateCustomerPaymentMethodResponseSchema: z.ZodType<CreateCustomerPaymentMethodResponse> =
    CustomerPaymentMethodListItemSchema;

export const UpdateCustomerPaymentMethodBodyWithScopeSchema: z.ZodType<
    Omit<UpdateCustomerPaymentMethodRequest, 'payment_method_token_uuid'>
> = z
    .object({
        customer_organization_uuid: UuidSchema.optional(),
        stored_for_future_use: z.boolean().optional(),
        cardholder_name: NonEmptyStringSchema.optional(),
        customer_details: CustomerDetailsSchema.optional(),
    })
    .refine(
        ({ stored_for_future_use, cardholder_name, customer_details }) =>
            stored_for_future_use !== undefined ||
            cardholder_name !== undefined ||
            customer_details !== undefined,
        { message: 'At least one payment-method change is required.' }
    );

export const UpdateCustomerPaymentMethodRequestSchema: z.ZodType<UpdateCustomerPaymentMethodRequest> =
    z
        .object({
            payment_method_token_uuid: UuidSchema,
        })
        .and(UpdateCustomerPaymentMethodBodyWithScopeSchema);

export const CustomerPaymentRequestDetailsRequestSchema: z.ZodType<CustomerPaymentRequestDetailsRequest> =
    z.object({
        customer_organization_uuid: UuidSchema.optional(),
    });

export const MerchantPaymentRequestDetailsRequestSchema: z.ZodType<MerchantPaymentRequestDetailsRequest> =
    z.object({
        merchant_organization_uuid: UuidSchema.optional(),
    });

export const UpdateCustomerPaymentMethodResponseSchema: z.ZodType<UpdateCustomerPaymentMethodResponse> =
    z.object({
        payment_method_token_uuid: UuidSchema,
        stored_for_future_use: z.boolean(),
        cardholder_name: NonEmptyStringSchema.optional(),
        customer_details: CustomerDetailsSchema.optional(),
    });

export const DeleteCustomerPaymentMethodRequestSchema: z.ZodType<DeleteCustomerPaymentMethodRequest> =
    z.object({
        payment_method_token_uuid: UuidSchema,
        customer_organization_uuid: UuidSchema.optional(),
    });

export const DeleteCustomerPaymentMethodScopeSchema: z.ZodType<
    Pick<DeleteCustomerPaymentMethodRequest, 'customer_organization_uuid'>
> = z.object({
    customer_organization_uuid: UuidSchema.optional(),
});

export const DeleteCustomerPaymentMethodResponseSchema: z.ZodType<DeleteCustomerPaymentMethodResponse> =
    z.object({
        payment_method_token_uuid: UuidSchema,
        stored_payment_method_uuid: UuidSchema,
        status: z.literal(Status.DELETED),
    });

export const GetDelegateTokenRequestSchema: z.ZodType<GetDelegateTokenRequest> = z.object({
    terminal_device_uuid: UuidSchema,
});

export const GetDelegateTokenResponseSchema: z.ZodType<GetDelegateTokenResponse> =
    IpgBrowserTokenizationSessionSchema;

export const CustomerPaymentMethodBootstrapResponseSchema: z.ZodType<CustomerPaymentMethodBootstrapResponse> =
    z.object({
        merchant_organization_uuid: UuidSchema,
        merchant_display_name: NonEmptyStringSchema,
        delegate_token: GetDelegateTokenResponseSchema,
    });

export const BinLookupRequestSchema: z.ZodType<BinLookupRequest> = z.object({
    terminal_device_uuid: UuidSchema,
    pan_prefix: z.string().regex(/^\d{6,9}$/),
});

export const BinLookupResponseSchema: z.ZodType<BinLookupResponse> = z.object({
    card_type: CardTypeSchema,
});

/**
 * Get Vault Transactions Request
 */
export const GetVaultTransactionsRequestSchema = PaginationParamsSchema.extend({
    ipw_customer_id: z.string().optional(),
    ipw_system_id: NumericIpwSystemIdSchema.optional(),
    search: z.string().optional(),
    gateway_transaction_types: z.array(z.enum(GatewayTransactionType)).min(1).optional(),
});

export const GetVaultTransactionsSearchRequestSchema: z.ZodType<GetVaultTransactionsSearchRequest> =
    PaginationParamsSchema.extend({
        ipw_customer_id: z.string().optional(),
        ipw_system_id: NumericIpwSystemIdSchema.optional(),
        terms: SearchTermsSchema,
        field_predicates: SearchFieldPredicatesSchema,
        json_path_predicates: PhysicalJsonStructurePredicatesSchema,
        gateway_transaction_types: z.array(z.enum(GatewayTransactionType)).min(1).optional(),
    }).strict();

export const PaymentTransactionFlowTypeSchema: z.ZodType<PaymentTransactionFlowType> = z.enum(
    PaymentTransactionFlowType
);

/**
 * Transaction List Item
 */
export const VaultTransactionListItemSchema: z.ZodType<VaultTransactionListItem> = z.object({
    payment_transaction_uuid: UuidSchema,
    merchant_organization_uuid: UuidSchema,
    merchant_display_name: NonEmptyStringSchema,
    merchant_user_uuid: UuidSchema,
    merchant_user_display_name: NonEmptyStringSchema,
    gateway_txn_id: z.string().nullable(),
    transaction_type: PaymentTransactionFlowTypeSchema,
    client_request_details: TransactionDetailsSchema.nullable().optional(),
    customer_name: z.string().min(1).nullable(),
    cardholder_name: z.string().min(1).nullable(),
    card_last4: z
        .string()
        .regex(/^\d{4}$/)
        .nullable(),
    base_amount: z.string().nullable(),
    original_amount: z.string(),
    batched_amount: z.string().nullable(),
    refunded_amount: z.string().nullable(),
    status: z.enum(PaymentTransactionStatus),
    reconciled: z.enum(PaymentReconciliationStatus),
    merchant_transaction_id: NonEmptyStringSchema,
    transaction_created_ts: z.string(),
    auth_decision_ts: z.string().nullable(), // ISO 8601 timestamp when auth status is known
    is_voidable: z.boolean(),
    is_refundable: z.boolean(),
    can_display: z.boolean(),
    data_quality: z
        .object({
            severity: z.enum(['warning', 'error']),
            issues: z.array(
                z.object({
                    severity: z.enum(['warning', 'error']),
                    message: z.string().min(1),
                })
            ),
        })
        .nullable(),
    merchant_timezone: MerchantTimeZoneSchema.nullable(),
});

/**
 * Get Vault Transactions Response
 */
export const GetVaultTransactionsResponseSchema: z.ZodType<GetVaultTransactionsResponse> = z.object(
    {
        transactions: z.array(VaultTransactionListItemSchema),
        pagination: z.object({
            page: z.number().int().nonnegative().optional(),
            limit: z.number().int().nonnegative().optional(),
            total: z.number().int().nonnegative().optional(),
            has_more: z.boolean(),
            next_cursor: z.string().optional(),
        }),
    }
);

export const PaymentTransactionLifecycleEventListItemSchema: z.ZodType<PaymentTransactionLifecycleEventListItem> =
    z.object({
        payment_transaction_lifecycle_event_uuid: UuidSchema,
        payment_transaction_uuid: UuidSchema,
        merchant_transaction_id: NonEmptyStringSchema,
        event_type: z.enum(PaymentTransactionLifecycleEventType),
        event_start: z.string().datetime(),
        event_end: z.string().datetime(),
        transaction_status: z.enum(PaymentTransactionStatus).nullable().optional(),
        reconciled: z.enum(PaymentReconciliationStatus).nullable().optional(),
        gateway_response_http_status: z.number().int().nullable().optional(),
        gateway_stan: z.string().nullable().optional(),
        gateway_rrn: z.string().nullable().optional(),
        gateway_auth_code: z.string().nullable().optional(),
        error_code: z.string().nullable().optional(),
        error_message: z.string().nullable().optional(),
        merchant_timezone: MerchantTimeZoneSchema.nullable(),
    });

export const PaymentTransactionLifecycleEventSearchRequestSchema: z.ZodType<PaymentTransactionLifecycleEventSearchRequest> =
    PaginationParamsSchema.extend({
        terms: SearchTermsSchema,
        field_predicates: SearchFieldPredicatesSchema,
        json_path_predicates: PhysicalJsonStructurePredicatesSchema,
    }).strict();

export const PaymentTransactionLifecycleEventListResponseSchema: z.ZodType<PaymentTransactionLifecycleEventListResponse> =
    z.object({
        lifecycle_events: z.array(PaymentTransactionLifecycleEventListItemSchema),
        pagination: OpaqueCursorPaginationSchema,
    });

/**
 * Transaction Lifecycle Event
 */
const TransactionLifecycleEventObjectSchema = z.object({
    payment_transaction_lifecycle_event_id: z.number(),
    event_type: z.enum(PaymentTransactionLifecycleEventType),
    event_start: z.string(), // ISO 8601 timestamp
    event_end: z.string(), // ISO 8601 timestamp
    event_ts: z.string().optional(), // Legacy compatibility alias
    amount: z.string().optional(),
    amounts: JsonValueSchema.optional(),
    error_code: z.string().optional(),
    error_message: z.string().optional(),
    gateway_request: JsonValueSchema.optional(),
    gateway_response_http_status: z.number().int().optional(),
    gateway_response_header: GatewayResponseHeaderSchema.optional(),
    gateway_response_json: GatewayLifecycleResponseJsonBodySchema.optional(),
    gateway_response_raw: z.string().optional(),
    normalized_result: PaymentGatewayTerminalSaleResultSchema.optional(),
    gateway_request_type: z.string().optional(),
    gateway_response_type: z.string().optional(),
    gateway_operation_mnemonic: z.string().optional(),
    gateway_endpoint_url: z.string().url().optional(),
    pillar_request: z.lazy(() => PillarPaymentRequestSchema).optional(),
    pillar_response: z.lazy(() => PillarPaymentResponseSchema).optional(),
});

export const TransactionLifecycleEventSchema: z.ZodType<TransactionLifecycleEvent> =
    TransactionLifecycleEventObjectSchema.superRefine(validateLifecycleBoundaryPayloads);

export const StoredTransactionLifecycleEventSchema: z.ZodType<TransactionLifecycleEvent> =
    z.preprocess(
        normalizeStoredLifecycleGatewayRequest,
        TransactionLifecycleEventObjectSchema.extend({
            gateway_response_json: StoredProviderGatewayResponseSnapshotSchema.optional(),
        }).superRefine(validateStoredLifecycleBoundaryPayloads)
    );

function sumExactAmounts(values: readonly (LosslessNumber | undefined)[]): LosslessNumber {
    return addLosslessNumbers(
        values.filter((value): value is LosslessNumber => value !== undefined)
    );
}

const LegacyPaymentTransactionAmountBreakdownInputSchema = z
    .object({
        base_amount: JsonLosslessNumberInputSchema,
        surcharge: JsonLosslessNumberInputSchema.optional(),
        convenience_fee: JsonLosslessNumberInputSchema.optional(),
        tax: JsonLosslessNumberInputSchema.optional(),
        donation: JsonLosslessNumberInputSchema.optional(),
        total_amount: JsonLosslessNumberInputSchema.optional(),
    })
    .strict();

export const LegacyPaymentTransactionAmountBreakdownSchema: z.ZodType<LegacyPaymentTransactionAmountBreakdown> =
    LegacyPaymentTransactionAmountBreakdownInputSchema.transform(
        (value): LegacyPaymentTransactionAmountBreakdown => ({
            ...value,
            total_amount:
                value.total_amount ??
                sumExactAmounts([
                    value.base_amount,
                    value.surcharge,
                    value.convenience_fee,
                    value.donation,
                    value.tax,
                ]),
        })
    );

export const PaymentTransactionAmountBreakdownSchema: z.ZodType<PaymentTransactionAmountBreakdown> =
    LegacyPaymentTransactionAmountBreakdownInputSchema.extend({
        tip: JsonLosslessNumberInputSchema.optional(),
    }).transform(
        (value): PaymentTransactionAmountBreakdown => ({
            ...value,
            total_amount:
                value.total_amount ??
                sumExactAmounts([
                    value.base_amount,
                    value.surcharge,
                    value.convenience_fee,
                    value.tip,
                    value.donation,
                    value.tax,
                ]),
        })
    );

const StoredAmountBreakdownNumberFields: ReadonlySet<string> = new Set([
    'base_amount',
    'surcharge',
    'convenience_fee',
    'tax',
    'tip',
    'donation',
    'total_amount',
]);

const StoredPaymentTransactionAmountBreakdownSafeIntegerDomains: ReadonlyMap<
    string,
    SafeIntegerDomain
> = new Map([
    [
        'payment_transaction_amount_breakdown_version',
        PaymentTransactionAmountBreakdownVersionSafeIntegerDomain,
    ],
]);

function normalizeStoredPaymentTransactionAmountBreakdownNumericLeaves(value: unknown): unknown {
    return normalizeStoredPaymentNumericLeaves(value, {
        safeIntegerDomains: StoredPaymentTransactionAmountBreakdownSafeIntegerDomains,
        boundedNumberFields: StoredAmountBreakdownNumberFields,
    });
}

export const PaymentTransactionAmountBreakdownStorageVersionSchema: z.ZodType<PaymentTransactionAmountBreakdownStorageVersion> =
    z.union([
        z.literal(PaymentTransactionAmountBreakdownStorageVersion.V2),
        z.literal(PaymentTransactionAmountBreakdownStorageVersion.V3),
    ]);

export const PaymentTransactionAmountBreakdownStorageV1Schema: z.ZodType<PaymentTransactionAmountBreakdownStorageV1> =
    LegacyPaymentTransactionAmountBreakdownSchema;

export const StoredPaymentTransactionAmountBreakdownStorageV1Schema: z.ZodType<PaymentTransactionAmountBreakdownStorageV1> =
    z.preprocess(
        normalizeStoredPaymentTransactionAmountBreakdownNumericLeaves,
        PaymentTransactionAmountBreakdownStorageV1Schema
    );

export const PaymentTransactionAmountBreakdownStorageV2Schema: z.ZodType<PaymentTransactionAmountBreakdownStorageV2> =
    z
        .object({
            payment_transaction_amount_breakdown_version: z.literal(
                PaymentTransactionAmountBreakdownStorageVersion.V2
            ),
            breakdown: LegacyPaymentTransactionAmountBreakdownSchema,
        })
        .strict();

export const StoredPaymentTransactionAmountBreakdownStorageV2Schema: z.ZodType<PaymentTransactionAmountBreakdownStorageV2> =
    z.preprocess(
        normalizeStoredPaymentTransactionAmountBreakdownNumericLeaves,
        PaymentTransactionAmountBreakdownStorageV2Schema
    );

export const PaymentTransactionAmountBreakdownStorageV3Schema: z.ZodType<PaymentTransactionAmountBreakdownStorageV3> =
    z
        .object({
            payment_transaction_amount_breakdown_version: z.literal(
                PaymentTransactionAmountBreakdownStorageVersion.V3
            ),
            breakdown: PaymentTransactionAmountBreakdownSchema,
        })
        .strict();

export const StoredPaymentTransactionAmountBreakdownStorageV3Schema: z.ZodType<PaymentTransactionAmountBreakdownStorageV3> =
    z.preprocess(
        normalizeStoredPaymentTransactionAmountBreakdownNumericLeaves,
        PaymentTransactionAmountBreakdownStorageV3Schema
    );

export function normalizePaymentTransactionAmountBreakdownStorageV1(
    breakdown: PaymentTransactionAmountBreakdownStorageV1
): PaymentTransactionAmountBreakdown {
    return breakdown;
}

export function normalizePaymentTransactionAmountBreakdownStorageV2(
    breakdown: PaymentTransactionAmountBreakdownStorageV2
): PaymentTransactionAmountBreakdown {
    return breakdown.breakdown;
}

export function normalizePaymentTransactionAmountBreakdownStorageV3(
    breakdown: PaymentTransactionAmountBreakdownStorageV3
): PaymentTransactionAmountBreakdown {
    return breakdown.breakdown;
}

export const PaymentTransactionAmountBreakdownStorageContract = defineStoredJsonContract({
    contractName: 'PaymentTransactionAmountBreakdown',
    semanticFieldValues: {
        baseAmount: defineStoredJsonSemanticFieldValue<number>('scalar'),
        surchargeAmount: defineStoredJsonSemanticFieldValue<number>('scalar'),
        convenienceFeeAmount: defineStoredJsonSemanticFieldValue<number>('scalar'),
        taxAmount: defineStoredJsonSemanticFieldValue<number>('scalar'),
        donationAmount: defineStoredJsonSemanticFieldValue<number>('scalar'),
        tipAmount: defineStoredJsonSemanticFieldValue<number>('scalar'),
        totalAmount: defineStoredJsonSemanticFieldValue<number>('scalar'),
    },
    variants: {
        v1: {
            schemaName: 'StoredPaymentTransactionAmountBreakdownStorageV1Schema',
            schema: StoredPaymentTransactionAmountBreakdownStorageV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['payment_transaction_amount_breakdown_version'],
                },
            ],
            normalize: normalizePaymentTransactionAmountBreakdownStorageV1,
            semanticPaths: {
                baseAmount: ['base_amount'],
                surchargeAmount: ['surcharge'],
                convenienceFeeAmount: ['convenience_fee'],
                taxAmount: ['tax'],
                donationAmount: ['donation'],
                totalAmount: ['total_amount'],
            },
            unrepresentedSemanticFields: ['tipAmount'],
        },
        v2: {
            schemaName: 'StoredPaymentTransactionAmountBreakdownStorageV2Schema',
            schema: StoredPaymentTransactionAmountBreakdownStorageV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['payment_transaction_amount_breakdown_version'],
                    markerValue: PaymentTransactionAmountBreakdownStorageVersion.V2,
                },
            ],
            normalize: normalizePaymentTransactionAmountBreakdownStorageV2,
            semanticPaths: {
                baseAmount: ['breakdown', 'base_amount'],
                surchargeAmount: ['breakdown', 'surcharge'],
                convenienceFeeAmount: ['breakdown', 'convenience_fee'],
                taxAmount: ['breakdown', 'tax'],
                donationAmount: ['breakdown', 'donation'],
                totalAmount: ['breakdown', 'total_amount'],
            },
            unrepresentedSemanticFields: ['tipAmount'],
        },
        v3: {
            schemaName: 'StoredPaymentTransactionAmountBreakdownStorageV3Schema',
            schema: StoredPaymentTransactionAmountBreakdownStorageV3Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['payment_transaction_amount_breakdown_version'],
                    markerValue: PaymentTransactionAmountBreakdownStorageVersion.V3,
                },
            ],
            normalize: normalizePaymentTransactionAmountBreakdownStorageV3,
            semanticPaths: {
                baseAmount: ['breakdown', 'base_amount'],
                surchargeAmount: ['breakdown', 'surcharge'],
                convenienceFeeAmount: ['breakdown', 'convenience_fee'],
                taxAmount: ['breakdown', 'tax'],
                donationAmount: ['breakdown', 'donation'],
                tipAmount: ['breakdown', 'tip'],
                totalAmount: ['breakdown', 'total_amount'],
            },
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v3',
});

export const StoredPaymentTransactionAmountBreakdownRowParser: z.ZodType<PaymentTransactionAmountBreakdown> =
    buildStoredJsonParser(PaymentTransactionAmountBreakdownStorageContract);

export const StoredPaymentTransactionAmountBreakdownParser: z.ZodType<PaymentTransactionAmountBreakdown> =
    z.preprocess(
        (value: unknown): unknown => ({ value }),
        StoredPaymentTransactionAmountBreakdownRowParser
    );

export function serializePaymentTransactionAmountBreakdownV2(
    breakdown: LegacyPaymentTransactionAmountBreakdown
): PaymentTransactionAmountBreakdownStorageV2 {
    return {
        payment_transaction_amount_breakdown_version:
            PaymentTransactionAmountBreakdownStorageVersion.V2,
        breakdown: LegacyPaymentTransactionAmountBreakdownSchema.parse(breakdown),
    };
}

export function serializePaymentTransactionAmountBreakdownV3(
    breakdown: PaymentTransactionAmountBreakdown
): PaymentTransactionAmountBreakdownStorageV3 {
    return {
        payment_transaction_amount_breakdown_version:
            PaymentTransactionAmountBreakdownStorageVersion.V3,
        breakdown: PaymentTransactionAmountBreakdownSchema.parse(breakdown),
    };
}

const StoredPaymentRequestAmountBreakdownSafeIntegerDomains: ReadonlyMap<
    string,
    SafeIntegerDomain
> = new Map([
    [
        'payment_request_amount_breakdown_version',
        PaymentRequestAmountBreakdownVersionSafeIntegerDomain,
    ],
]);

function normalizeStoredPaymentRequestAmountBreakdownNumericLeaves(value: unknown): unknown {
    return normalizeStoredPaymentNumericLeaves(value, {
        safeIntegerDomains: StoredPaymentRequestAmountBreakdownSafeIntegerDomains,
        boundedNumberFields: StoredAmountBreakdownNumberFields,
    });
}

export const PaymentRequestAmountBreakdownStorageVersionSchema: z.ZodType<
    typeof PaymentRequestAmountBreakdownStorageVersion.V2
> = z.literal(PaymentRequestAmountBreakdownStorageVersion.V2);

export const PaymentRequestAmountBreakdownStorageV1Schema: z.ZodType<PaymentRequestAmountBreakdownStorageV1> =
    LegacyPaymentTransactionAmountBreakdownSchema;

export const StoredPaymentRequestAmountBreakdownStorageV1Schema: z.ZodType<PaymentRequestAmountBreakdownStorageV1> =
    z.preprocess(
        normalizeStoredPaymentRequestAmountBreakdownNumericLeaves,
        PaymentRequestAmountBreakdownStorageV1Schema
    );

export const PaymentRequestAmountBreakdownStorageV2Schema: z.ZodType<PaymentRequestAmountBreakdownStorageV2> =
    z
        .object({
            payment_request_amount_breakdown_version:
                PaymentRequestAmountBreakdownStorageVersionSchema,
            amount_breakdown: LegacyPaymentTransactionAmountBreakdownSchema,
        })
        .strict();

export const StoredPaymentRequestAmountBreakdownStorageV2Schema: z.ZodType<PaymentRequestAmountBreakdownStorageV2> =
    z.preprocess(
        normalizeStoredPaymentRequestAmountBreakdownNumericLeaves,
        PaymentRequestAmountBreakdownStorageV2Schema
    );

export const PaymentRequestAmountBreakdownVersionedObjectMarkerSchema: z.ZodType<{
    readonly payment_request_amount_breakdown_version: unknown;
}> = z
    .object({
        payment_request_amount_breakdown_version: z.unknown(),
    })
    .passthrough();

export function normalizePaymentRequestAmountBreakdownStorageV1(
    amountBreakdown: PaymentRequestAmountBreakdownStorageV1
): PaymentTransactionAmountBreakdown {
    return amountBreakdown;
}

export function normalizePaymentRequestAmountBreakdownStorageV2(
    amountBreakdown: PaymentRequestAmountBreakdownStorageV2
): PaymentTransactionAmountBreakdown {
    return amountBreakdown.amount_breakdown;
}

export const PaymentRequestAmountBreakdownStorageContract = defineStoredJsonContract({
    contractName: 'PaymentRequestAmountBreakdown',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredPaymentRequestAmountBreakdownStorageV1Schema',
            schema: StoredPaymentRequestAmountBreakdownStorageV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['payment_request_amount_breakdown_version'],
                },
            ],
            normalize: normalizePaymentRequestAmountBreakdownStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredPaymentRequestAmountBreakdownStorageV2Schema',
            schema: StoredPaymentRequestAmountBreakdownStorageV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['payment_request_amount_breakdown_version'],
                    markerValue: PaymentRequestAmountBreakdownStorageVersion.V2,
                },
            ],
            normalize: normalizePaymentRequestAmountBreakdownStorageV2,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const StoredPaymentRequestAmountBreakdownParser: z.ZodType<PaymentTransactionAmountBreakdown> =
    buildStoredJsonValueParser(PaymentRequestAmountBreakdownStorageContract);

export function serializePaymentRequestAmountBreakdownV2(
    amountBreakdown: LegacyPaymentTransactionAmountBreakdown
): PaymentRequestAmountBreakdownStorageV2 {
    return {
        payment_request_amount_breakdown_version: PaymentRequestAmountBreakdownStorageVersion.V2,
        amount_breakdown: LegacyPaymentTransactionAmountBreakdownSchema.parse(amountBreakdown),
    };
}

export const PaymentRequestDocumentDescriptorSchema: z.ZodType<PaymentRequestDocumentDescriptor> =
    z.object({
        payment_request_document_uuid: UuidSchema,
        source_kind: z.union([z.literal('UPLOADED'), z.literal('GENERATED')]),
        original_filename: NonEmptyStringSchema.optional(),
        media_type: NonEmptyStringSchema,
        content_length_bytes: z.number().int().nonnegative(),
        content_sha256: NonEmptyStringSchema,
        document_template_uuid: UuidSchema.optional(),
    });

export const DocumentTemplateDescriptorSchema: z.ZodType<DocumentTemplateDescriptor> = z.object({
    document_template_uuid: UuidSchema,
    merchant_organization_uuid: UuidSchema,
    name: NonEmptyStringSchema.optional(),
    payment_request_details_type_id: NonEmptyStringSchema,
    payment_request_details_type_version: z.number().int().nonnegative(),
    description: NonEmptyStringSchema.optional(),
    content_sha256: NonEmptyStringSchema,
    status: z.enum(Status),
});

export const UploadPaymentRequestDocumentResponseSchema: z.ZodType<UploadPaymentRequestDocumentResponse> =
    z.object({
        payment_request_document: PaymentRequestDocumentDescriptorSchema,
    });

export const PaymentRequestGeneratedDocumentKindSchema = z.enum(
    PaymentRequestGeneratedDocumentKind
);

export const GenericCreditCardReceiptTemplateSelectionSchema = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.CREDIT_CARD_RECEIPT),
    payment_request_details_type_id: z.literal(TransactionDetailsTypeId.GENERIC),
    payment_request_details_type_version: z.literal(0),
    document_template_uuid: UuidSchema.optional(),
}) satisfies z.ZodType<
    Extract<
        CreditCardReceiptTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.GENERIC }
    >
>;

export const DealertrackPartsCreditCardReceiptTemplateSelectionSchema = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.CREDIT_CARD_RECEIPT),
    payment_request_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_PARTS),
    payment_request_details_type_version: z.literal(1),
    document_template_uuid: UuidSchema.optional(),
}) satisfies z.ZodType<
    Extract<
        CreditCardReceiptTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_PARTS }
    >
>;

export const DealertrackServiceCreditCardReceiptTemplateSelectionSchema = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.CREDIT_CARD_RECEIPT),
    payment_request_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_SERVICE),
    payment_request_details_type_version: z.literal(1),
    document_template_uuid: UuidSchema.optional(),
}) satisfies z.ZodType<
    Extract<
        CreditCardReceiptTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_SERVICE }
    >
>;

export const DealertrackDealCreditCardReceiptTemplateSelectionSchema = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.CREDIT_CARD_RECEIPT),
    payment_request_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_DEAL),
    payment_request_details_type_version: z.literal(1),
    document_template_uuid: UuidSchema.optional(),
}) satisfies z.ZodType<
    Extract<
        CreditCardReceiptTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_DEAL }
    >
>;

export const DealertrackLeasingCreditCardReceiptTemplateSelectionSchema = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.CREDIT_CARD_RECEIPT),
    payment_request_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_LEASING),
    payment_request_details_type_version: z.literal(1),
    document_template_uuid: UuidSchema.optional(),
}) satisfies z.ZodType<
    Extract<
        CreditCardReceiptTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_LEASING }
    >
>;

export const DealertrackArStatementCustomersCreditCardReceiptTemplateSelectionSchema: z.ZodType<
    Extract<
        CreditCardReceiptTemplateSelection,
        {
            payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_AR_STATEMENT_CUSTOMERS;
        }
    >
> = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.CREDIT_CARD_RECEIPT),
    payment_request_details_type_id: z.literal(
        TransactionDetailsTypeId.DEALERTRACK_AR_STATEMENT_CUSTOMERS
    ),
    payment_request_details_type_version: z.literal(1),
    document_template_uuid: UuidSchema.optional(),
});

export const CreditCardReceiptTemplateSelectionSchema: z.ZodType<CreditCardReceiptTemplateSelection> =
    z.union([
        GenericCreditCardReceiptTemplateSelectionSchema,
        DealertrackPartsCreditCardReceiptTemplateSelectionSchema,
        DealertrackServiceCreditCardReceiptTemplateSelectionSchema,
        DealertrackDealCreditCardReceiptTemplateSelectionSchema,
        DealertrackLeasingCreditCardReceiptTemplateSelectionSchema,
        DealertrackArStatementCustomersCreditCardReceiptTemplateSelectionSchema,
    ]);

const DealertrackPartsInvoiceTemplateSelectionSchema = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.INVOICE),
    payment_request_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_PARTS),
    payment_request_details_type_version: z.literal(1),
    document_template_uuid: UuidSchema.optional(),
}) satisfies z.ZodType<
    Extract<
        InvoiceTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_PARTS }
    >
>;

const DealertrackServiceInvoiceTemplateSelectionSchema = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.INVOICE),
    payment_request_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_SERVICE),
    payment_request_details_type_version: z.literal(1),
    document_template_uuid: UuidSchema.optional(),
}) satisfies z.ZodType<
    Extract<
        InvoiceTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_SERVICE }
    >
>;

const DealertrackDealInvoiceTemplateSelectionSchema = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.INVOICE),
    payment_request_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_DEAL),
    payment_request_details_type_version: z.literal(1),
    document_template_uuid: UuidSchema.optional(),
}) satisfies z.ZodType<
    Extract<
        InvoiceTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_DEAL }
    >
>;

const DealertrackLeasingInvoiceTemplateSelectionSchema = z.object({
    document_kind: z.literal(PaymentRequestGeneratedDocumentKind.INVOICE),
    payment_request_details_type_id: z.literal(TransactionDetailsTypeId.DEALERTRACK_LEASING),
    payment_request_details_type_version: z.literal(1),
    document_template_uuid: UuidSchema.optional(),
}) satisfies z.ZodType<
    Extract<
        InvoiceTemplateSelection,
        { payment_request_details_type_id: typeof TransactionDetailsTypeId.DEALERTRACK_LEASING }
    >
>;

export const InvoiceTemplateSelectionSchema: z.ZodType<InvoiceTemplateSelection> = z.union([
    DealertrackPartsInvoiceTemplateSelectionSchema,
    DealertrackServiceInvoiceTemplateSelectionSchema,
    DealertrackDealInvoiceTemplateSelectionSchema,
    DealertrackLeasingInvoiceTemplateSelectionSchema,
]);

export const PaymentRequestDocumentTemplateSelectionSchema: z.ZodType<PaymentRequestDocumentTemplateSelection> =
    z.union([CreditCardReceiptTemplateSelectionSchema, InvoiceTemplateSelectionSchema]);

export const GeneratePaymentRequestDocumentRequestSchema: z.ZodType<GeneratePaymentRequestDocumentRequest> =
    z.object({
        payment_request_uuid: UuidSchema,
        template_selection: PaymentRequestDocumentTemplateSelectionSchema,
    });

export const GeneratePaymentRequestDocumentResponseSchema: z.ZodType<GeneratePaymentRequestDocumentResponse> =
    z.object({
        payment_request_document: PaymentRequestDocumentDescriptorSchema,
    });

export const DocumentGenerationMerchantBrandingSchema: z.ZodType<DocumentGenerationMerchantBranding> =
    z.object({
        legal_name: NonEmptyStringSchema.optional(),
        dba_name: NonEmptyStringSchema.optional(),
        address_lines: z.array(NonEmptyStringSchema),
        phone_number: PhoneNumberSchema.optional(),
        css_stylesheet: z.string().optional(),
        logo_asset_url: z.string().url().optional(),
        anchor_href_allowlist: z.array(z.string().url()),
    });

export const DocumentGenerationPaymentRequestSnapshotSchema: z.ZodType<DocumentGenerationPaymentRequestSnapshot> =
    z.object({
        payment_request_uuid: UuidSchema,
        title: NonEmptyStringSchema.optional(),
        source_document_reference: NonEmptyStringSchema.optional(),
        currency_code: NonEmptyStringSchema,
        original_amount: JsonLosslessNumberInputSchema.optional(),
        amount_breakdown: PaymentTransactionAmountBreakdownSchema.optional(),
        recipient_email: z.string().email().optional(),
        recipient_phone: PhoneNumberSchema.optional(),
        expires_ts: z.string().datetime().optional(),
    });

export const PaymentRequestDocumentReferenceSchema: z.ZodType<PaymentRequestDocumentReference> =
    z.object({
        payment_request_document_uuid: UuidSchema,
    });

export const CreateDraftPaymentRequestRequestSchema: z.ZodType<CreateDraftPaymentRequestRequest> =
    z.object({
        created_by_app: z.union([z.literal('IPS'), z.literal('PILLAR')]),
        created_by_ux_state: NonEmptyStringSchema.optional(),
        terminal_device_uuid: UuidSchema,
        merchant_customer_record_uuid: UuidSchema.optional(),
        merchant_user_uuid: UuidSchema.optional(),
        customer_organization_uuid: UuidSchema.optional(),
        customer_user_uuid: UuidSchema.optional(),
        source_ipw_system_id: NumericIpwSystemIdSchema.optional(),
        source_document_reference: NonEmptyStringSchema.optional(),
        source_document_key: PaymentRequestSourceDocumentKeySchema.optional(),
        title: NonEmptyStringSchema.optional(),
        message: NonEmptyStringSchema.optional(),
        payment_request_details: TransactionDetailsSchema.optional(),
        original_amount: JsonLosslessNumberInputSchema.optional(),
        amount_breakdown: PaymentTransactionAmountBreakdownSchema.optional(),
        currency_code: NonEmptyStringSchema,
        expires_ts: z.string().datetime().optional(),
        recipient_email: z.string().email().optional(),
        recipient_phone: PhoneNumberSchema.optional(),
        email_opt_in: z.boolean().optional(),
        sms_opt_in: z.boolean().optional(),
    });

export const CreateDraftPaymentRequestResponseSchema: z.ZodType<CreateDraftPaymentRequestResponse> =
    z.object({
        payment_request_uuid: UuidSchema,
        status: PaymentRequestStatusSchema,
    });

const FinalizeSendPaymentRequestRequestBaseSchema = z.object({
    payment_request_uuid: UuidSchema,
    allow_resend_existing_request: z.boolean().optional(),
});

const FinalizeSendPaymentRequestEmailOnlyRequestSchema: z.ZodType<FinalizeSendPaymentRequestEmailOnlyRequest> =
    FinalizeSendPaymentRequestRequestBaseSchema.extend({
        send_email: z.literal(true),
        send_sms: z.literal(false),
        recipient_email: z.string().email(),
    });

const FinalizeSendPaymentRequestSmsOnlyRequestSchema: z.ZodType<FinalizeSendPaymentRequestSmsOnlyRequest> =
    FinalizeSendPaymentRequestRequestBaseSchema.extend({
        send_email: z.literal(false),
        send_sms: z.literal(true),
        recipient_phone: PhoneNumberSchema,
    });

const FinalizeSendPaymentRequestEmailAndSmsRequestSchema: z.ZodType<FinalizeSendPaymentRequestEmailAndSmsRequest> =
    FinalizeSendPaymentRequestRequestBaseSchema.extend({
        send_email: z.literal(true),
        send_sms: z.literal(true),
        recipient_email: z.string().email(),
        recipient_phone: PhoneNumberSchema,
    });

export const FinalizeSendPaymentRequestRequestSchema: z.ZodType<FinalizeSendPaymentRequestRequest> =
    z.union([
        FinalizeSendPaymentRequestEmailOnlyRequestSchema,
        FinalizeSendPaymentRequestSmsOnlyRequestSchema,
        FinalizeSendPaymentRequestEmailAndSmsRequestSchema,
    ]);

const ResendPaymentRequestRequestBaseSchema = z.object({
    send_email: z.boolean(),
    send_sms: z.boolean(),
});

const ResendPaymentRequestEmailOnlyRequestSchema: z.ZodType<ResendPaymentRequestEmailOnlyRequest> =
    ResendPaymentRequestRequestBaseSchema.extend({
        send_email: z.literal(true),
        send_sms: z.literal(false),
        recipient_email: z.string().email(),
    });

const ResendPaymentRequestSmsOnlyRequestSchema: z.ZodType<ResendPaymentRequestSmsOnlyRequest> =
    ResendPaymentRequestRequestBaseSchema.extend({
        send_email: z.literal(false),
        send_sms: z.literal(true),
        recipient_phone: PhoneNumberSchema,
    });

const ResendPaymentRequestEmailAndSmsRequestSchema: z.ZodType<ResendPaymentRequestEmailAndSmsRequest> =
    ResendPaymentRequestRequestBaseSchema.extend({
        send_email: z.literal(true),
        send_sms: z.literal(true),
        recipient_email: z.string().email(),
        recipient_phone: PhoneNumberSchema,
    });

export const ResendPaymentRequestRequestSchema: z.ZodType<ResendPaymentRequestRequest> = z.union([
    ResendPaymentRequestEmailOnlyRequestSchema,
    ResendPaymentRequestSmsOnlyRequestSchema,
    ResendPaymentRequestEmailAndSmsRequestSchema,
]);

const CancelAndReissuePaymentRequestRequestBaseSchema = z.object({
    send_email: z.boolean(),
    send_sms: z.boolean(),
});

const CancelAndReissuePaymentRequestEmailOnlyRequestSchema: z.ZodType<CancelAndReissuePaymentRequestEmailOnlyRequest> =
    CancelAndReissuePaymentRequestRequestBaseSchema.extend({
        send_email: z.literal(true),
        send_sms: z.literal(false),
        recipient_email: z.string().email(),
    });

const CancelAndReissuePaymentRequestSmsOnlyRequestSchema: z.ZodType<CancelAndReissuePaymentRequestSmsOnlyRequest> =
    CancelAndReissuePaymentRequestRequestBaseSchema.extend({
        send_email: z.literal(false),
        send_sms: z.literal(true),
        recipient_phone: PhoneNumberSchema,
    });

const CancelAndReissuePaymentRequestEmailAndSmsRequestSchema: z.ZodType<CancelAndReissuePaymentRequestEmailAndSmsRequest> =
    CancelAndReissuePaymentRequestRequestBaseSchema.extend({
        send_email: z.literal(true),
        send_sms: z.literal(true),
        recipient_email: z.string().email(),
        recipient_phone: PhoneNumberSchema,
    });

export const CancelAndReissuePaymentRequestRequestSchema: z.ZodType<CancelAndReissuePaymentRequestRequest> =
    z.union([
        CancelAndReissuePaymentRequestEmailOnlyRequestSchema,
        CancelAndReissuePaymentRequestSmsOnlyRequestSchema,
        CancelAndReissuePaymentRequestEmailAndSmsRequestSchema,
    ]);

export const FinalizeSendPaymentRequestChannelDispatchResultSchema: z.ZodType<FinalizeSendPaymentRequestChannelDispatchResult> =
    z.object({
        delivery_channel: DeliveryChannelSchema,
        accepted: z.boolean(),
        provider: DeliveryProviderSchema,
        provider_message_id: NonEmptyStringSchema.optional(),
        error_code: NonEmptyStringSchema.optional(),
        error_message: NonEmptyStringSchema.optional(),
    });

export const FinalizeSendPaymentRequestResponseSchema: z.ZodType<FinalizeSendPaymentRequestResponse> =
    z.object({
        payment_request_uuid: UuidSchema,
        status: PaymentRequestStatusSchema,
        single_use_auth_token_uuid: UuidSchema,
        single_use_auth_token_value: NonEmptyStringSchema,
        public_payment_url: z.string().url(),
        payment_request_reused: z.boolean(),
        channel_results: z.array(FinalizeSendPaymentRequestChannelDispatchResultSchema),
    });

export const CancelAndReissuePaymentRequestResponseSchema: z.ZodType<CancelAndReissuePaymentRequestResponse> =
    z.object({
        payment_request_uuid: UuidSchema,
        status: PaymentRequestStatusSchema,
        single_use_auth_token_uuid: UuidSchema,
        single_use_auth_token_value: NonEmptyStringSchema,
        public_payment_url: z.string().url(),
        payment_request_reused: z.boolean(),
        channel_results: z.array(FinalizeSendPaymentRequestChannelDispatchResultSchema),
        superseded_payment_request_uuid: UuidSchema,
    });

export const PublicPaymentLinkActionResponseSchema: z.ZodType<PublicPaymentLinkActionResponse> =
    z.object({
        payment_request_uuid: UuidSchema,
        channel_results: z.array(FinalizeSendPaymentRequestChannelDispatchResultSchema),
    });

export const PublicPaymentRequestStateSchema = z.enum(PublicPaymentRequestState);
export const PublicPaymentRequestErrorCodeSchema = z.enum(PublicPaymentRequestErrorCode);

export const PublicPaymentRequestDocumentViewSchema = z.object({
    payment_request_document_uuid: UuidSchema,
    original_filename: NonEmptyStringSchema.optional(),
    media_type: NonEmptyStringSchema,
}) satisfies z.ZodType<PublicPaymentRequestDocumentView>;

const PublicResolvedPaymentRequestViewBaseSchema = z.object({
    payment_request_uuid: UuidSchema,
    merchant_display_name: NonEmptyStringSchema,
    title: NonEmptyStringSchema.optional(),
    source_document_reference: NonEmptyStringSchema.optional(),
    source_document_key: PaymentRequestSourceDocumentKeySchema.optional(),
    original_amount: JsonLosslessNumberInputSchema.optional(),
    amount_breakdown: PaymentTransactionAmountBreakdownSchema.optional(),
    currency_code: NonEmptyStringSchema,
    terminal_device_uuid: UuidSchema.optional(),
    documents: z.array(PublicPaymentRequestDocumentViewSchema),
    merchant_timezone: MerchantTimeZoneSchema.nullable(),
});

export const PublicActivePaymentRequestViewSchema =
    PublicResolvedPaymentRequestViewBaseSchema.extend({
        state: z.literal(PublicPaymentRequestState.ACTIVE),
        expires_ts: z.string().datetime().optional(),
    }) satisfies z.ZodType<PublicActivePaymentRequestView>;

export const PublicExpiredPaymentRequestViewSchema =
    PublicResolvedPaymentRequestViewBaseSchema.extend({
        state: z.literal(PublicPaymentRequestState.EXPIRED),
        expires_ts: z.string().datetime().optional(),
    }) satisfies z.ZodType<PublicExpiredPaymentRequestView>;

export const PublicPaidPaymentRequestViewSchema = PublicResolvedPaymentRequestViewBaseSchema.extend(
    {
        state: z.literal(PublicPaymentRequestState.PAID),
        fulfilled_ts: z.string().datetime().optional(),
    }
) satisfies z.ZodType<PublicPaidPaymentRequestView>;

export const PublicCanceledPaymentRequestViewSchema =
    PublicResolvedPaymentRequestViewBaseSchema.extend({
        state: z.literal(PublicPaymentRequestState.CANCELED),
    }) satisfies z.ZodType<PublicCanceledPaymentRequestView>;

export const PublicReissuedPaymentRequestViewSchema =
    PublicResolvedPaymentRequestViewBaseSchema.extend({
        state: z.literal(PublicPaymentRequestState.REISSUED),
        replacement_payment_request_uuid: UuidSchema.optional(),
        replacement_public_payment_url: z.string().url().optional(),
        replacement_expires_ts: z.string().datetime().optional(),
    }) satisfies z.ZodType<PublicReissuedPaymentRequestView>;

export const PublicErroredPaymentRequestViewSchema = z.object({
    state: z.literal(PublicPaymentRequestState.ERROR),
    error_code: PublicPaymentRequestErrorCodeSchema,
}) satisfies z.ZodType<PublicErroredPaymentRequestView>;

export const PublicPaymentRequestViewSchema = z.discriminatedUnion('state', [
    PublicActivePaymentRequestViewSchema,
    PublicExpiredPaymentRequestViewSchema,
    PublicCanceledPaymentRequestViewSchema,
    PublicPaidPaymentRequestViewSchema,
    PublicReissuedPaymentRequestViewSchema,
    PublicErroredPaymentRequestViewSchema,
]) satisfies z.ZodType<PublicPaymentRequestView>;

export const GuestPaymentEntryContextViewSchema: z.ZodType<GuestPaymentEntryContextView> = z.object(
    {
        payment_request_uuid: UuidSchema,
        merchant_display_name: NonEmptyStringSchema,
        title: NonEmptyStringSchema.optional(),
        source_document_reference: NonEmptyStringSchema.optional(),
        source_document_key: PaymentRequestSourceDocumentKeySchema.optional(),
        transaction_details: TransactionDetailsCreateRequestSchema,
        original_amount: JsonLosslessNumberInputSchema.optional(),
        amount_breakdown: PaymentTransactionAmountBreakdownSchema.optional(),
        currency_code: NonEmptyStringSchema,
        terminal_device_uuid: UuidSchema.optional(),
        receipt_email: ReceiptEmailSchema.optional(),
        payment_configuration: PaymentConfigurationResponseSchema,
        login_cta: z.lazy(() => GuestPaymentEntryLoginCtaViewSchema).optional(),
    }
);

export const GuestPaymentEntryLoginCtaViewSchema: z.ZodType<GuestPaymentEntryLoginCtaView> =
    z.object({
        href: NonEmptyStringSchema,
        email: z.string().email(),
    });

export const AuthenticatedPaymentEntryContextViewSchema: z.ZodType<AuthenticatedPaymentEntryContextView> =
    z.object({
        payment_request_uuid: UuidSchema,
        stored_payment_methods: z.array(CustomerPaymentMethodListItemSchema),
    });

export const GuestPaymentSuccessAccountCtaViewSchema: z.ZodType<GuestPaymentSuccessAccountCtaView> =
    z.discriminatedUnion('kind', [
        z.object({
            kind: z.literal(GuestPaymentSuccessAccountCtaKind.LOGIN),
            href: NonEmptyStringSchema,
            email: z.string().email(),
        }),
        z.object({
            kind: z.literal(GuestPaymentSuccessAccountCtaKind.SIGNUP),
            href: NonEmptyStringSchema,
            email: z.string().email().optional(),
            payment_method_storage_available: z.boolean(),
        }),
    ]);

export const GuestPaymentSuccessViewSchema: z.ZodType<GuestPaymentSuccessView> = z.object({
    payment_request_uuid: UuidSchema,
    merchant_display_name: NonEmptyStringSchema,
    gateway_txn_id: NonEmptyStringSchema.optional(),
    gateway_auth_code: NonEmptyStringSchema.optional(),
    receipt_html_path: NonEmptyStringSchema.optional(),
    account_cta: GuestPaymentSuccessAccountCtaViewSchema.optional(),
});

export const GuestPaymentRequestDocumentRedirectAccessResponseSchema: z.ZodType<GuestPaymentRequestDocumentRedirectAccessResponse> =
    z.object({
        kind: z.literal('redirect'),
        url: z.string().url(),
    });

export const GuestPaymentRequestDocumentInlineAccessResponseSchema: z.ZodType<GuestPaymentRequestDocumentInlineAccessResponse> =
    z.object({
        kind: z.literal('inline'),
        content_type: NonEmptyStringSchema,
        content_length: z.number().int().nonnegative().optional(),
    });

export const GuestPaymentRequestDocumentAccessResponseSchema: z.ZodType<GuestPaymentRequestDocumentAccessResponse> =
    z.union([
        GuestPaymentRequestDocumentRedirectAccessResponseSchema,
        GuestPaymentRequestDocumentInlineAccessResponseSchema,
    ]);

export const TwilioStatusCallbackPayloadSchema: z.ZodType<TwilioStatusCallbackPayload> = z.record(
    z.string(),
    z.union([z.string(), z.array(z.string()), z.undefined()])
);

export const PaymentRequestDeliveryAcceptedProviderPayloadSchema: z.ZodType<PaymentRequestDeliveryAcceptedProviderPayload> =
    z.record(z.string(), z.string());

export const StoredTwilioStatusCallbackPayloadSchema: z.ZodType<StoredTwilioStatusCallbackPayload> =
    z.record(z.string(), z.union([z.string(), z.array(z.string())]));

export const StoredSendgridEventWebhookEventSchema: z.ZodType<StoredSendgridEventWebhookEvent> =
    z.record(z.string(), JsonValueSchema);

export const PaymentRequestDeliveryEventPayloadSchema: z.ZodType<PaymentRequestDeliveryEventPayload> =
    z.union([
        PaymentRequestDeliveryAcceptedProviderPayloadSchema,
        StoredTwilioStatusCallbackPayloadSchema,
        StoredSendgridEventWebhookEventSchema,
    ]);

export const PaymentRequestDeliveryAcceptedProviderPayloadRowSchema: z.ZodType<PaymentRequestDeliveryProviderPayloadRow> =
    z.union([
        z.object({
            delivery_channel: z.literal(DeliveryChannel.SMS),
            delivery_provider: z.literal(DeliveryProvider.TWILIO),
            provider_event_type: z.literal('accepted'),
            provider_payload: PaymentRequestDeliveryAcceptedProviderPayloadSchema,
        }),
        z.object({
            delivery_channel: z.literal(DeliveryChannel.EMAIL),
            delivery_provider: z.literal(DeliveryProvider.SENDGRID),
            provider_event_type: z.literal('accepted'),
            provider_payload: PaymentRequestDeliveryAcceptedProviderPayloadSchema,
        }),
    ]);

export const TwilioStatusCallbackProviderPayloadRowSchema: z.ZodType<PaymentRequestDeliveryProviderPayloadRow> =
    z.object({
        delivery_channel: z.literal(DeliveryChannel.SMS),
        delivery_provider: z.literal(DeliveryProvider.TWILIO),
        provider_event_type: NonEmptyStringSchema,
        provider_payload: StoredTwilioStatusCallbackPayloadSchema,
    });

export const SendgridEventWebhookProviderPayloadRowSchema: z.ZodType<PaymentRequestDeliveryProviderPayloadRow> =
    z.object({
        delivery_channel: z.literal(DeliveryChannel.EMAIL),
        delivery_provider: z.literal(DeliveryProvider.SENDGRID),
        provider_event_type: NonEmptyStringSchema,
        provider_payload: StoredSendgridEventWebhookEventSchema,
    });

export const PaymentRequestDeliveryProviderPayloadRowSchema: z.ZodType<PaymentRequestDeliveryProviderPayloadRow> =
    z.union([
        PaymentRequestDeliveryAcceptedProviderPayloadRowSchema,
        TwilioStatusCallbackProviderPayloadRowSchema,
        SendgridEventWebhookProviderPayloadRowSchema,
    ]);

export function normalizeProviderPayloadStorageV1(
    row: PaymentRequestDeliveryProviderPayloadRow
): PaymentRequestDeliveryProviderPayloadRow {
    return row;
}

export const ProviderPayloadStorageContract = defineStoredJsonContract({
    contractName: 'ProviderPayload',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'PaymentRequestDeliveryProviderPayloadRowSchema',
            schema: PaymentRequestDeliveryProviderPayloadRowSchema,
            schemaInput: (boundary: StoredJsonRowBoundary) => ({
                delivery_channel: boundary.siblingValues.delivery_channel,
                delivery_provider: boundary.siblingValues.delivery_provider,
                provider_event_type: boundary.siblingValues.provider_event_type,
                provider_payload: boundary.value,
            }),
            guards: [
                {
                    kind: 'sibling_field_present',
                    siblingField: 'delivery_channel',
                },
                {
                    kind: 'sibling_field_present',
                    siblingField: 'delivery_provider',
                },
                {
                    kind: 'sibling_field_present',
                    siblingField: 'provider_event_type',
                },
            ],
            normalize: normalizeProviderPayloadStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const StoredProviderPayloadRowParser: z.ZodType<PaymentRequestDeliveryProviderPayloadRow> =
    buildStoredJsonParser(ProviderPayloadStorageContract);

export const SendgridEventWebhookPayloadSchema: z.ZodType<SendgridEventWebhookPayload> = z.array(
    z.record(z.string(), z.unknown())
);

export const GuestProcessStoredPaymentMethodPaymentRequestSchema: z.ZodType<GuestProcessStoredPaymentMethodPaymentRequest> =
    z.object({
        payment_method_token_uuid: UuidSchema,
        receipt_email: ReceiptEmailSchema.optional(),
    });

export const AuthenticatedProcessCardTokenPaymentRequestSchema: z.ZodType<AuthenticatedProcessCardTokenPaymentRequest> =
    z.union([
        z
            .object({
                ...GuestProcessCardTokenPaymentRequestCommonShape,
                ...MetadataRichSubmittedHostedCardTokenSchemaShape,
                stored_for_future_use: z.boolean(),
            })
            .strict(),
        z
            .object({
                ...GuestProcessCardTokenPaymentRequestCommonShape,
                ...TokenOnlyHostedCardTokenSchemaShape,
                stored_for_future_use: z.boolean(),
            })
            .strict(),
    ]);

export const PaymentRequestDeliveryEventViewSchema = z.object({
    payment_request_delivery_event_uuid: UuidSchema,
    delivery_channel: DeliveryChannelSchema,
    delivery_provider: DeliveryProviderSchema,
    provider_event_type: NonEmptyStringSchema,
    provider_message_id: NonEmptyStringSchema.optional(),
    provider_event_ts: z.string().datetime().optional(),
    received_ts: z.string().datetime(),
}) satisfies z.ZodType<PaymentRequestDeliveryEventView>;

export const PaymentRequestDeliveryEventDetailsViewSchema =
    PaymentRequestDeliveryEventViewSchema.extend({
        provider_payload: PaymentRequestDeliveryEventPayloadSchema.optional(),
    }) satisfies z.ZodType<PaymentRequestDeliveryEventDetailsView>;

export const PaymentRequestListItemSchema = z.object({
    payment_request_uuid: UuidSchema,
    merchant_organization_uuid: UuidSchema,
    merchant_display_name: NonEmptyStringSchema,
    customer_organization_uuid: UuidSchema.optional(),
    merchant_customer_record_uuid: UuidSchema.optional(),
    status: PaymentRequestStatusSchema,
    reconciled: z.enum(PaymentReconciliationStatus).nullable(),
    customer_name: NonEmptyStringSchema.optional(),
    title: NonEmptyStringSchema.optional(),
    recipient_email: z.string().email().optional(),
    recipient_phone: PhoneNumberSchema.optional(),
    source_document_reference: NonEmptyStringSchema.optional(),
    merchant_transaction_id: NonEmptyStringSchema.optional(),
    source_document_key: PaymentRequestSourceDocumentKeySchema.optional(),
    base_amount: JsonLosslessNumberInputSchema.optional(),
    original_amount: JsonLosslessNumberInputSchema.optional(),
    currency_code: NonEmptyStringSchema,
    created_ts: z.string().datetime(),
    expires_ts: z.string().datetime().optional(),
    can_display: z.boolean(),
    merchant_timezone: MerchantTimeZoneSchema.nullable(),
    virtual_terminal: z
        .object({
            terminal_device_uuid: UuidSchema,
            name: NonEmptyStringSchema.optional(),
            processor_device_id: NonEmptyStringSchema,
        })
        .optional(),
}) satisfies z.ZodType<PaymentRequestListItem>;

export const PaymentRequestListSchema: z.ZodType<PaymentRequestList> = z.object({
    payment_requests: z.array(PaymentRequestListItemSchema),
});

const PaymentRequestStatusQueryArraySchema = z.preprocess((value: unknown) => {
    if (value === undefined) {
        return undefined;
    }
    return Array.isArray(value) ? value : [value];
}, z.array(PaymentRequestStatusSchema).optional());

export const MerchantPaymentRequestStatusBucketSchema: z.ZodType<MerchantPaymentRequestStatusBucket> =
    z.enum(MerchantPaymentRequestStatusBucket);

export const MerchantPaymentRequestSearchRequestSchema: z.ZodType<MerchantPaymentRequestSearchRequest> =
    z
        .object({
            terms: SearchTermsSchema,
            field_predicates: SearchFieldPredicatesSchema,
            json_path_predicates: PhysicalJsonStructurePredicatesSchema,
            statuses: PaymentRequestStatusQueryArraySchema,
            status_bucket: MerchantPaymentRequestStatusBucketSchema.optional(),
            require_reconcile_candidates: z.boolean().optional(),
            payment_request_uuids: z.array(UuidSchema).min(1).max(100).optional(),
            limit: z.coerce
                .number()
                .int()
                .positive()
                .optional()
                .transform((value: number | undefined) => {
                    if (value === undefined) {
                        return undefined;
                    }
                    return Math.min(value, 1000);
                }),
            cursor: PositiveIntSchema.optional(),
        })
        .strict();

export const MerchantPaymentRequestListResponseSchema: z.ZodType<MerchantPaymentRequestListResponse> =
    z.object({
        payment_requests: z.array(PaymentRequestListItemSchema),
        pagination: CursorPaginationSchema,
    });

export const PaymentRequestTransactionListItemSchema: z.ZodType<PaymentRequestTransactionListItem> =
    z.object({
        payment_transaction_uuid: UuidSchema,
        merchant_transaction_id: NonEmptyStringSchema.optional(),
        original_amount: JsonLosslessNumberInputSchema.optional(),
        currency_code: NonEmptyStringSchema.optional(),
        status: PaymentTransactionStatusSchema,
        created_ts: z.string().datetime(),
        is_voidable: z.boolean(),
        is_refundable: z.boolean(),
        refundable_amount: JsonLosslessNumberInputSchema.optional(),
        can_display: z.boolean(),
    });

export const PaymentRequestDetailsViewLegacySchema = PaymentRequestListItemSchema.extend({
    message: NonEmptyStringSchema.optional(),
    payment_request_details: TransactionDetailsSchema.optional(),
    amount_breakdown: PaymentTransactionAmountBreakdownSchema.optional(),
    public_payment_url: z.string().url().optional(),
    documents: z.array(PaymentRequestDocumentDescriptorSchema),
    delivery_events: z.array(PaymentRequestDeliveryEventViewSchema),
    payment_transactions: z.array(PaymentRequestTransactionListItemSchema),
}) satisfies z.ZodType<PaymentRequestDetailsViewLegacy>;

export const PaymentRequestDetailsViewSchema: z.ZodType<PaymentRequestDetailsView> =
    PaymentRequestListItemSchema.extend({
        contract_version: z.literal('v1'),
        message: NonEmptyStringSchema.optional(),
        payment_request_details: TransactionDetailsSchema.optional(),
        amount_breakdown: PaymentTransactionAmountBreakdownSchema.optional(),
        public_payment_url: z.string().url().optional(),
        documents: z.array(PaymentRequestDocumentDescriptorSchema),
        payment_transactions: z.array(PaymentRequestTransactionListItemSchema),
    }).strict();

export const PaymentRequestDetailsViewContract = {
    variants: {
        v1: {
            normalize: (
                paymentRequestDetailsView: PaymentRequestDetailsView
            ): PaymentRequestDetailsView =>
                PaymentRequestDetailsViewSchema.parse(paymentRequestDetailsView),
        },
    },
    writerVariant: 'v1',
} as const;

export const PaymentRequestDeliveryEventListRequestLegacySchema: z.ZodType<PaymentRequestDeliveryEventListRequestLegacy> =
    z.object({}).strict();

export const PaymentRequestDeliveryEventListRequestSchema: z.ZodType<PaymentRequestDeliveryEventListRequest> =
    z
        .object({
            contract_version: z.literal('v1'),
            limit: z
                .union([z.number(), z.string()])
                .transform((value: string | number): number => Number(value))
                .pipe(z.number().int().min(1).max(50)),
            cursor: UuidSchema.optional(),
            merchant_organization_uuid: UuidSchema.optional(),
        })
        .strict();

export const PaymentRequestDeliveryEventListRequestContract = {
    variants: {
        v1: {
            normalize: (
                paymentRequestDeliveryEventListRequest: PaymentRequestDeliveryEventListRequest
            ): PaymentRequestDeliveryEventListRequest =>
                PaymentRequestDeliveryEventListRequestSchema.parse(
                    paymentRequestDeliveryEventListRequest
                ),
        },
    },
    writerVariant: 'v1',
} as const;

export const PaymentRequestDeliveryEventListLegacySchema: z.ZodType<PaymentRequestDeliveryEventListLegacy> =
    z.object({
        payment_request_uuid: UuidSchema,
        delivery_events: z.array(PaymentRequestDeliveryEventViewSchema),
    });

export const PaymentRequestDeliveryEventListSchema: z.ZodType<PaymentRequestDeliveryEventList> = z
    .object({
        contract_version: z.literal('v1'),
        payment_request_uuid: UuidSchema,
        delivery_events: z.array(PaymentRequestDeliveryEventViewSchema),
        pagination: OpaqueCursorPaginationSchema,
    })
    .strict();

export const PaymentRequestDeliveryEventListContract = {
    variants: {
        v1: {
            normalize: (
                paymentRequestDeliveryEventList: PaymentRequestDeliveryEventList
            ): PaymentRequestDeliveryEventList =>
                PaymentRequestDeliveryEventListSchema.parse(paymentRequestDeliveryEventList),
        },
    },
    writerVariant: 'v1',
} as const;

export const PaymentRequestOwnerCandidateSchema: z.ZodType<PaymentRequestOwnerCandidate> = z.object(
    {
        user_uuid: UuidSchema,
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        primary_email: z.string().email(),
    }
);

export const PaymentRequestOwnerCandidatesResponseSchema: z.ZodType<PaymentRequestOwnerCandidatesResponse> =
    z.object({
        current_user_uuid: UuidSchema,
        users: z.array(PaymentRequestOwnerCandidateSchema),
    });

export const CustomerPortalDashboardRequestSchema: z.ZodType<CustomerPortalDashboardRequest> =
    z.object({
        merchant_organization_uuid: UuidSchema.optional(),
        customer_organization_uuid: UuidSchema.optional(),
    });

export const CustomerPortalDashboardMerchantOptionSchema: z.ZodType<CustomerPortalDashboardMerchantOption> =
    z.object({
        merchant_organization_uuid: UuidSchema,
        merchant_display_name: NonEmptyStringSchema,
    });

export const CustomerPortalDashboardSummarySchema: z.ZodType<CustomerPortalDashboardSummary> =
    z.object({
        open_payment_request_count: z.number().int().nonnegative(),
        overdue_payment_request_count: z.number().int().nonnegative(),
        open_payment_request_total_amount: JsonLosslessNumberInputSchema.optional(),
        currency_code: NonEmptyStringSchema.optional(),
    });

export const CustomerPortalDashboardResponseSchema: z.ZodType<CustomerPortalDashboardResponse> =
    z.object({
        merchant_options: z.array(CustomerPortalDashboardMerchantOptionSchema),
        selected_merchant_organization_uuid: UuidSchema.optional(),
        summary: CustomerPortalDashboardSummarySchema,
        recent_payment_requests: z.array(PaymentRequestListItemSchema),
    });

export const CustomerPaymentRequestListRequestSchema: z.ZodType<CustomerPaymentRequestListRequest> =
    z.object({
        merchant_organization_uuid: UuidSchema.optional(),
        customer_organization_uuid: UuidSchema.optional(),
        statuses: PaymentRequestStatusQueryArraySchema,
        limit: z.coerce
            .number()
            .int()
            .positive()
            .optional()
            .transform((value: number | undefined) => {
                if (value === undefined) {
                    return undefined;
                }
                return Math.min(value, 1000);
            }),
        cursor: PositiveIntSchema.optional(),
    });

export const CustomerPaymentRequestSearchRequestSchema: z.ZodType<CustomerPaymentRequestSearchRequest> =
    z
        .object({
            merchant_organization_uuid: UuidSchema.optional(),
            customer_organization_uuid: UuidSchema.optional(),
            terms: SearchTermsSchema,
            statuses: PaymentRequestStatusQueryArraySchema,
            limit: z.coerce
                .number()
                .int()
                .positive()
                .optional()
                .transform((value: number | undefined) => {
                    if (value === undefined) {
                        return undefined;
                    }
                    return Math.min(value, 1000);
                }),
            cursor: PositiveIntSchema.optional(),
        })
        .strict();

export const CustomerPaymentRequestListResponseSchema: z.ZodType<CustomerPaymentRequestListResponse> =
    z.object({
        payment_requests: z.array(PaymentRequestListItemSchema),
        pagination: CursorPaginationSchema,
    });

export const CustomerPaymentHistoryRequestSchema: z.ZodType<CustomerPaymentHistoryRequest> =
    z.object({
        merchant_organization_uuid: UuidSchema.optional(),
        customer_organization_uuid: UuidSchema.optional(),
        limit: z.coerce
            .number()
            .int()
            .positive()
            .optional()
            .transform((value: number | undefined) => {
                if (value === undefined) {
                    return undefined;
                }
                return Math.min(value, 1000);
            }),
        cursor: PositiveIntSchema.optional(),
    });

export const CustomerPaymentHistoryListItemSchema: z.ZodType<CustomerPaymentHistoryListItem> =
    z.object({
        payment_transaction_uuid: UuidSchema,
        merchant_organization_uuid: UuidSchema,
        merchant_display_name: NonEmptyStringSchema,
        payment_request_uuid: UuidSchema.optional(),
        amount: z.number(),
        currency_code: NonEmptyStringSchema,
        payment_rail: PaymentRailSchema.optional(),
        payment_method_summary: NonEmptyStringSchema.optional(),
        status: z.enum(PaymentTransactionStatus),
        submitted_ts: z.string().datetime(),
        merchant_timezone: MerchantTimeZoneSchema.nullable(),
    });

export const CustomerPaymentHistoryResponseSchema: z.ZodType<CustomerPaymentHistoryResponse> =
    z.object({
        payments: z.array(CustomerPaymentHistoryListItemSchema),
        pagination: CursorPaginationSchema,
    });

/**
 * Get Transaction Details Response
 */
const GetTransactionDetailsResponseObjectSchema = z.object({
    payment_transaction_uuid: UuidSchema,
    payment_request_uuid: UuidSchema.optional(),
    payment_method_token_uuid: UuidSchema.optional(),
    merchant_user_uuid: UuidSchema,
    merchant_organization_uuid: UuidSchema,
    merchant_organization_display_name: z.string().nullable().optional(),
    merchant_timezone: MerchantTimeZoneSchema.nullable(),
    terminal_device_uuid: UuidSchema,
    processor_device_id: NonEmptyStringSchema,
    processor_tid: z.string().nullable().optional(),
    terminal_device_name: z.string().nullable().optional(),
    amount_breakdown: PaymentTransactionAmountBreakdownSchema.nullable(),
    ipw_transaction_details: TransactionDetailsSchema.nullable().optional(),
    gateway_id: GatewayIdSchema,
    gateway_transaction_type: z.enum(GatewayTransactionType),
    gateway_txn_id: z.string().optional(),
    gateway_batch: z.string().optional(),
    gateway_auth_code: z.string().optional(),
    gateway_stan: z.string().optional(),
    gateway_rrn: z.string().optional(),
    currency_code: z.string().optional(),
    card_brand: z.string().optional(),
    card_type: CardTypeSchema.optional(),
    card_last4: z.string().optional(),
    cardholder_name: z.string().optional(),
    original_amount: z.string(),
    batched_amount: z.string().nullable(),
    refunded_amount: z.string().nullable(),
    refundable_amount: z.string().nullable(),
    status: z.enum(PaymentTransactionStatus),
    reconciled: z.enum(PaymentReconciliationStatus),
    ipw_system_id: z.number(),
    ipw_customer_id: z.string().nullable().optional(),
    transaction_created_ts: z.string(),
    auth_decision_ts: z.string().nullable().optional(),
    merchant_transaction_id: NonEmptyStringSchema,
    description: z.string().optional(),
    lifecycle_events: z.array(TransactionLifecycleEventSchema),
    gateway_request: JsonValueSchema.optional(),
    gateway_request_type: z.string().optional(),
    transaction_configuration: PaymentTransactionConfigurationSchema.optional(),
});

export const GetTransactionDetailsResponseSchema: z.ZodType<GetTransactionDetailsResponse> =
    GetTransactionDetailsResponseObjectSchema.superRefine(validateLifecycleBoundaryPayloads);

const StoredGetTransactionDetailsResponseSchema: z.ZodType<GetTransactionDetailsResponse> =
    z.preprocess(
        normalizeStoredLifecycleGatewayRequest,
        GetTransactionDetailsResponseObjectSchema.extend({
            lifecycle_events: z.array(StoredTransactionLifecycleEventSchema),
        }).superRefine(validateStoredLifecycleBoundaryPayloads)
    );

export const GetReceiptDataResponseSchema = z.object({
    merchant_transaction_id: z.string(),
    transaction_type: z.enum(ReceiptTransactionType),
    receipt_timestamp: z.string(),
    merchant_timezone: MerchantTimeZoneSchema.nullable(),
    gateway_rrn: z.string().optional(),
    gateway_auth_code: z.string().optional(),
    card_brand: z.string().optional(),
    card_type: CardTypeSchema,
    card_last4: z.string().optional(),
    surcharge_pct: JsonPercentageLosslessNumberInputSchema.optional(),
    surcharge_label: z.string().min(1).optional(),
    original_amount: z.string(),
    amount_breakdown: PaymentTransactionAmountBreakdownSchema,
    transactionDetails: z.lazy(() => TransactionDetailsSchema),
    business_details: BusinessDetailsSchema.optional(),
    can_get_receipt: z.boolean(),
    missing_required_fields: z.array(z.string()),
});

export const CreditCardReceiptDocumentHydrationContextSchema: z.ZodType<CreditCardReceiptDocumentHydrationContext> =
    z.object({
        template_selection: CreditCardReceiptTemplateSelectionSchema,
        merchant_branding: DocumentGenerationMerchantBrandingSchema,
        payment_request: DocumentGenerationPaymentRequestSnapshotSchema,
        receipt: GetReceiptDataResponseSchema,
        merchant_timezone: MerchantTimeZoneSchema.nullable(),
        receipt_timestamp_display: z.string(),
    });

const DealertrackPartsInvoiceDocumentHydrationContextSchema: z.ZodType<DealertrackPartsInvoiceDocumentHydrationContext> =
    z.object({
        template_selection: DealertrackPartsInvoiceTemplateSelectionSchema,
        merchant_branding: DocumentGenerationMerchantBrandingSchema,
        payment_request: DocumentGenerationPaymentRequestSnapshotSchema,
        payment_request_details: DealertrackPartsTransactionDetailsV1Schema,
    });

const DealertrackServiceInvoiceDocumentHydrationContextSchema: z.ZodType<DealertrackServiceInvoiceDocumentHydrationContext> =
    z.object({
        template_selection: DealertrackServiceInvoiceTemplateSelectionSchema,
        merchant_branding: DocumentGenerationMerchantBrandingSchema,
        payment_request: DocumentGenerationPaymentRequestSnapshotSchema,
        payment_request_details: DealertrackServiceTransactionDetailsV1Schema,
    });

const DealertrackDealInvoiceDocumentHydrationContextSchema: z.ZodType<DealertrackDealInvoiceDocumentHydrationContext> =
    z.object({
        template_selection: DealertrackDealInvoiceTemplateSelectionSchema,
        merchant_branding: DocumentGenerationMerchantBrandingSchema,
        payment_request: DocumentGenerationPaymentRequestSnapshotSchema,
        payment_request_details: DealertrackDealTransactionDetailsV1Schema,
    });

const DealertrackLeasingInvoiceDocumentHydrationContextSchema: z.ZodType<DealertrackLeasingInvoiceDocumentHydrationContext> =
    z.object({
        template_selection: DealertrackLeasingInvoiceTemplateSelectionSchema,
        merchant_branding: DocumentGenerationMerchantBrandingSchema,
        payment_request: DocumentGenerationPaymentRequestSnapshotSchema,
        payment_request_details: DealertrackLeasingTransactionDetailsV1Schema,
    });

export const InvoiceDocumentHydrationContextSchema: z.ZodType<InvoiceDocumentHydrationContext> =
    z.union([
        DealertrackPartsInvoiceDocumentHydrationContextSchema,
        DealertrackServiceInvoiceDocumentHydrationContextSchema,
        DealertrackDealInvoiceDocumentHydrationContextSchema,
        DealertrackLeasingInvoiceDocumentHydrationContextSchema,
    ]);

export const PaymentRequestDocumentHydrationContextSchema: z.ZodType<PaymentRequestDocumentHydrationContext> =
    z.union([
        CreditCardReceiptDocumentHydrationContextSchema,
        InvoiceDocumentHydrationContextSchema,
    ]);

export const EInvoiceEmailHydrationContextSchema: z.ZodType<EInvoiceEmailHydrationContext> =
    z.object({
        merchant_branding: DocumentGenerationMerchantBrandingSchema,
        payment_request: DocumentGenerationPaymentRequestSnapshotSchema,
        merchant_name: NonEmptyStringSchema,
        base_amount: JsonLosslessNumberInputSchema,
        payment_link: z.string().url(),
        customer_name: NonEmptyStringSchema.optional(),
        formatted_source_document_reference: NonEmptyStringSchema.optional(),
    });

/**
 * Record Reconciliation Request
 */
const RecordReconciliationRequestBaseSchema = z.object({
    reconciled: z.boolean(),
    gateway_request: GatewayLifecycleRequestSchema.optional(),
    gateway_response: JsonValueSchema.optional(),
    gateway_response_status: z.number().int().optional(),
    gateway_response_header: GatewayResponseHeaderSchema.optional(),
    gateway_request_type: z.string().optional(),
    gateway_response_type: z.string().optional(),
    error_code: z.string().optional(),
    error_message: z.string().optional(),
});

const RecordReconciliationRequestSingleSchema: z.ZodType<RecordReconciliationRequest> =
    RecordReconciliationRequestBaseSchema.extend({
        mode: z.literal('single'),
        payment_transaction_uuid: UuidSchema,
    });

const RecordReconciliationRequestBatchSchema: z.ZodType<RecordReconciliationRequest> =
    RecordReconciliationRequestBaseSchema.extend({
        mode: z.literal('batch'),
        payment_transaction_uuids: z.array(UuidSchema).min(1),
    });

export const RecordReconciliationRequestSchema: z.ZodType<RecordReconciliationRequest> = z.union([
    RecordReconciliationRequestSingleSchema,
    RecordReconciliationRequestBatchSchema,
]);

/**
 * Record Reconciliation Response
 */
export const RecordReconciliationResponseSchema: z.ZodType<RecordReconciliationResponse> = z.object(
    {
        success: z.boolean(),
        payment_transaction_lifecycle_event_uuid: UuidSchema.optional(),
        payment_transaction_lifecycle_event_uuids: z.array(UuidSchema).optional(),
        error: z.string().optional(),
    }
);

/**
 * Record Payment Request Reconciliation Request
 */
export const RecordPaymentRequestReconciliationRequestSchema: z.ZodType<RecordPaymentRequestReconciliationRequest> =
    z.object({
        payment_request_uuids: z.array(UuidSchema).min(1),
        reconciled: z.boolean(),
        gateway_request: GatewayLifecycleRequestSchema.optional(),
        gateway_response: JsonValueSchema.optional(),
        gateway_request_type: z.string().optional(),
        gateway_response_type: z.string().optional(),
        error_code: z.string().optional(),
        error_message: z.string().optional(),
    });

/**
 * Record Payment Request Reconciliation Response
 */
export const RecordPaymentRequestReconciliationResponseSchema: z.ZodType<RecordPaymentRequestReconciliationResponse> =
    z.object({
        success: z.boolean(),
        payment_transaction_lifecycle_event_uuids: z.array(UuidSchema).optional(),
        error: z.string().optional(),
    });

const ReconcileCandidatesLimitSchema = z.preprocess((value) => {
    if (value === null || value === undefined || value === '') {
        return value;
    }
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? value : numericValue;
}, z.number().int().min(1).max(200));

export const DetectedDetailsReconcilePaymentsCandidatesRequestSchema: z.ZodType<
    Extract<GetReconcilePaymentsCandidatesRequest, { customer_name: string }>
> = z.object({
    customer_name: z.string().min(1),
    ipw_system_id: NumericIpwSystemIdSchema,
    phone_number: PhoneNumberSchema.optional(),
    parts_invoice_number: z.string().min(1).optional(),
    ro_number: z.string().min(1).optional(),
    invoice_id: z.string().min(1).max(GENERIC_IPW_INVOICE_ID_MAX_LENGTH).optional(),
    reconciled: z.nativeEnum(PaymentReconciliationStatus).optional(),
    limit: ReconcileCandidatesLimitSchema.optional(),
});

export const ExactTransactionReconcilePaymentsCandidatesRequestSchema: z.ZodType<
    Extract<GetReconcilePaymentsCandidatesRequest, { payment_transaction_uuid: string }>
> = z.object({
    payment_transaction_uuid: UuidSchema,
    ipw_system_id: NumericIpwSystemIdSchema,
    reconciled: z.nativeEnum(PaymentReconciliationStatus).optional(),
    limit: ReconcileCandidatesLimitSchema.optional(),
});

export const GetReconcilePaymentsCandidatesRequestSchema: z.ZodType<GetReconcilePaymentsCandidatesRequest> =
    z.union([
        DetectedDetailsReconcilePaymentsCandidatesRequestSchema,
        ExactTransactionReconcilePaymentsCandidatesRequestSchema,
    ]);

export const ReconcilePaymentsCandidateSchema: z.ZodType<ReconcilePaymentsCandidate> = z.object({
    payment_transaction_uuid: UuidSchema,
    status: z.nativeEnum(PaymentTransactionStatus),
    reconciled: z.nativeEnum(PaymentReconciliationStatus),
    base_amount: z.string(),
    original_amount: z.string(),
    auth_decision_ts: z.string(),
    merchant_transaction_id: z.string(),
    customer_name: z.string().optional(),
    gateway_txn_id: z.string().optional(),
    phone_number: PhoneNumberSchema.optional(),
    ro_number: z.string().optional(),
});

export const GetReconcilePaymentsCandidatesResponseSchema: z.ZodType<GetReconcilePaymentsCandidatesResponse> =
    z.object({
        transactions: z.array(ReconcilePaymentsCandidateSchema),
        merchant_timezone: MerchantTimeZoneSchema.nullable(),
    });

const LegacyTerminalSaleRequestSchema: z.ZodType<LegacyTerminalSaleRequest> = z.object({
    terminal_device_uuid: UuidSchema,
    terminal_card_entry_mode: z.literal(LegacyTerminalCardEntryMode),
    amount: z.string().optional(),
    description: z.string().optional(),
    customer_name: z.string().optional(),
    transaction_details: TransactionDetailsCreateRequestSchema,
    merchant_transaction_id: z.never().optional(),
});

const PillarTerminalSaleRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.TERMINAL_SALE),
    request: z.union([TerminalSaleRequestSchema, LegacyTerminalSaleRequestSchema]),
    transaction_details: TransactionDetailsCreateRequestSchema,
});

const PillarTerminalSaleRequestLatestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.TERMINAL_SALE),
    request: TerminalSaleRequestSchema,
    transaction_details: TransactionDetailsCreateRequestSchema,
});

const PillarTerminalAbortRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.TERMINAL_ABORT),
    request: TerminalAbortRequestSchema,
});

const PillarVoidTransactionRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.VOID_TRANSACTION),
    request: VoidTransactionRequestSchema,
});

const PillarRefundTransactionRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.REFUND_TRANSACTION),
    request: RefundTransactionRequestSchema,
});

const PillarAchPaymentRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.ACH_PAYMENT),
    request: ProcessAchPaymentRequestSchema,
    transaction_details: TransactionDetailsCreateRequestSchema,
});

const PillarRecordTokenRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.RECORD_TOKEN),
    request: RecordTokenRequestSchema,
});

const PillarProcessCardTokenPaymentRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.PROCESS_CARD_TOKEN_PAYMENT),
    request: ProcessCardTokenPaymentRequestSchema,
    transaction_details: TransactionDetailsCreateRequestSchema,
});

// V1/V2 history required a cardholder name; current admission must not widen it.
const PillarProcessCardTokenPaymentRequestStorageV1Schema =
    PillarProcessCardTokenPaymentRequestSchema.extend({
        request: ProcessCardTokenPaymentRequestStorageV1Schema,
    });

const PillarProcessCardNotPresentPaymentRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.PROCESS_CARD_NOT_PRESENT_PAYMENT),
    request: ProcessCardNotPresentPaymentLifecycleRequestSchema,
    transaction_details: TransactionDetailsCreateRequestSchema,
});

const PillarProcessCardNotPresentPaymentRequestStorageV1Schema =
    PillarProcessCardNotPresentPaymentRequestSchema.extend({
        request: ProcessCardNotPresentPaymentLifecycleRequestStorageV1Schema,
    });

const PillarGetOpenBatchRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.GET_OPEN_BATCH),
    request: GetOpenBatchRequestSchema,
});

const PillarQueryTransactionsRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.QUERY_TRANSACTIONS),
    request: QueryTransactionsRequestSchema,
});

const PillarGetTransactionStatusRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.GET_TRANSACTION_STATUS),
    request: GetTransactionStatusRequestSchema,
});

const PillarGetVaultTransactionsRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.GET_VAULT_TRANSACTIONS),
    request: GetVaultTransactionsRequestSchema,
});

const PillarGetTransactionDetailsRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.GET_TRANSACTION_DETAILS),
    payment_transaction_id: PositiveIntSchema,
});

const PillarRecordReconciliationRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.RECORD_RECONCILIATION),
    request: RecordReconciliationRequestSchema,
});

const PillarPaymentAuditLogRequestSchema = z.object({
    request_type: z.literal(PillarPaymentRequestType.PAYMENT_AUDIT_LOG),
    request: GetPaymentAuditLogRequestSchema,
});

export const PillarPaymentRequestTypeSchema: z.ZodType<PillarPaymentRequestType> = z.union([
    z.literal(PillarPaymentRequestType.TERMINAL_SALE),
    z.literal(PillarPaymentRequestType.TERMINAL_ABORT),
    z.literal(PillarPaymentRequestType.VOID_TRANSACTION),
    z.literal(PillarPaymentRequestType.REFUND_TRANSACTION),
    z.literal(PillarPaymentRequestType.ACH_PAYMENT),
    z.literal(PillarPaymentRequestType.RECORD_TOKEN),
    z.literal(PillarPaymentRequestType.PROCESS_CARD_TOKEN_PAYMENT),
    z.literal(PillarPaymentRequestType.PROCESS_CARD_NOT_PRESENT_PAYMENT),
    z.literal(PillarPaymentRequestType.GET_OPEN_BATCH),
    z.literal(PillarPaymentRequestType.QUERY_TRANSACTIONS),
    z.literal(PillarPaymentRequestType.GET_TRANSACTION_STATUS),
    z.literal(PillarPaymentRequestType.GET_VAULT_TRANSACTIONS),
    z.literal(PillarPaymentRequestType.GET_TRANSACTION_DETAILS),
    z.literal(PillarPaymentRequestType.RECORD_RECONCILIATION),
    z.literal(PillarPaymentRequestType.PAYMENT_AUDIT_LOG),
]);

export const PillarPaymentRequestSchema: z.ZodType<PillarPaymentRequest> = z.discriminatedUnion(
    'request_type',
    [
        PillarTerminalSaleRequestSchema,
        PillarTerminalAbortRequestSchema,
        PillarVoidTransactionRequestSchema,
        PillarRefundTransactionRequestSchema,
        PillarAchPaymentRequestSchema,
        PillarRecordTokenRequestSchema,
        PillarProcessCardTokenPaymentRequestSchema,
        PillarProcessCardNotPresentPaymentRequestSchema,
        PillarGetOpenBatchRequestSchema,
        PillarQueryTransactionsRequestSchema,
        PillarGetTransactionStatusRequestSchema,
        PillarGetVaultTransactionsRequestSchema,
        PillarGetTransactionDetailsRequestSchema,
        PillarRecordReconciliationRequestSchema,
        PillarPaymentAuditLogRequestSchema,
    ]
);

export const PillarPaymentRequestLatestSchema: z.ZodType<PillarPaymentRequestLatest> =
    z.discriminatedUnion('request_type', [
        PillarTerminalSaleRequestLatestSchema,
        PillarTerminalAbortRequestSchema,
        PillarVoidTransactionRequestSchema,
        PillarRefundTransactionRequestSchema,
        PillarAchPaymentRequestSchema,
        PillarRecordTokenRequestSchema,
        PillarProcessCardTokenPaymentRequestSchema,
        PillarProcessCardNotPresentPaymentRequestSchema,
        PillarGetOpenBatchRequestSchema,
        PillarQueryTransactionsRequestSchema,
        PillarGetTransactionStatusRequestSchema,
        PillarGetVaultTransactionsRequestSchema,
        PillarGetTransactionDetailsRequestSchema,
        PillarRecordReconciliationRequestSchema,
        PillarPaymentAuditLogRequestSchema,
    ]);

const StoredPillarPaymentRequestSafeIntegerDomains: ReadonlyMap<string, SafeIntegerDomain> =
    new Map([
        ...StoredTransactionDetailsSafeIntegerDomains,
        ['payment_transaction_id', PostgresqlGeneratedBigintIdentifierSafeIntegerDomain],
        ['gateway_response_status', HttpStatusSafeIntegerDomain],
        ['ipg_system_id', IpgSystemIdentifierSafeIntegerDomain],
        ['ipw_system_id', IpwSystemIdentifierSafeIntegerDomain],
        ['page', PaginationSafeIntegerDomain],
        ['limit', PaginationSafeIntegerDomain],
        ['pillar_payment_request_version', PillarPaymentRequestVersionSafeIntegerDomain],
    ]);

const StoredPillarPaymentRequestNumberFields: ReadonlySet<string> = new Set([
    ...StoredTransactionDetailsNumberFields,
    'surcharge_amount',
    'convenience_fee_amount',
    'total_fee_amount',
    'total_amount_paid',
]);

const StoredPillarPaymentRequestProviderSubtrees: ReadonlySet<string> = new Set([
    'gateway_request',
    'gateway_response',
]);

const StoredPillarPaymentRequestNumericContract: StoredPaymentNumericObjectContract = {
    safeIntegerDomains: StoredPillarPaymentRequestSafeIntegerDomains,
    boundedNumberFields: StoredPillarPaymentRequestNumberFields,
    excludedSubtreeFields: StoredPillarPaymentRequestProviderSubtrees,
};

function normalizeEmbeddedStoredGatewayLifecycleRequests(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map((item) => normalizeEmbeddedStoredGatewayLifecycleRequests(item));
    }
    const recordResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!recordResult.success) {
        return value;
    }
    if (
        Object.hasOwn(recordResult.data, 'gateway_request_type') &&
        Object.hasOwn(recordResult.data, 'gateway_request')
    ) {
        const normalizedRequest = normalizeStoredGatewayLifecycleRequestNumericLeaves(
            recordResult.data
        );
        if (normalizedRequest !== value) {
            return normalizedRequest;
        }
    }

    const normalized: Record<string, unknown> = {};
    for (const key in recordResult.data) {
        if (!Object.prototype.hasOwnProperty.call(recordResult.data, key)) {
            continue;
        }
        normalized[key] = normalizeEmbeddedStoredGatewayLifecycleRequests(recordResult.data[key]);
    }
    return normalized;
}

const StoredPillarTokenizationResultSafeIntegerDomains: ReadonlyMap<string, SafeIntegerDomain> =
    new Map([
        ['created', ProviderTimestampSafeIntegerDomain],
        ['errorcode', ProviderErrorCodeSafeIntegerDomain],
    ]);

const StoredPillarTokenizationResultNumericContract: StoredPaymentNumericObjectContract = {
    safeIntegerDomains: StoredPillarTokenizationResultSafeIntegerDomains,
    boundedNumberFields: NoStoredPaymentNumericFields,
};

export function normalizeStoredPillarPaymentRequestNumericLeaves(value: unknown): unknown {
    const normalizedValue = normalizeEmbeddedStoredGatewayLifecycleRequests(
        normalizeStoredPaymentNumericLeaves(value, StoredPillarPaymentRequestNumericContract)
    );
    const rootResult = StoredJsonNumberRecordSchema.safeParse(normalizedValue);
    if (
        !rootResult.success ||
        rootResult.data.request_type !== PillarPaymentRequestType.RECORD_TOKEN
    ) {
        return normalizedValue;
    }

    const requestResult = StoredJsonNumberRecordSchema.safeParse(rootResult.data.request);
    if (!requestResult.success || !Object.hasOwn(requestResult.data, 'gateway_response')) {
        return normalizedValue;
    }

    return {
        ...rootResult.data,
        request: {
            ...requestResult.data,
            gateway_response: normalizeStoredPaymentNumericLeaves(
                requestResult.data.gateway_response,
                StoredPillarTokenizationResultNumericContract
            ),
        },
    };
}

export const PillarPaymentRequestStorageV1RecordSchema: z.ZodType<Record<string, unknown>> =
    z.record(z.string(), z.unknown());

const TerminalSaleRequestStorageV1Schema: z.ZodType<TerminalSaleRequestStorageV1> = z.object({
    terminal_device_uuid: UuidSchema,
    amount: z.string().optional(),
    description: z.string().optional(),
    customer_name: z.string().optional(),
    transaction_details: TransactionDetailsCreateRequestSchema,
    merchant_transaction_id: z.never().optional(),
});

const PillarTerminalSaleRequestStorageV1Schema = z.object({
    request_type: z.literal(PillarPaymentRequestType.TERMINAL_SALE),
    request: TerminalSaleRequestStorageV1Schema,
    transaction_details: TransactionDetailsCreateRequestSchema,
});

const PillarPaymentRequestStorageV1PhysicalSchema: z.ZodType<PillarPaymentRequestStorageV1> =
    z.discriminatedUnion('request_type', [
        PillarTerminalSaleRequestStorageV1Schema,
        PillarTerminalAbortRequestSchema,
        PillarVoidTransactionRequestSchema,
        PillarRefundTransactionRequestSchema,
        PillarAchPaymentRequestSchema,
        PillarRecordTokenRequestSchema,
        PillarProcessCardTokenPaymentRequestStorageV1Schema,
        PillarProcessCardNotPresentPaymentRequestStorageV1Schema,
        PillarGetOpenBatchRequestSchema,
        PillarQueryTransactionsRequestSchema,
        PillarGetTransactionStatusRequestSchema,
        PillarGetVaultTransactionsRequestSchema,
        PillarGetTransactionDetailsRequestSchema,
        PillarRecordReconciliationRequestSchema,
        PillarPaymentAuditLogRequestSchema,
    ]);

export function completePillarPaymentRequestStorageV1CompatibilityCopies(value: unknown): unknown {
    const valueParseResult = PillarPaymentRequestStorageV1RecordSchema.safeParse(value);
    if (!valueParseResult.success) {
        return value;
    }
    const valueRecord = valueParseResult.data;
    const requestParseResult = PillarPaymentRequestStorageV1RecordSchema.safeParse(
        valueRecord.request
    );
    if (!requestParseResult.success) {
        return value;
    }
    const requestRecord = requestParseResult.data;
    if (
        valueRecord.request_type !== PillarPaymentRequestType.TERMINAL_SALE &&
        valueRecord.request_type !== PillarPaymentRequestType.ACH_PAYMENT &&
        valueRecord.request_type !== PillarPaymentRequestType.PROCESS_CARD_TOKEN_PAYMENT &&
        valueRecord.request_type !== PillarPaymentRequestType.PROCESS_CARD_NOT_PRESENT_PAYMENT
    ) {
        return value;
    }

    const outerTransactionDetails: unknown = valueRecord.transaction_details;
    const nestedTransactionDetails: unknown = requestRecord.transaction_details;
    if (outerTransactionDetails === undefined && nestedTransactionDetails === undefined) {
        return value;
    }

    return {
        ...valueRecord,
        request: {
            ...requestRecord,
            transaction_details:
                nestedTransactionDetails === undefined
                    ? outerTransactionDetails
                    : nestedTransactionDetails,
        },
        transaction_details:
            outerTransactionDetails === undefined
                ? nestedTransactionDetails
                : outerTransactionDetails,
    };
}

export const PillarPaymentRequestStorageV1Schema: z.ZodType<PillarPaymentRequestStorageV1> =
    z.preprocess(
        completePillarPaymentRequestStorageV1CompatibilityCopies,
        PillarPaymentRequestStorageV1PhysicalSchema
    );

export const StoredPillarPaymentRequestStorageV1Schema: z.ZodType<PillarPaymentRequestStorageV1> =
    z.preprocess(
        normalizeStoredPillarPaymentRequestNumericLeaves,
        PillarPaymentRequestStorageV1Schema
    );

export function normalizePillarPaymentRequestStorageV1(
    request: PillarPaymentRequestStorageV1
): PillarPaymentRequest {
    switch (request.request_type) {
        case PillarPaymentRequestType.TERMINAL_SALE:
            return {
                ...request,
                request: {
                    ...request.request,
                    terminal_card_entry_mode: LegacyTerminalCardEntryMode,
                    transaction_details: request.transaction_details,
                },
            };
        case PillarPaymentRequestType.ACH_PAYMENT:
            return {
                ...request,
                request: {
                    ...request.request,
                    transaction_details: request.transaction_details,
                },
            };
        case PillarPaymentRequestType.PROCESS_CARD_TOKEN_PAYMENT:
            return {
                ...request,
                request: {
                    ...request.request,
                    transaction_details: request.transaction_details,
                },
            };
        case PillarPaymentRequestType.TERMINAL_ABORT:
        case PillarPaymentRequestType.VOID_TRANSACTION:
        case PillarPaymentRequestType.REFUND_TRANSACTION:
        case PillarPaymentRequestType.RECORD_TOKEN:
        case PillarPaymentRequestType.PROCESS_CARD_NOT_PRESENT_PAYMENT:
        case PillarPaymentRequestType.GET_OPEN_BATCH:
        case PillarPaymentRequestType.QUERY_TRANSACTIONS:
        case PillarPaymentRequestType.GET_TRANSACTION_STATUS:
        case PillarPaymentRequestType.GET_VAULT_TRANSACTIONS:
        case PillarPaymentRequestType.GET_TRANSACTION_DETAILS:
        case PillarPaymentRequestType.RECORD_RECONCILIATION:
        case PillarPaymentRequestType.PAYMENT_AUDIT_LOG:
            return request;
    }
}

export const PillarPaymentRequestStorageV2Schema: z.ZodType<PillarPaymentRequestStorageV2> =
    z.discriminatedUnion('request_type', [
        PillarTerminalSaleRequestLatestSchema.extend({
            pillar_payment_request_version: z.literal(2),
        }),
        PillarTerminalAbortRequestSchema.extend({ pillar_payment_request_version: z.literal(2) }),
        PillarVoidTransactionRequestSchema.extend({ pillar_payment_request_version: z.literal(2) }),
        PillarRefundTransactionRequestSchema.extend({
            pillar_payment_request_version: z.literal(2),
        }),
        PillarAchPaymentRequestSchema.extend({ pillar_payment_request_version: z.literal(2) }),
        PillarRecordTokenRequestSchema.extend({ pillar_payment_request_version: z.literal(2) }),
        PillarProcessCardTokenPaymentRequestStorageV1Schema.extend({
            pillar_payment_request_version: z.literal(2),
        }),
        PillarProcessCardNotPresentPaymentRequestStorageV1Schema.extend({
            pillar_payment_request_version: z.literal(2),
        }),
        PillarGetOpenBatchRequestSchema.extend({ pillar_payment_request_version: z.literal(2) }),
        PillarQueryTransactionsRequestSchema.extend({
            pillar_payment_request_version: z.literal(2),
        }),
        PillarGetTransactionStatusRequestSchema.extend({
            pillar_payment_request_version: z.literal(2),
        }),
        PillarGetVaultTransactionsRequestSchema.extend({
            pillar_payment_request_version: z.literal(2),
        }),
        PillarGetTransactionDetailsRequestSchema.extend({
            pillar_payment_request_version: z.literal(2),
        }),
        PillarRecordReconciliationRequestSchema.extend({
            pillar_payment_request_version: z.literal(2),
        }),
        PillarPaymentAuditLogRequestSchema.extend({ pillar_payment_request_version: z.literal(2) }),
    ]);

export const StoredPillarPaymentRequestStorageV2Schema: z.ZodType<PillarPaymentRequestStorageV2> =
    z.preprocess(
        normalizeStoredPillarPaymentRequestNumericLeaves,
        PillarPaymentRequestStorageV2Schema
    );

export function normalizePillarPaymentRequestStorageV2(
    request: PillarPaymentRequestStorageV2
): PillarPaymentRequest {
    const { pillar_payment_request_version: _version, ...current } = request;
    return PillarPaymentRequestSchema.parse(current);
}

export const PillarPaymentRequestStorageV3Schema: z.ZodType<PillarPaymentRequestStorageV3> =
    PillarPaymentRequestLatestSchema.and(
        z.object({ pillar_payment_request_version: z.literal(3) })
    );

export const StoredPillarPaymentRequestStorageV3Schema: z.ZodType<PillarPaymentRequestStorageV3> =
    z.preprocess(
        normalizeStoredPillarPaymentRequestNumericLeaves,
        PillarPaymentRequestStorageV3Schema
    );

export function normalizePillarPaymentRequestStorageV3(
    request: PillarPaymentRequestStorageV3
): PillarPaymentRequest {
    const { pillar_payment_request_version: _version, ...current } = request;
    return PillarPaymentRequestSchema.parse(current);
}

export const PillarPaymentRequestStorageContract = defineStoredJsonContract({
    contractName: 'PillarPaymentRequest',
    semanticFieldValues: {
        requestType:
            defineStoredJsonSemanticFieldValue<PillarPaymentRequest['request_type']>('scalar'),
        customerName: defineStoredJsonSemanticFieldValue<string>('scalar'),
    },
    variants: {
        v1: {
            schemaName: 'StoredPillarPaymentRequestStorageV1Schema',
            schema: StoredPillarPaymentRequestStorageV1Schema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['pillar_payment_request_version'],
                },
            ],
            normalize: normalizePillarPaymentRequestStorageV1,
            semanticPaths: {
                requestType: ['request_type'],
                customerName: ['request', 'transaction_details', 'customerName'],
            },
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'StoredPillarPaymentRequestStorageV2Schema',
            schema: StoredPillarPaymentRequestStorageV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['pillar_payment_request_version'],
                    markerValue: 2,
                },
            ],
            normalize: normalizePillarPaymentRequestStorageV2,
            semanticPaths: {
                requestType: ['request_type'],
                customerName: ['request', 'transaction_details', 'customerName'],
            },
            unrepresentedSemanticFields: [],
        },
        v3: {
            schemaName: 'StoredPillarPaymentRequestStorageV3Schema',
            schema: StoredPillarPaymentRequestStorageV3Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['pillar_payment_request_version'],
                    markerValue: 3,
                },
            ],
            normalize: normalizePillarPaymentRequestStorageV3,
            semanticPaths: {
                requestType: ['request_type'],
                customerName: ['request', 'transaction_details', 'customerName'],
            },
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v3',
});

export const StoredPillarPaymentRequestRowParser: z.ZodType<PillarPaymentRequest> =
    buildStoredJsonParser(PillarPaymentRequestStorageContract);

export const StoredPillarPaymentRequestParser: z.ZodType<PillarPaymentRequest> = z.preprocess(
    (value: unknown): unknown => ({ value }),
    StoredPillarPaymentRequestRowParser
);

export function serializePillarPaymentRequestV3(
    request: PillarPaymentRequestLatest
): PillarPaymentRequestStorageV3 {
    switch (request.request_type) {
        case PillarPaymentRequestType.TERMINAL_SALE:
        case PillarPaymentRequestType.ACH_PAYMENT:
        case PillarPaymentRequestType.PROCESS_CARD_TOKEN_PAYMENT:
            return PillarPaymentRequestStorageV3Schema.parse({
                ...request,
                transaction_details: request.request.transaction_details,
                pillar_payment_request_version: 3,
            });
        case PillarPaymentRequestType.TERMINAL_ABORT:
        case PillarPaymentRequestType.VOID_TRANSACTION:
        case PillarPaymentRequestType.REFUND_TRANSACTION:
        case PillarPaymentRequestType.RECORD_TOKEN:
        case PillarPaymentRequestType.PROCESS_CARD_NOT_PRESENT_PAYMENT:
        case PillarPaymentRequestType.GET_OPEN_BATCH:
        case PillarPaymentRequestType.QUERY_TRANSACTIONS:
        case PillarPaymentRequestType.GET_TRANSACTION_STATUS:
        case PillarPaymentRequestType.GET_VAULT_TRANSACTIONS:
        case PillarPaymentRequestType.GET_TRANSACTION_DETAILS:
        case PillarPaymentRequestType.RECORD_RECONCILIATION:
        case PillarPaymentRequestType.PAYMENT_AUDIT_LOG:
            return PillarPaymentRequestStorageV3Schema.parse({
                ...request,
                pillar_payment_request_version: 3,
            });
    }
}

const StoredPillarPaymentResponseSafeIntegerDomains: ReadonlyMap<string, SafeIntegerDomain> =
    new Map([
        ...StoredPillarPaymentRequestSafeIntegerDomains,
        [
            'payment_transaction_lifecycle_event_id',
            PostgresqlGeneratedBigintIdentifierSafeIntegerDomain,
        ],
        ['credential_id', PostgresqlGeneratedBigintIdentifierSafeIntegerDomain],
        ['gateway_id', DatabaseIntegerSafeIntegerDomain],
        ['gateway_response_http_status', HttpStatusSafeIntegerDomain],
        ['created', ProviderTimestampSafeIntegerDomain],
        ['number_of_records', ProviderRecordCountSafeIntegerDomain],
        ['errorcode', ProviderErrorCodeSafeIntegerDomain],
    ]);

const StoredPillarPaymentResponseNumberFields: ReadonlySet<string> = new Set([
    ...StoredPillarPaymentRequestNumberFields,
    'base_amount',
    'total_amount',
    'convenience_fee',
    'surcharge_pct',
    'donation',
    'TOTAL_TAX',
    'rate',
    'taxableSales',
]);

const StoredPillarPaymentResponseProviderSubtrees: ReadonlySet<string> = new Set([
    'gateway_request',
    'gateway_response',
    'gateway_response_json',
]);

const StoredPillarPaymentResponseNumericContract: StoredPaymentNumericObjectContract = {
    safeIntegerDomains: StoredPillarPaymentResponseSafeIntegerDomains,
    boundedNumberFields: StoredPillarPaymentResponseNumberFields,
    excludedSubtreeFields: StoredPillarPaymentResponseProviderSubtrees,
};

function normalizeEmbeddedStoredPillarResponsePayloads(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map((item: unknown): unknown =>
            normalizeEmbeddedStoredPillarResponsePayloads(item)
        );
    }
    const recordResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!recordResult.success) {
        return value;
    }
    if (
        Object.hasOwn(recordResult.data, 'gateway_response_type') &&
        Object.hasOwn(recordResult.data, 'gateway_response_json')
    ) {
        return normalizeStoredGatewayLifecycleResponseNumericLeaves(recordResult.data);
    }
    if (
        Object.hasOwn(recordResult.data, 'request_type') &&
        Object.hasOwn(recordResult.data, 'request')
    ) {
        return normalizeStoredPillarPaymentRequestNumericLeaves(recordResult.data);
    }

    const normalized: Record<string, unknown> = {};
    for (const key of Object.keys(recordResult.data)) {
        normalized[key] = StoredPillarPaymentResponseProviderSubtrees.has(key)
            ? recordResult.data[key]
            : normalizeEmbeddedStoredPillarResponsePayloads(recordResult.data[key]);
    }
    return normalized;
}

export function normalizeStoredPillarPaymentResponseNumericLeaves(value: unknown): unknown {
    return normalizeStoredJsonSafeIntegerFieldsAtPath(
        normalizeEmbeddedStoredPillarResponsePayloads(
            normalizeStoredPaymentNumericLeaves(value, StoredPillarPaymentResponseNumericContract)
        ),
        {
            path: ['response', 'pagination'],
            safeIntegerDomain: PaginationSafeIntegerDomain,
            fields: ['page', 'limit', 'total'],
        }
    );
}

const PillarTerminalSaleResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.TERMINAL_SALE),
    response: TerminalSaleResponseSchema,
});

const PillarTerminalAbortResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.TERMINAL_ABORT),
    response: TerminalAbortResponseSchema,
});

const PillarVoidTransactionResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.VOID_TRANSACTION),
    response: VoidTransactionResponseSchema,
});

const PillarRefundTransactionResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.REFUND_TRANSACTION),
    response: RefundTransactionResponseSchema,
});

const PillarAchPaymentResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.ACH_PAYMENT),
    response: ProcessAchPaymentResponseSchema,
});

const PillarRecordTokenResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.RECORD_TOKEN),
    response: RecordTokenResponseSchema,
});

const PillarProcessCardTokenPaymentResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.PROCESS_CARD_TOKEN_PAYMENT),
    response: ProcessCardTokenPaymentResponseSchema,
});

const PillarProcessCardNotPresentPaymentResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.PROCESS_CARD_NOT_PRESENT_PAYMENT),
    response: ProcessCardNotPresentPaymentResponseSchema,
});

const PillarGetOpenBatchResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.GET_OPEN_BATCH),
    response: GetOpenBatchResponseSchema,
});

const PillarQueryTransactionsResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.QUERY_TRANSACTIONS),
    response: QueryTransactionsResponseSchema,
});

const PillarGetTransactionStatusResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.GET_TRANSACTION_STATUS),
    response: GetTransactionStatusResponseSchema,
});

const PillarGetVaultTransactionsResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.GET_VAULT_TRANSACTIONS),
    response: GetVaultTransactionsResponseSchema,
});

const PillarGetTransactionDetailsResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.GET_TRANSACTION_DETAILS),
    response: GetTransactionDetailsResponseSchema,
});

const StoredPillarGetTransactionDetailsResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.GET_TRANSACTION_DETAILS),
    response: StoredGetTransactionDetailsResponseSchema,
});

const PillarRecordReconciliationResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.RECORD_RECONCILIATION),
    response: RecordReconciliationResponseSchema,
});

const PillarPaymentAuditLogResponseSchema = z.object({
    response_type: z.literal(PillarPaymentResponseType.PAYMENT_AUDIT_LOG),
    response: GetPaymentAuditLogResponseSchema,
});

export const PillarPaymentResponseSchema: z.ZodType<PillarPaymentResponse> = z.discriminatedUnion(
    'response_type',
    [
        PillarTerminalSaleResponseSchema,
        PillarTerminalAbortResponseSchema,
        PillarVoidTransactionResponseSchema,
        PillarRefundTransactionResponseSchema,
        PillarAchPaymentResponseSchema,
        PillarRecordTokenResponseSchema,
        PillarProcessCardTokenPaymentResponseSchema,
        PillarProcessCardNotPresentPaymentResponseSchema,
        PillarGetOpenBatchResponseSchema,
        PillarQueryTransactionsResponseSchema,
        PillarGetTransactionStatusResponseSchema,
        PillarGetVaultTransactionsResponseSchema,
        PillarGetTransactionDetailsResponseSchema,
        PillarRecordReconciliationResponseSchema,
        PillarPaymentAuditLogResponseSchema,
    ]
);

export const StoredPaymentAuditLogEntrySchema: z.ZodType<PaymentAuditLogEntry> = z.preprocess(
    normalizeStoredLifecycleGatewayRequest,
    PaymentAuditLogEntryObjectSchema.extend({
        gateway_response_json: StoredProviderGatewayResponseSnapshotSchema.optional(),
        pillar_request: z.lazy(() => StoredPillarPaymentRequestParser).optional(),
        pillar_response: z.lazy(() => StoredPillarPaymentResponseSchema).optional(),
        client_request: z.lazy(() => StoredPillarPaymentRequestParser).optional(),
    }).superRefine(validateStoredLifecycleBoundaryPayloads)
);

export const StoredGetPaymentAuditLogResponseSchema: z.ZodType<GetPaymentAuditLogResponse> =
    z.object({
        events: z.array(StoredPaymentAuditLogEntrySchema),
        pagination: OpaqueCursorPaginationSchema,
        merchant_timezone: MerchantTimeZoneSchema.nullable(),
    });

export const StoredPillarPaymentAuditLogResponseSchema: z.ZodObject<{
    response_type: z.ZodLiteral<typeof PillarPaymentResponseType.PAYMENT_AUDIT_LOG>;
    response: typeof StoredGetPaymentAuditLogResponseSchema;
}> = z.object({
    response_type: z.literal(PillarPaymentResponseType.PAYMENT_AUDIT_LOG),
    response: StoredGetPaymentAuditLogResponseSchema,
}) satisfies z.ZodType<PillarPaymentAuditLogResponse>;

export const StoredPillarPaymentResponseUnionSchema: z.ZodType<PillarPaymentResponse> =
    z.discriminatedUnion('response_type', [
        PillarTerminalSaleResponseSchema,
        PillarTerminalAbortResponseSchema,
        PillarVoidTransactionResponseSchema,
        PillarRefundTransactionResponseSchema,
        PillarAchPaymentResponseSchema,
        PillarRecordTokenResponseSchema,
        PillarProcessCardTokenPaymentResponseSchema,
        PillarProcessCardNotPresentPaymentResponseSchema,
        PillarGetOpenBatchResponseSchema,
        PillarQueryTransactionsResponseSchema,
        PillarGetTransactionStatusResponseSchema,
        PillarGetVaultTransactionsResponseSchema,
        StoredPillarGetTransactionDetailsResponseSchema,
        PillarRecordReconciliationResponseSchema,
        StoredPillarPaymentAuditLogResponseSchema,
    ]);

export const StoredPillarPaymentResponseSchema: z.ZodType<PillarPaymentResponse> = z.preprocess(
    normalizeStoredPillarPaymentResponseNumericLeaves,
    StoredPillarPaymentResponseUnionSchema
);

export function normalizePillarPaymentResponseStorageV1(
    response: PillarPaymentResponse
): PillarPaymentResponse {
    return response;
}

export const PillarPaymentResponseStorageContract = defineStoredJsonContract({
    contractName: 'PillarPaymentResponse',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredPillarPaymentResponseSchema',
            schema: StoredPillarPaymentResponseSchema,
            guards: [],
            normalize: normalizePillarPaymentResponseStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const StoredPillarPaymentResponseParser: z.ZodType<PillarPaymentResponse> =
    buildStoredJsonValueParser(PillarPaymentResponseStorageContract);

/**
 * Common param/query schemas
 */
export const TransactionIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const PaymentTransactionUuidParamSchema: z.ZodType<string> = UuidSchema;
export const AccountIdQuerySchema: z.ZodType<string> = NonEmptyStringSchema;
